// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * AccountAction
 *
 * Account action record.
 */
export const zAccountAction = z.object({
    id: z.int(),
    createdAt: z.iso.datetime({ offset: true }),
    actionType: z.string(),
    userId: z.string().nullable(),
    organizationId: z.string().nullable(),
    details: z.record(z.string(), z.unknown()).nullish(),
    userName: z.string().nullish(),
    organizationName: z.string().nullish(),
    isDistributorAction: z.boolean().optional().default(false)
});

/**
 * AccountDetailsMiscModel
 *
 * Explorium misc fields used by account details.
 *
 * Excludes Explorium products/services because the account overview uses
 * Supplyco-sourced products/projects instead.
 */
export const zAccountDetailsMiscModel = z.object({
    businessType: z.string().nullable(),
    businessCategory: z.string().nullable(),
    commercialStructure: z.string().nullable(),
    equipments: z.array(z.string()).nullable(),
    industries: z.array(z.string()).nullable(),
    labels: z.array(z.string()).nullable(),
    numberOfLocationsRange: z.string().nullable(),
    specialties: z.array(z.string()).nullable(),
    verticals: z.array(z.string()).nullable(),
    webpageHighlights: z.array(z.string()).nullable(),
    yearFounded: z.int().nullable()
});

/**
 * AccountFacetOption
 *
 * One selectable account facet value.
 */
export const zAccountFacetOption = z.object({
    value: z.string(),
    label: z.string(),
    description: z.string().nullish(),
    count: z.int()
});

/**
 * AccountFacetOptionsResponse
 *
 * Options for a single account filter facet.
 */
export const zAccountFacetOptionsResponse = z.object({
    options: z.array(zAccountFacetOption)
});

/**
 * AccountGroupTag
 *
 * A buyer profile tag for a single-account group.
 */
export const zAccountGroupTag = z.object({
    id: z.int(),
    profileName: z.string()
});

/**
 * AccountNoteResponse
 *
 * Response model for an account note.
 */
export const zAccountNoteResponse = z.object({
    id: z.int(),
    accountId: z.int(),
    userId: z.string(),
    organizationId: z.string(),
    content: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
    userName: z.string().nullish(),
    organizationName: z.string().nullish(),
    isDistributorNote: z.boolean().optional().default(false),
    deletedAt: z.iso.datetime({ offset: true }).nullish(),
    deletedByName: z.string().nullish()
});

/**
 * AccountSummary
 *
 * Minimal account data for execute response.
 */
export const zAccountSummary = z.object({
    id: z.int(),
    accountName: z.string().nullish(),
    companyId: z.int().nullish()
});

/**
 * AcquiredBy
 *
 * Acquired by model.
 */
export const zAcquiredBy = z.object({
    Acquirer: z.string(),
    'Acquisition date': z.iso.date()
});

/**
 * ActionRequest
 *
 * Request body for actioning a recommendation.
 */
export const zActionRequest = z.object({
    note: z.string().min(1).max(5000),
    isOutreachNote: z.boolean().optional().default(false)
});

/**
 * ActiveMergeIntegrationInfo
 *
 * Active CRM integration info for admin display.
 */
export const zActiveMergeIntegrationInfo = z.object({
    id: z.int(),
    integrationName: z.string().nullish(),
    integrationSlug: z.string().nullish(),
    status: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
    lastSyncedAt: z.iso.datetime({ offset: true }).nullish()
});

/**
 * ActivityActionResponse
 *
 * Response for activity actions.
 */
export const zActivityActionResponse = z.object({
    success: z.boolean(),
    actionsLogged: z.int()
});

/**
 * ActivityLogEntry
 *
 * A single entry in the activity log.
 */
export const zActivityLogEntry = z.object({
    id: z.int(),
    createdAt: z.iso.datetime({ offset: true }),
    actionType: z.string(),
    userId: z.string().nullable(),
    userName: z.string().nullable(),
    organizationId: z.string().nullable(),
    organizationName: z.string().nullable(),
    objectType: z.string().nullable(),
    objectId: z.int().nullable(),
    accountId: z.int().nullable(),
    companyName: z.string().nullable(),
    personName: z.string().nullable(),
    details: z.record(z.string(), z.unknown()).nullish()
});

/**
 * ActivityLogResponse
 *
 * Response for activity log.
 */
export const zActivityLogResponse = z.object({
    entries: z.array(zActivityLogEntry),
    total: z.int(),
    page: z.int(),
    pageSize: z.int(),
    totalPages: z.int()
});

/**
 * ActivityStatsResponse
 *
 * Response for activity stats.
 */
export const zActivityStatsResponse = z.object({
    activeReps: z.int(),
    totalAccounts: z.int(),
    unassigned: z.int(),
    contacted: z.int(),
    positiveFeedback: z.int(),
    accountsRatedPct: z.int()
});

/**
 * AdminOrganizationCreateResponse
 *
 * Response model for admin organization creation.
 */
export const zAdminOrganizationCreateResponse = z.object({
    organizationId: z.string(),
    usersCreated: z.int(),
    buyerProfilesCreated: z.int(),
    companiesCreated: z.int().optional().default(0),
    companiesFound: z.int().optional().default(0),
    accountsCreated: z.int().optional().default(0),
    buyerProfileAssignmentsSucceeded: z.int().optional().default(0)
});

/**
 * AdvisorPayload
 *
 * Schema for the Explorium advisor payload.
 */
export const zAdvisorPayload = z.object({
    'advisor name': z.string(),
    'advisor job type': z.string()
});

/**
 * AgentExecutionEventType
 */
export const zAgentExecutionEventType = z.enum([
    'RUN_STARTED',
    'MODEL_REQUEST',
    'MODEL_RESPONSE',
    'TOOL_CALL_REQUESTED',
    'TOOL_CALL_COMPLETED',
    'PHASE_MILESTONE',
    'RUN_COMPLETED',
    'RUN_FAILED'
]);

/**
 * AgentExecutionStatus
 */
export const zAgentExecutionStatus = z.enum([
    'PENDING',
    'RUNNING',
    'AWAITING_INPUT',
    'COMPLETED',
    'FAILED'
]);

/**
 * AgentRunEventModel
 *
 * One step in an agent run's event log.
 */
export const zAgentRunEventModel = z.object({
    seq: z.int(),
    eventType: zAgentExecutionEventType,
    payload: z.record(z.string(), z.unknown()),
    occurredAt: z.iso.datetime({ offset: true })
});

/**
 * AgentRunDetail
 *
 * Full agent run plus its chronological event log.
 */
export const zAgentRunDetail = z.object({
    id: z.int(),
    agentName: z.string(),
    status: zAgentExecutionStatus,
    inputPrompt: z.string(),
    finalOutput: z.string().nullable(),
    errorMessage: z.string().nullable(),
    startedAt: z.iso.datetime({ offset: true }).nullable(),
    completedAt: z.iso.datetime({ offset: true }).nullable(),
    totalInputTokens: z.int().nullable(),
    totalOutputTokens: z.int().nullable(),
    temporalWorkflowId: z.string(),
    events: z.array(zAgentRunEventModel)
});

/**
 * AgentRunSummary
 *
 * Lean run row for list views.
 */
export const zAgentRunSummary = z.object({
    id: z.int(),
    agentName: z.string(),
    status: zAgentExecutionStatus,
    inputPrompt: z.string(),
    startedAt: z.iso.datetime({ offset: true }).nullable(),
    completedAt: z.iso.datetime({ offset: true }).nullable(),
    temporalWorkflowId: z.string()
});

/**
 * AssignAccountsResponse
 *
 * Response for assign accounts operation.
 */
export const zAssignAccountsResponse = z.object({
    message: z.string(),
    updatedAccounts: z.array(z.int()).nullish(),
    newAssignmentsByUser: z.record(z.string(), z.array(z.int())).nullish(),
    reassignedAccountsCount: z.int().nullish(),
    totalAccountsProcessed: z.int().nullish(),
    accountsShared: z.int().nullish(),
    sharedWithOrgName: z.string().nullish()
});

/**
 * AssignableUser
 *
 * A user who can be assigned to accounts.
 */
export const zAssignableUser = z.object({
    id: z.string(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    email: z.string().nullish(),
    organizationId: z.string(),
    organizationName: z.string(),
    isOwnOrg: z.boolean()
});

/**
 * AssignableUsersResponse
 *
 * Response for the assignable users endpoint.
 */
export const zAssignableUsersResponse = z.object({
    users: z.array(zAssignableUser)
});

/**
 * AssigneeInfo
 *
 * Info about a single assignee (deal participant).
 */
export const zAssigneeInfo = z.object({
    userId: z.string(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    email: z.string().nullish()
});

/**
 * AssignmentContent
 *
 * Typed content for ASSIGNMENT inbox items.
 */
export const zAssignmentContent = z.object({
    assignedByName: z.string().optional().default(''),
    description: z.string().optional().default(''),
    summary: z.string().nullish(),
    suggestedNextSteps: z.array(z.string()).optional()
});

/**
 * BackfillInboxProgress
 *
 * Progress snapshot returned by BackfillInboxWorkflow queries.
 */
export const zBackfillInboxProgress = z.object({
    organizationId: z.string(),
    days: z.int(),
    status: z.string().optional().default('queued'),
    accountsScanned: z.int().optional().default(0),
    eventsScanned: z.int().optional().default(0),
    eventsProcessed: z.int().optional().default(0),
    actionableEvents: z.int().optional().default(0),
    recommendationsCreated: z.int().optional().default(0),
    recommendationsSkipped: z.int().optional().default(0),
    error: z.string().nullish()
});

/**
 * BackfillInboxStartResponse
 *
 * Response returned after starting an inbox backfill workflow.
 */
export const zBackfillInboxStartResponse = z.object({
    workflowId: z.string(),
    runId: z.string(),
    organizationId: z.string(),
    organizationName: z.string(),
    days: z.int()
});

/**
 * BackfillInboxStatusResponse
 *
 * Current Temporal status and progress for an inbox backfill workflow.
 */
export const zBackfillInboxStatusResponse = z.object({
    workflowId: z.string(),
    runId: z.string().nullish(),
    temporalStatus: z.string(),
    progress: zBackfillInboxProgress
});

/**
 * BaseUser
 *
 * Base User model.
 */
export const zBaseUser = z.object({
    id: z.unknown(),
    email: z.email(),
    is_active: z.boolean().optional().default(true),
    is_superuser: z.boolean().optional().default(false),
    is_verified: z.boolean().optional().default(false)
});

/**
 * BatchProcessingStatus
 *
 * Batch processing status for legacy endpoint compatibility.
 */
export const zBatchProcessingStatus = z.object({
    accountId: z.int(),
    overallStatus: z.enum([
        'COMPLETED',
        'FAILED',
        'IN_PROGRESS',
        'PENDING',
        'UNKNOWN'
    ])
});

/**
 * BatchUploadData
 *
 * Metadata for creating a batch upload record.
 */
export const zBatchUploadData = z.object({
    fileName: z.string(),
    fileSize: z.int(),
    fileHash: z.string(),
    uploadedBy: z.string(),
    organizationId: z.string()
});

/**
 * BearerResponse
 */
export const zBearerResponse = z.object({
    access_token: z.string(),
    token_type: z.string()
});

/**
 * Body_auth_bearer_login_auth_bearer_login_post
 */
export const zBodyAuthBearerLoginAuthBearerLoginPost = z.object({
    grant_type: z.string().regex(/^password$/).nullish(),
    username: z.string(),
    password: z.string(),
    scope: z.string().optional().default(''),
    client_id: z.string().nullish(),
    client_secret: z.string().nullish()
});

/**
 * Body_auth_cookie_login_auth_login_post
 */
export const zBodyAuthCookieLoginAuthLoginPost = z.object({
    grant_type: z.string().regex(/^password$/).nullish(),
    username: z.string(),
    password: z.string(),
    scope: z.string().optional().default(''),
    client_id: z.string().nullish(),
    client_secret: z.string().nullish()
});

/**
 * Body_custom_export_parse_template_app_accounts_export_custom_export_parse_template_post
 */
export const zBodyCustomExportParseTemplateAppAccountsExportCustomExportParseTemplatePost = z.object({
    file: z.string()
});

/**
 * Body_magic_link_auth_magic_link_post
 */
export const zBodyMagicLinkAuthMagicLinkPost = z.object({
    email: z.string()
});

/**
 * Body_magic_link_callback_auth_magic_link_callback_post
 */
export const zBodyMagicLinkCallbackAuthMagicLinkCallbackPost = z.object({
    token: z.string()
});

/**
 * Body_parse_validate_csv_text_app_accounts_batch_uploads_parse_validate_csv_text_post
 */
export const zBodyParseValidateCsvTextAppAccountsBatchUploadsParseValidateCsvTextPost = z.object({
    csv_text: z.string()
});

/**
 * Body_parse_validate_file_app_accounts_batch_uploads_parse_validate_file_post
 */
export const zBodyParseValidateFileAppAccountsBatchUploadsParseValidateFilePost = z.object({
    file: z.string()
});

/**
 * Body_parse_validate_superuser_batch_uploads_parse_validate_post
 */
export const zBodyParseValidateSuperuserBatchUploadsParseValidatePost = z.object({
    organization_id: z.string(),
    owner_user_id: z.string(),
    file: z.string()
});

/**
 * Body_reset_forgot_password_auth_forgot_password_post
 */
export const zBodyResetForgotPasswordAuthForgotPasswordPost = z.object({
    email: z.email()
});

/**
 * Body_reset_reset_password_auth_reset_password_post
 */
export const zBodyResetResetPasswordAuthResetPasswordPost = z.object({
    token: z.string(),
    password: z.string()
});

/**
 * Body_transcribe_debrief_app_debrief_transcribe_post
 */
export const zBodyTranscribeDebriefAppDebriefTranscribePost = z.object({
    file: z.string()
});

/**
 * Body_verify_request_token_auth_request_verify_token_post
 */
export const zBodyVerifyRequestTokenAuthRequestVerifyTokenPost = z.object({
    email: z.email()
});

/**
 * Body_verify_verify_auth_verify_post
 */
export const zBodyVerifyVerifyAuthVerifyPost = z.object({
    token: z.string()
});

/**
 * BuyerProfileModel
 *
 * Base model for a buyer profile.
 */
export const zBuyerProfileModel = z.object({
    profileName: z.string().min(1),
    profileDescription: z.string().nullish(),
    industry: z.string().nullish()
});

/**
 * BuyerProfileReturn
 *
 * Model for a buyer profile.
 */
export const zBuyerProfileReturn = z.object({
    profileName: z.string().min(1),
    profileDescription: z.string().nullish(),
    industry: z.string().nullish(),
    id: z.int(),
    ownerUserId: z.string(),
    organizationId: z.string().nullish(),
    organizationName: z.string().nullish(),
    isOwnOrg: z.boolean().optional().default(true),
    accountCount: z.int().optional().default(0)
});

/**
 * BuyerProfileSummary
 *
 * Summary of buyer profile for account list view.
 */
export const zBuyerProfileSummary = z.object({
    id: z.int(),
    profileName: z.string()
});

/**
 * BuyerProfileUpdate
 *
 * Model for updating a buyer profile (tag).
 */
export const zBuyerProfileUpdate = z.object({
    profileName: z.string().min(1).nullish(),
    profileDescription: z.string().nullish(),
    industry: z.string().nullish()
});

/**
 * BuyerProfileUpload
 *
 * Model for buyer profile data.
 */
export const zBuyerProfileUpload = z.object({
    profileName: z.string().min(1),
    profileDescription: z.string().nullish(),
    industry: z.string().nullish(),
    slug: z.string()
});

/**
 * CandidateLead
 *
 * HTTP wire shape for a candidate lead.
 *
 * Pydantic so the FastAPI endpoint can validate incoming JSON and emit
 * camelCase output, and so the generated TypeScript client picks it up.
 * The conversation workflow holds these directly in
 * :attr:`ConversationState.payload`; the activity boundary converts to/from
 * :class:`WorkflowCandidate` only when calling per-query discovery activities.
 */
export const zCandidateLead = z.object({
    company: z.string(),
    website: z.string(),
    location: z.string()
});

/**
 * ChannelPartnerDirection
 *
 * Direction of channel partner relationship relative to the current organization.
 */
export const zChannelPartnerDirection = z.enum(['SUPPLIER', 'DISTRIBUTOR']);

/**
 * ChatAuthResponse
 *
 * Response for the chat authentication.
 */
export const zChatAuthResponse = z.object({
    available: z.boolean(),
    email: z.string().nullish(),
    emailHash: z.string().nullish(),
    fullName: z.string().nullish(),
    shortName: z.string().nullish()
});

/**
 * ChatMessage
 *
 * One entry in the user-visible transcript.
 *
 * Distinct from the pydantic-ai ``message_history`` (which lives in
 * :attr:`ConversationState.history` and is full of provider-specific tool-call
 * plumbing). This is the simple "user said X, assistant said Y" record the
 * React UI renders.
 *
 * ``is_error`` flags an assistant message that's the workflow's synthetic
 * fallback after a terminal activity failure (LLM outage, missing Langfuse
 * prompt, etc.). The frontend uses it to color-switch the banner so the
 * user can tell at a glance that their last turn didn't actually succeed.
 * Always ``False`` for user messages.
 */
export const zChatMessage = z.object({
    role: z.enum(['user', 'assistant']),
    content: z.string(),
    turnIndex: z.int(),
    isError: z.boolean().optional().default(false)
});

/**
 * ChatState
 *
 * Snapshot returned by the ``get_chat_state`` workflow query.
 *
 * Polled by the frontend on a smart interval (1 s while a turn is in flight,
 * 10 s otherwise). ``is_processing`` is true while a turn is *running or
 * queued* — the frontend doesn't need to know the difference, only "should I
 * poll fast and show the typing indicator?".
 */
export const zChatState = z.object({
    transcript: z.array(zChatMessage),
    payload: z.array(zCandidateLead),
    isProcessing: z.boolean(),
    ended: z.boolean(),
    turnCount: z.int()
});

/**
 * CompanyInfoBase
 *
 * Base model for company info.
 */
export const zCompanyInfoBase = z.object({
    companyName: z.string(),
    websiteUrl: z.string().min(1).max(2083).nullish(),
    companyDescription: z.string(),
    yearFounded: z.int().nullish(),
    countryName: z.string(),
    regionName: z.string(),
    naicsCode: z.string(),
    naicsCodeDescription: z.string(),
    qualificationArguments: z.string(),
    qualificationLogic: z.string(),
    emailSystemPrompt: z.string().nullish()
});

/**
 * CompanyInfoResponse
 *
 * Response model for company info.
 */
export const zCompanyInfoResponse = z.object({
    companyName: z.string(),
    websiteUrl: z.string().min(1).max(2083).nullish(),
    companyDescription: z.string(),
    yearFounded: z.int().nullish(),
    countryName: z.string(),
    regionName: z.string(),
    naicsCode: z.string(),
    naicsCodeDescription: z.string(),
    qualificationArguments: z.string(),
    qualificationLogic: z.string(),
    emailSystemPrompt: z.string().nullish(),
    id: z.int(),
    organizationId: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true })
});

/**
 * CompanyInfoUpdate
 *
 * Update type for company info.
 */
export const zCompanyInfoUpdate = z.object({
    organizationId: z.string().nullish(),
    companyName: z.string().nullish(),
    websiteUrl: z.string().min(1).max(2083).nullish(),
    companyDescription: z.string().nullish(),
    yearFounded: z.int().nullish(),
    countryName: z.string().nullish(),
    regionName: z.string().nullish(),
    naicsCode: z.string().nullish(),
    naicsCodeDescription: z.string().nullish(),
    qualificationArguments: z.string().nullish(),
    qualificationLogic: z.string().nullish(),
    emailSystemPrompt: z.string().nullish()
});

/**
 * Competitor
 *
 * Model representing a competitor company.
 */
export const zCompetitor = z.object({
    name: z.string(),
    url: z.string(),
    description: z.string(),
    address: z.string().nullish(),
    isAccount: z.boolean().optional().default(false),
    accountId: z.int().nullish()
});

/**
 * CompetitorList
 *
 * Model representing a list of competitors.
 */
export const zCompetitorList = z.object({
    discriminator: z.literal('COMPETITORS').optional().default('COMPETITORS'),
    competitors: z.array(zCompetitor)
});

/**
 * CompetitorsStatsResponse
 *
 * Response for competitors identification stats.
 */
export const zCompetitorsStatsResponse = z.object({
    identified: z.int(),
    addedAsAccounts: z.int()
});

/**
 * ContactMethod
 *
 * Method of contact.
 */
export const zContactMethod = z.enum([
    'EMAIL',
    'PHONE',
    'MOBILE',
    'LINKEDIN'
]);

/**
 * ContactedActionDetails
 *
 * Details for a contacted action.
 */
export const zContactedActionDetails = z.object({
    action: z.literal('contacted').optional().default('contacted'),
    personasPeopleId: z.int(),
    method: zContactMethod,
    contactedPersonName: z.string().nullish()
});

/**
 * ConversationMessage
 *
 * A single message in the Scout conversation transcript.
 */
export const zConversationMessage = z.object({
    role: z.string(),
    content: z.string()
});

/**
 * ConversationCompleteRequest
 *
 * Payload sent when conversation ends — stores transcript and triggers email.
 */
export const zConversationCompleteRequest = z.object({
    messages: z.array(zConversationMessage),
    extracted_answers: z.record(z.string(), z.string().nullable()).optional().default({}),
    duration_seconds: z.number().nullish()
});

/**
 * ConversationStartResponse
 *
 * Response from conversation/start endpoint.
 */
export const zConversationStartResponse = z.object({
    debrief_id: z.string(),
    signed_url: z.string(),
    agent_id: z.string(),
    dynamic_variables: z.record(z.string(), z.string())
});

/**
 * Coordinate
 *
 * Coordinate parses Latitude and Longitude.
 *
 * You can use the `Coordinate` data type for storing coordinates. Coordinates can be
 * defined using one of the following formats:
 *
 * 1. Tuple: `(Latitude, Longitude)`. For example: `(41.40338, 2.17403)` or `(Decimal('41.40338'), Decimal('2.17403'))`.
 * 2. `Coordinate` instance: `Coordinate(latitude=Latitude, longitude=Longitude)`.
 *
 * ```py
 * from decimal import Decimal
 * from pydantic import BaseModel
 *
 * from pydantic_extra_types.coordinate import Coordinate
 *
 *
 * class Location(BaseModel):
 * coordinate: Coordinate
 *
 *
 * # Using float values
 * location1 = Location(coordinate=(41.40338, 2.17403))
 * # > coordinate=Coordinate(latitude=41.40338, longitude=2.17403)
 *
 * # Using Decimal values
 * location2 = Location(coordinate=(Decimal('41.40338'), Decimal('2.17403')))
 * # > coordinate=Coordinate(latitude=41.40338, longitude=2.17403)
 * ```
 */
export const zCoordinate = z.object({
    latitude: z.union([
        z.number().gte(-90).lte(90),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]),
    longitude: z.union([
        z.number().gte(-180).lte(180),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ])
});

/**
 * CompanyModel
 *
 * Company model.
 */
export const zCompanyModel = z.object({
    id_: z.int(),
    companyName: z.string(),
    websiteDomain: z.string().nullable(),
    coordinate: zCoordinate.nullable(),
    addressCity: z.string().nullable(),
    addressCountry: z.string().nullable(),
    addressCountryLong: z.string().nullable(),
    addressPostalCode: z.string().nullable(),
    addressRegion: z.string().nullable(),
    addressRegionLong: z.string().nullable(),
    addressStreet: z.string().nullable(),
    websiteUrl: z.string().nullable(),
    redirectedUrl: z.string().nullable(),
    websiteScrapeStatus: z.string().nullish()
});

/**
 * CreateDistributorRelationshipRequest
 *
 * Request model for creating a distributor relationship.
 */
export const zCreateDistributorRelationshipRequest = z.object({
    distributorOrgId: z.string(),
    supplierOrgId: z.string().nullish(),
    defaultPocUserId: z.string().nullish()
});

/**
 * CreateMarketResearchReportRequest
 *
 * Request body for creating a new market research report.
 */
export const zCreateMarketResearchReportRequest = z.object({
    profileName: z.string(),
    profileDescription: z.string().nullish(),
    industry: z.string().nullish()
});

/**
 * CreateNoteRequest
 *
 * Request body for creating a note.
 */
export const zCreateNoteRequest = z.object({
    content: z.string().min(1).max(10000)
});

/**
 * CrmCleanupContent
 *
 * Typed content for CRM_CLEANUP inbox items.
 *
 * Fired after the dedupe tool merges companies and at least one affected
 * account has a `crm_account_id`. The admin is expected to manually update
 * their CRM (rename merged company, merge duplicate contacts, etc.).
 */
export const zCrmCleanupContent = z.object({
    canonicalCompanyId: z.int().optional().default(0),
    canonicalCompanyName: z.string().optional().default(''),
    duplicateCompanyIds: z.array(z.int()).optional(),
    duplicateCompanyNames: z.array(z.string()).optional(),
    message: z.string().optional().default('')
});

/**
 * CrmContactDetails
 *
 * Contact information from CRM system via Merge.dev.
 */
export const zCrmContactDetails = z.object({
    id: z.int(),
    mergeId: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    owner: z.string().nullable(),
    addresses: z.array(z.record(z.string(), z.unknown())).nullable(),
    emailAddresses: z.array(z.record(z.string(), z.unknown())).nullable(),
    phoneNumbers: z.array(z.record(z.string(), z.unknown())).nullable(),
    lastActivityAt: z.iso.datetime({ offset: true }).nullable(),
    remoteCreatedAt: z.iso.datetime({ offset: true }).nullable(),
    remoteModifiedAt: z.iso.datetime({ offset: true }).nullable(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true })
});

/**
 * CrmLocation
 *
 * A location from a linked CRM account.
 */
export const zCrmLocation = z.object({
    customerName: z.string(),
    address: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    salesRep: z.string().nullish(),
    crmUrl: z.string().nullish()
});

/**
 * CrmAccountsResponse
 *
 * Response for the CRM accounts endpoint.
 */
export const zCrmAccountsResponse = z.object({
    accountId: z.int(),
    companyName: z.string().nullish(),
    crmLocations: z.array(zCrmLocation),
    hasCrmIntegration: z.boolean().optional().default(false),
    crmName: z.string().nullish()
});

/**
 * CustomExportColumn
 *
 * A single target column in an org's custom export template.
 *
 * Each entry has a `target_header` (the literal column name written to the CSV)
 * plus exactly one of `source_field` (a key into AccountExportRow) or
 * `default_value` (a static string applied to every row). Both null = column
 * intentionally left blank for the user to fill in the grid.
 */
export const zCustomExportColumn = z.object({
    targetHeader: z.string().min(1),
    sourceField: z.string().nullish(),
    defaultValue: z.string().nullish()
});

/**
 * CustomExportGridRow
 *
 * A single row in the preview grid: the account ID plus its values.
 *
 * `values` is keyed by `target_header` to match what the frontend renders.
 */
export const zCustomExportGridRow = z.object({
    accountId: z.int(),
    values: z.record(z.string(), z.string().nullable())
});

/**
 * CustomExportParseResponse
 *
 * Response from parsing an uploaded template CSV/Excel.
 */
export const zCustomExportParseResponse = z.object({
    columns: z.array(z.string())
});

/**
 * CustomExportPreviewResponse
 *
 * Response containing the populated grid rows for a preview.
 */
export const zCustomExportPreviewResponse = z.object({
    columns: z.array(z.string()),
    rows: z.array(zCustomExportGridRow)
});

/**
 * CustomExportSourceField
 *
 * An available SUPPLYCO field the user can map to a target column.
 */
export const zCustomExportSourceField = z.object({
    key: z.string(),
    label: z.string(),
    category: z.string()
});

/**
 * CustomExportTemplate
 *
 * Full custom export template: ordered list of target columns.
 */
export const zCustomExportTemplate = z.object({
    columns: z.array(zCustomExportColumn).max(200)
});

/**
 * CustomExportTemplateResponse
 *
 * Response containing the org's saved template (or null if unset).
 */
export const zCustomExportTemplateResponse = z.object({
    columns: z.array(zCustomExportColumn).nullable()
});

/**
 * DataIssueCategory
 *
 * Categories for data issue reports.
 */
export const zDataIssueCategory = z.enum([
    'CLOSED_BUSINESS',
    'BAD_URL',
    'WRONG_CONTACT_INFO',
    'INCORRECT_ADDRESS',
    'OTHER'
]);

/**
 * DealBucket
 *
 * Coarse stage groupings used by the pipeline tab strip.
 *
 * Maps onto subsets of ``DealStage`` so filtering is cheap (single ``IN``
 * clause) and the URL-visible enum doesn't churn when stages are added.
 *
 * ``ALL`` is the union (no stage filter), used as the default so the
 * pipeline page lands on the full deal list rather than excluding deals
 * that happen to sit in stages outside the narrower ``OPEN`` bucket.
 */
export const zDealBucket = z.enum([
    'all',
    'open',
    'awaiting_reply',
    'in_progress',
    'closed',
    'errors'
]);

/**
 * DealParticipantInfo
 *
 * Lightweight deal participant info for display.
 */
export const zDealParticipantInfo = z.object({
    userId: z.string(),
    name: z.string().nullish(),
    email: z.string().nullish(),
    organizationName: z.string().nullish()
});

/**
 * DealSort
 *
 * Sort keys exposed via ``?sort=…`` on the list endpoint.
 */
export const zDealSort = z.enum([
    'last_activity_at',
    'requested_delivery',
    'stage'
]);

/**
 * DealStage
 *
 * Pipeline stage of an ``OrderDeal``.
 *
 * The Phase 1 foundation only ever sets ``RFQ_RECEIVED`` or ``PO_RECEIVED``
 * (based on the originating document_type). Manual transitions and
 * matching-driven transitions are deferred to the deal-pipeline plan's
 * Phase 2 onward.
 */
export const zDealStage = z.enum([
    'RFQ_RECEIVED',
    'QUOTE_DRAFTED',
    'QUOTE_SENT',
    'PO_RECEIVED',
    'IN_FULFILLMENT',
    'DELIVERED',
    'LOST',
    'CANCELLED'
]);

/**
 * DealStageTransitionRequest
 *
 * Body for POST /app/orders/deals/{id}/stage.
 */
export const zDealStageTransitionRequest = z.object({
    stage: zDealStage,
    note: z.string().nullish()
});

/**
 * DealStageTransitionResponse
 *
 * Result of a stage transition — returns the updated deal summary.
 */
export const zDealStageTransitionResponse = z.object({
    id: z.int(),
    stage: zDealStage,
    lastActivityAt: z.iso.datetime({ offset: true }),
    allowedTransitions: z.array(zDealStage)
});

/**
 * DebriefHistoryItem
 *
 * A single debrief in the history list.
 */
export const zDebriefHistoryItem = z.object({
    id: z.string(),
    transcript: z.string(),
    extracted_fields: z.record(z.string(), z.string().nullable()),
    status: z.string(),
    company_name: z.string().nullable(),
    contact_name: z.string().nullable(),
    recorded_at: z.iso.datetime({ offset: true }).nullable(),
    created_at: z.iso.datetime({ offset: true }),
    conversation_transcript: z.record(z.string(), z.unknown()).nullish()
});

/**
 * DebriefHistoryResponse
 *
 * Response for debrief history endpoint.
 */
export const zDebriefHistoryResponse = z.object({
    items: z.array(zDebriefHistoryItem),
    total: z.int()
});

/**
 * DeleteOrdersRequest
 *
 * Bulk hard-delete request for orders.
 */
export const zDeleteOrdersRequest = z.object({
    orderIds: z.array(z.string()).min(1).max(200)
});

/**
 * DeletedAccount
 *
 * Represents a deleted account.
 */
export const zDeletedAccount = z.object({
    id: z.int(),
    accountName: z.string().nullable()
});

/**
 * DeletedOrder
 *
 * One order deleted by the bulk delete endpoint.
 */
export const zDeletedOrder = z.object({
    id: z.string(),
    poNumber: z.string().nullable()
});

/**
 * DeleteOrdersResponse
 *
 * Summary of a bulk hard-delete request.
 */
export const zDeleteOrdersResponse = z.object({
    deleted: z.array(zDeletedOrder),
    deletedCount: z.int(),
    skippedIds: z.array(z.string())
});

/**
 * DemoDayJoinMember
 *
 * A single user collected on the demoday picker form (primary or team).
 */
export const zDemoDayJoinMember = z.object({
    firstName: z.string().min(1).max(80),
    lastName: z.string().min(1).max(80),
    email: z.email()
});

/**
 * DemoDayJoinRequest
 *
 * Body for POST /demoday/join.
 *
 * ``organization_id`` must reference an org tagged with
 * ``onboarding_data.demoday_event``. ``primary`` is the user who will
 * be auto-logged-in (gets the auth cookie). ``team_members`` is up to
 * ``_MAX_TEAM_MEMBERS`` additional admins to create alongside.
 */
export const zDemoDayJoinRequest = z.object({
    organizationId: z.string(),
    primary: zDemoDayJoinMember,
    teamMembers: z.array(zDemoDayJoinMember).max(4).optional()
});

/**
 * DemoDayJoinResponse
 *
 * Body returned on success. Auth cookie set via Set-Cookie header.
 *
 * ``emails_sent`` counts attempted DEMODAY_INVITE sends (best-effort —
 * transient SES failures don't fail the request).
 */
export const zDemoDayJoinResponse = z.object({
    firstName: z.string(),
    organizationName: z.string(),
    emailsSent: z.int()
});

/**
 * DemoDayOrgEntry
 *
 * One row in the demoday picker dropdown.
 */
export const zDemoDayOrgEntry = z.object({
    id: z.string(),
    name: z.string(),
    website: z.string().min(1).max(2083).nullish()
});

/**
 * DemoDaySignupResponse
 *
 * Body returned on success. Auth cookie set via Set-Cookie header.
 *
 * No plaintext passwords — every user is emailed a single-use
 * set-password link via EmailType.DEMODAY_INVITE.
 */
export const zDemoDaySignupResponse = z.object({
    firstName: z.string(),
    organizationName: z.string(),
    emailsSent: z.int()
});

/**
 * DemoDayTeamMember
 *
 * A single user collected in the demoday signup form (primary or team).
 */
export const zDemoDayTeamMember = z.object({
    firstName: z.string().min(1).max(80),
    lastName: z.string().min(1).max(80),
    email: z.email()
});

/**
 * DemoDaySignupRequest
 *
 * Body for POST /demoday/signup.
 *
 * ``primary`` is the user who will be auto-logged-in (gets the auth
 * cookie) and shown as the dashboard greeting recipient. ``team_members``
 * is an optional list of additional admins created alongside; each is
 * emailed a DEMODAY_INVITE set-password link.
 */
export const zDemoDaySignupRequest = z.object({
    primary: zDemoDayTeamMember,
    teamMembers: z.array(zDemoDayTeamMember).max(4).optional(),
    website: z.string().min(3).max(200)
});

/**
 * DevUser
 *
 * User info for dev switcher.
 */
export const zDevUser = z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    organizationId: z.string().nullable(),
    organizationName: z.string().nullable(),
    role: z.string().nullable(),
    isSuperuser: z.boolean()
});

/**
 * DevUsersResponse
 *
 * Response with all available dev users.
 */
export const zDevUsersResponse = z.object({
    users: z.array(zDevUser)
});

/**
 * DisconnectCrmResponse
 *
 * Response after disconnecting a CRM integration.
 */
export const zDisconnectCrmResponse = z.object({
    message: z.string()
});

/**
 * DismissRequest
 *
 * Request body for dismissing a recommendation.
 */
export const zDismissRequest = z.object({
    reason: z.string().nullish()
});

/**
 * DistributorOrganizationResponse
 *
 * Model for channel partner responses with company information.
 *
 * Used for both directions of the relationship:
 * - DISTRIBUTOR: orgs that distribute FOR us (we are the supplier)
 * - SUPPLIER: orgs that supply TO us (we are the distributor)
 */
export const zDistributorOrganizationResponse = z.object({
    relationshipId: z.string(),
    distributorOrgId: z.string(),
    distributorName: z.string(),
    description: z.string().nullable(),
    direction: zChannelPartnerDirection.optional().default('DISTRIBUTOR'),
    relationshipCreatedAt: z.iso.datetime({ offset: true }).nullable(),
    defaultPocUserId: z.string().nullish(),
    defaultPocUserName: z.string().nullish()
});

/**
 * DocumentType
 *
 * Classification of the inbound document.
 *
 * PURCHASE_ORDER: firm order. Has a PO#, may reference a prior quote.
 * REQUEST_FOR_QUOTE: pricing request, often casual / email body only.
 * QUOTE: outgoing pricing. Informational inbound (rare).
 * UNKNOWN: cannot be classified confidently.
 */
export const zDocumentType = z.enum([
    'PURCHASE_ORDER',
    'REQUEST_FOR_QUOTE',
    'QUOTE',
    'UNKNOWN'
]);

/**
 * DuplicateWithExistingAccount
 *
 * Duplicate with existing account.
 *
 * `will_be_restored` is True when the matched existing account is currently
 * soft-deleted and will be un-deleted when the upload is executed. Used by
 * the frontend to distinguish a true no-op duplicate from a restore.
 */
export const zDuplicateWithExistingAccount = z.object({
    rowIndex: z.int(),
    matchedAccountId: z.int(),
    name: z.string(),
    existingTags: z.array(z.int()),
    willBeRestored: z.boolean()
});

/**
 * DuplicateWithinBatch
 *
 * Duplicate within batch.
 */
export const zDuplicateWithinBatch = z.object({
    rowIndex: z.int(),
    duplicateRowIndex: z.int()
});

/**
 * EmailGenerationResponse
 *
 * API response containing generated email subject and body.
 */
export const zEmailGenerationResponse = z.object({
    subject: z.string(),
    body: z.string()
});

/**
 * EmailPreference
 *
 * A user's preference for a single email type.
 *
 * Stored as JSONB on the User model. Uses str (not EmailType) for email_type
 * so that removed enum values in old JSONB data don't cause validation errors.
 */
export const zEmailPreference = z.object({
    emailType: z.string(),
    enabled: z.boolean().optional().default(true)
});

/**
 * EmailPreferenceResponse
 *
 * A single email preference with metadata from the registry.
 */
export const zEmailPreferenceResponse = z.object({
    emailType: z.string(),
    description: z.string(),
    route: z.string(),
    enabled: z.boolean()
});

/**
 * EmailShareRecipients
 *
 * Who the manual-share email is addressed to.
 */
export const zEmailShareRecipients = z.enum([
    'SELF',
    'ALL_ADMINS',
    'SPECIFIC'
]);

/**
 * EmailShareRequest
 *
 * Request body for the inbox manual-share action.
 *
 * When ``recipients == SPECIFIC``, ``user_ids`` and/or ``emails`` carry the
 * chosen targets. At least one total recipient is required across both lists.
 * All ``user_ids`` must belong to the caller's organization (cross-org IDs
 * are rejected to prevent sharing leaks). Each entry in ``emails`` must be
 * a syntactically valid address.
 */
export const zEmailShareRequest = z.object({
    recipients: zEmailShareRecipients,
    userIds: z.array(z.string()).optional(),
    emails: z.array(z.string()).optional()
});

/**
 * EmailShareResponse
 *
 * Response body echoing how many recipients got the email.
 */
export const zEmailShareResponse = z.object({
    status: z.string(),
    recipientCount: z.int()
});

/**
 * EnrichmentCompleteContent
 *
 * Typed content for ENRICHMENT_COMPLETE inbox items.
 */
export const zEnrichmentCompleteContent = z.object({
    totalAccounts: z.int().optional().default(0),
    highPriorityCount: z.int().optional().default(0),
    failedCount: z.int().optional().default(0),
    message: z.string().optional().default('')
});

/**
 * EnrichmentRetryResponse
 *
 * Response for enrichment retry endpoints.
 */
export const zEnrichmentRetryResponse = z.object({
    workflowId: z.string(),
    message: z.string()
});

/**
 * EnrichmentStatus
 *
 * Contact enrichment status.
 */
export const zEnrichmentStatus = z.enum([
    'EMPTY',
    'COMPLETED',
    'FAILED',
    'MISS'
]);

/**
 * EntitlementsInfo
 *
 * Computed feature entitlements for the current user+org.
 *
 * Returned as part of OrganizationInfoResponse.
 * Computed by build_frontend_permissions() in the organization info endpoint.
 */
export const zEntitlementsInfo = z.object({
    canImportAccounts: z.boolean(),
    canDeleteAccounts: z.boolean(),
    canEditOrgSettings: z.boolean(),
    canShareWithPartners: z.boolean(),
    canManageDistributors: z.boolean(),
    canEditPersonas: z.boolean(),
    canEditOutreach: z.boolean(),
    canExportAccounts: z.boolean(),
    canUseMarketResearch: z.boolean(),
    canUseIntegrations: z.boolean(),
    canUseOrderInbox: z.boolean(),
    canUseScoutLeads: z.boolean(),
    canViewCrm: z.boolean(),
    maxAccounts: z.int().nullable(),
    currentAccountCount: z.int(),
    maxSeats: z.int().nullable(),
    currentSeatCount: z.int()
});

/**
 * EntityStatus
 *
 * Status of a single entity's enrichment workflow.
 */
export const zEntityStatus = z.object({
    entityType: z.enum(['company', 'account']),
    workflowId: z.string(),
    status: z.enum([
        'NOT_STARTED',
        'QUEUED',
        'RUNNING',
        'SUCCEEDED',
        'FAILED'
    ])
});

/**
 * AccountProcessingStatusResponse
 *
 * Response containing workflow statuses for an account's enrichment.
 */
export const zAccountProcessingStatusResponse = z.object({
    accountId: z.int(),
    entities: z.array(zEntityStatus).optional(),
    generatedAt: z.iso.datetime({ offset: true })
});

/**
 * EntryType
 *
 * Type of leaderboard entry.
 */
export const zEntryType = z.enum(['USER', 'DISTRIBUTOR_ORG']);

/**
 * EpaFacility
 *
 * An EPA FRS facility whose point falls inside the building polygon.
 */
export const zEpaFacility = z.object({
    registryId: z.string(),
    name: z.string().nullish(),
    siteType: z.string().nullish(),
    coordMethod: z.string().nullish(),
    naics: z.array(z.string()).nullish()
});

/**
 * EpaAttrs
 *
 * EPA FRS facilities located in the building.
 *
 * Not necessarily the account's own — multi-tenant buildings can list
 * neighbors.
 */
export const zEpaAttrs = z.object({
    facilities: z.array(zEpaFacility).optional(),
    facilityCount: z.int().optional().default(0)
});

/**
 * ErrorModel
 */
export const zErrorModel = z.object({
    detail: z.union([
        z.string(),
        z.record(z.string(), z.string())
    ])
});

/**
 * ExchangeTokenRequest
 *
 * Request to exchange a public token for an account token.
 */
export const zExchangeTokenRequest = z.object({
    publicToken: z.string()
});

/**
 * ExchangeTokenResponse
 *
 * Response after successfully exchanging and storing a token.
 */
export const zExchangeTokenResponse = z.object({
    integrationId: z.int(),
    integrationName: z.string().nullable()
});

/**
 * ExecuteStats
 *
 * Statistics for execute response.
 */
export const zExecuteStats = z.object({
    newAccounts: z.int(),
    newCompaniesCreated: z.int(),
    existingCompaniesFound: z.int(),
    companiesWithErrors: z.int(),
    totalCompaniesInCsv: z.int()
});

/**
 * ExecuteResponse
 *
 * Response for execute endpoint.
 */
export const zExecuteResponse = z.object({
    stats: zExecuteStats,
    accounts: z.array(zAccountSummary),
    message: z.string()
});

/**
 * ExploriumEventType
 */
export const zExploriumEventType = z.enum([
    'ipo_announcement',
    'new_funding_round',
    'new_investment',
    'new_product',
    'new_office',
    'closing_office',
    'new_partnership',
    'increase_in_engineering_department',
    'increase_in_sales_department',
    'increase_in_marketing_department',
    'increase_in_operations_department',
    'increase_in_customer_service_department',
    'increase_in_all_departments',
    'decrease_in_engineering_department',
    'decrease_in_sales_department',
    'decrease_in_marketing_department',
    'decrease_in_operations_department',
    'decrease_in_customer_service_department',
    'decrease_in_all_departments',
    'employee_joined_company',
    'hiring_in_creative_department',
    'hiring_in_education_department',
    'hiring_in_engineering_department',
    'hiring_in_finance_department',
    'hiring_in_health_department',
    'hiring_in_human_resources_department',
    'hiring_in_legal_department',
    'hiring_in_operations_department',
    'hiring_in_professional_service_department',
    'hiring_in_sales_department',
    'hiring_in_support_department',
    'hiring_in_marketing_department',
    'hiring_in_trade_department',
    'hiring_in_unknown_department',
    'company_award',
    'outages_and_security_breaches',
    'cost_cutting',
    'merger_and_acquisitions',
    'lawsuits_and_legal_issues'
]);

/**
 * ExploriumClosingOfficeEvent
 */
export const zExploriumClosingOfficeEvent = z.object({
    eventName: zExploriumEventType.optional().default('closing_office'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    reasonForClosure: z.string().nullish(),
    officeLocation: z.string().nullish(),
    numberOfEmployeesAffected: z.int().nullish()
});

/**
 * ExploriumCompanyAwardEvent
 */
export const zExploriumCompanyAwardEvent = z.object({
    eventName: zExploriumEventType.optional().default('company_award'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    awardName: z.string().nullish(),
    awardReason: z.string().nullish(),
    awardingBody: z.string().nullish()
});

/**
 * ExploriumCostCuttingEvent
 */
export const zExploriumCostCuttingEvent = z.object({
    eventName: zExploriumEventType.optional().default('cost_cutting'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    actionTaken: z.string().nullish(),
    expectedSavingsAmount: z.number().nullish(),
    departmentsAffected: z.array(z.string()).nullish(),
    expectedSavingsCurrency: z.string().nullish()
});

/**
 * ExploriumEmployeeJoinedCompanyEvent
 */
export const zExploriumEmployeeJoinedCompanyEvent = z.object({
    eventName: zExploriumEventType.optional().default('employee_joined_company'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    companyName: z.string().nullish(),
    jobDepartment: z.string().nullish(),
    fullName: z.string().nullish(),
    changeType: z.string().nullish(),
    jobRoleTitle: z.string().nullish(),
    linkedinUrl: z.string().min(1).max(2083).nullish()
});

/**
 * ExploriumFirmographicsModel
 *
 * Explorium firmographics model.
 */
export const zExploriumFirmographicsModel = z.object({
    sicCode: z.string().nullable(),
    sicCodeDescription: z.string().nullable(),
    linkedinIndustryCategory: z.string().nullable(),
    annualRevenueDisplay: z.string().nullable(),
    companyDescription: z.string().nullable(),
    linkedinUrl: z.string().nullable(),
    naicsCode: z.string().nullable(),
    naicsCodeDescription: z.string().nullable(),
    numEmployeesDisplay: z.string().nullable(),
    stockTicker: z.string().nullable()
});

/**
 * ExploriumHiringInEvent
 */
export const zExploriumHiringInEvent = z.object({
    eventName: zExploriumEventType,
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    location: z.string().nullish(),
    jobCount: z.int().nullish(),
    jobTitles: z.array(z.string()).nullish(),
    department: z.enum([
        'creative',
        'education',
        'engineering',
        'finance',
        'health',
        'human_resources',
        'legal',
        'operations',
        'professional_service',
        'sales',
        'support',
        'marketing',
        'trade',
        'unknown'
    ]).nullish()
});

/**
 * ExploriumIncreaseDecreaseEvent
 */
export const zExploriumIncreaseDecreaseEvent = z.object({
    eventName: zExploriumEventType,
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    department: z.enum([
        'engineering',
        'sales',
        'marketing',
        'operations',
        'customer_service',
        'all'
    ]).nullish(),
    departmentChange: z.int().nullish(),
    changeType: z.enum(['increase', 'decrease']).nullish()
});

/**
 * ExploriumIpoAnnouncementEvent
 */
export const zExploriumIpoAnnouncementEvent = z.object({
    eventName: zExploriumEventType.optional().default('ipo_announcement'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    ipoDate: z.iso.datetime({ offset: true }).nullish(),
    companyName: z.string().nullish(),
    offerAmount: z.number().nullish(),
    numberOfShares: z.int().nullish(),
    stockExchange: z.string().nullish(),
    pricePerShare: z.number().nullish(),
    ticker: z.string().nullish(),
    status: z.string().nullish()
});

/**
 * ExploriumLawsuitsAndLegalIssuesEvent
 */
export const zExploriumLawsuitsAndLegalIssuesEvent = z.object({
    eventName: zExploriumEventType.optional().default('lawsuits_and_legal_issues'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    caseType: z.string().nullish(),
    court: z.string().nullish()
});

/**
 * ExploriumMergerAndAcquisitionsEvent
 */
export const zExploriumMergerAndAcquisitionsEvent = z.object({
    eventName: zExploriumEventType.optional().default('merger_and_acquisitions'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    mergerOrAcquisition: z.enum(['merger', 'acquisition']).nullish(),
    description: z.string().nullish(),
    companiesInvolved: z.array(z.string()).nullish(),
    acquisitionPriceAmount: z.number().nullish(),
    strategicObjective: z.string().nullish(),
    acquisitionPriceCurrency: z.string().nullish()
});

/**
 * ExploriumNewFundingRoundEvent
 */
export const zExploriumNewFundingRoundEvent = z.object({
    eventName: zExploriumEventType.optional().default('new_funding_round'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    foundingDate: z.iso.datetime({ offset: true }).nullish(),
    amountRaised: z.number().nullish(),
    foundingStage: z.string().nullish(),
    investors: z.array(z.string()).nullish(),
    leadInvestor: z.string().nullish()
});

/**
 * ExploriumNewInvestmentEvent
 */
export const zExploriumNewInvestmentEvent = z.object({
    eventName: zExploriumEventType.optional().default('new_investment'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    investmentType: z.string().nullish(),
    investmentTarget: z.string().nullish(),
    investmentAmount: z.number().nullish(),
    investmentAmountCurrency: z.string().nullish()
});

/**
 * ExploriumNewOfficeEvent
 */
export const zExploriumNewOfficeEvent = z.object({
    eventName: zExploriumEventType.optional().default('new_office'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    purposeOfNewOffice: z.string().nullish(),
    officeLocation: z.string().nullish(),
    numberOfEmployees: z.int().nullish()
});

/**
 * ExploriumNewPartnershipEvent
 */
export const zExploriumNewPartnershipEvent = z.object({
    eventName: zExploriumEventType.optional().default('new_partnership'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    partnerCompany: z.string().nullish(),
    purposeOfPartnership: z.string().nullish()
});

/**
 * ExploriumNewProductEvent
 */
export const zExploriumNewProductEvent = z.object({
    eventName: zExploriumEventType.optional().default('new_product'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    productName: z.string().nullish(),
    productDescription: z.string().nullish()
});

/**
 * ExploriumOutagesAndSecurityBreachesEvent
 */
export const zExploriumOutagesAndSecurityBreachesEvent = z.object({
    eventName: zExploriumEventType.optional().default('outages_and_security_breaches'),
    link: z.string().min(1).max(2083).nullish(),
    title: z.string().nullish(),
    snippet: z.string().nullish(),
    eventId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/).nullish(),
    data: z.record(z.string(), z.unknown()).nullish(),
    incidentType: z.string().nullish(),
    impactedSystems: z.array(z.string()).nullish(),
    numberOfAffectedCustomers: z.int().nullish()
});

export const zExploriumEventData = z.union([
    zExploriumIpoAnnouncementEvent,
    zExploriumNewFundingRoundEvent,
    zExploriumNewInvestmentEvent,
    zExploriumNewProductEvent,
    zExploriumNewOfficeEvent,
    zExploriumClosingOfficeEvent,
    zExploriumCompanyAwardEvent,
    zExploriumNewPartnershipEvent,
    zExploriumMergerAndAcquisitionsEvent,
    zExploriumHiringInEvent,
    zExploriumIncreaseDecreaseEvent,
    zExploriumEmployeeJoinedCompanyEvent,
    zExploriumLawsuitsAndLegalIssuesEvent,
    zExploriumOutagesAndSecurityBreachesEvent,
    zExploriumCostCuttingEvent
]);

/**
 * ExploriumEvent
 */
export const zExploriumEvent = z.object({
    eventName: zExploriumEventType,
    eventId: z.string().regex(/[0-9a-fA-F]{32}/),
    data: zExploriumEventData,
    eventTime: z.iso.datetime({ offset: true }),
    businessId: z.string().regex(/[0-9a-fA-F]{32}/)
});

/**
 * CompanyEvent
 *
 * Company event record.
 */
export const zCompanyEvent = z.object({
    id: z.int(),
    createdAt: z.iso.datetime({ offset: true }),
    eventName: z.string(),
    businessId: z.string(),
    updatedAt: z.iso.datetime({ offset: true }),
    companyId: z.int(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    link: z.string().min(1).max(2083).nullish(),
    data: zExploriumEvent.nullish()
});

/**
 * AccountHistoryResponse
 *
 * Response containing account history (events, actions, and notes).
 */
export const zAccountHistoryResponse = z.object({
    companyEvents: z.array(zCompanyEvent),
    accountActions: z.array(zAccountAction),
    accountNotes: z.array(zAccountNoteResponse),
    eventsTotal: z.int().nullish(),
    actionsTotal: z.int().nullish()
});

/**
 * CompanyEventResponse
 *
 * Lightweight company event for inline display in recommendations.
 */
export const zCompanyEventResponse = z.object({
    id: z.int(),
    eventName: z.string(),
    eventTime: z.iso.datetime({ offset: true }).nullish(),
    data: zExploriumEvent.nullish()
});

/**
 * CompanyEventContent
 *
 * Typed content for COMPANY_EVENT inbox items.
 */
export const zCompanyEventContent = z.object({
    signalSource: z.string().optional().default(''),
    actionableEventIds: z.array(z.int()).optional(),
    reason: z.string().nullish(),
    talkingPoints: z.array(z.string()).optional(),
    sourceEvents: z.array(zCompanyEventResponse).optional()
});

/**
 * ExportContactsRequest
 *
 * Request body for exporting contacts.
 */
export const zExportContactsRequest = z.object({
    format: z.enum(['csv', 'excel']).optional().default('csv'),
    selectedFields: z.array(z.string()).nullish(),
    accountId: z.int(),
    selectedPersonIds: z.array(z.int()).nullish()
});

/**
 * ExportEmailsResponse
 *
 * Response with email addresses for bulk email.
 */
export const zExportEmailsResponse = z.object({
    emailAddresses: z.array(z.string()),
    accountsCount: z.int(),
    contactsCount: z.int()
});

/**
 * ExportedActionDetails
 *
 * Details for an exported action.
 */
export const zExportedActionDetails = z.object({
    action: z.literal('exported').optional().default('exported'),
    format: z.enum(['CSV', 'EXCEL']),
    count: z.int().nullish(),
    selectedPersonasPeopleIds: z.array(z.int()).nullish()
});

/**
 * ActivityActionRequest
 *
 * Request for activity actions.
 */
export const zActivityActionRequest = z.object({
    accountIds: z.array(z.int()),
    actionType: z.enum(['contacted', 'exported']),
    details: z.union([
        zContactedActionDetails,
        zExportedActionDetails
    ]).nullish()
});

/**
 * ExtractedDebriefFields
 *
 * Fields extracted from a meeting debrief transcript.
 */
export const zExtractedDebriefFields = z.object({
    contact_name: z.string().nullish(),
    company_name: z.string().nullish(),
    meeting_date: z.string().nullish(),
    deal_stage: z.string().nullish(),
    next_steps: z.string().nullish()
});

/**
 * FeedbackEntityType
 *
 * Feedback entity type enum.
 */
export const zFeedbackEntityType = z.enum(['ACCOUNT', 'PERSON']);

/**
 * FeedbackReasonType
 *
 * Feedback reason type enum.
 */
export const zFeedbackReasonType = z.enum([
    'IRRELEVANT',
    'INCORRECT',
    'NOT_DECISION_MAKER',
    'TOO_SMALL',
    'POOR_TIMING',
    'OTHER'
]);

/**
 * FeedbackType
 *
 * Feedback type enum.
 */
export const zFeedbackType = z.enum(['GOOD', 'BAD']);

/**
 * Feedback
 *
 * Feedback model.
 */
export const zFeedback = z.object({
    feedback: zFeedbackType,
    reasonType: zFeedbackReasonType.nullish(),
    entityType: zFeedbackEntityType,
    entityId: z.int(),
    reasonText: z.string().nullish()
});

/**
 * The type of feedback to get
 */
export const zFeedbackTypePath = zFeedbackEntityType;

/**
 * FieldEditUserSummary
 *
 * User who last manually edited an extracted field.
 */
export const zFieldEditUserSummary = z.object({
    id: z.string(),
    displayName: z.string().nullable()
});

/**
 * FieldEdit
 *
 * Latest manual edit metadata for one extracted field.
 */
export const zFieldEdit = z.object({
    editedBy: zFieldEditUserSummary,
    editedAt: z.iso.datetime({ offset: true })
});

/**
 * FieldHighlight
 *
 * Resolved location of one quote in the source document or email.
 */
export const zFieldHighlight = z.object({
    kind: z.enum(['pdf', 'email']),
    attachmentIndex: z.int().nullish(),
    page: z.int().nullish(),
    x1: z.int().nullish(),
    y1: z.int().nullish(),
    x2: z.int().nullish(),
    y2: z.int().nullish(),
    widthPx: z.int().nullish(),
    heightPx: z.int().nullish(),
    start: z.int().nullish(),
    end: z.int().nullish()
});

/**
 * FieldSource
 *
 * One or more highlights for a single extracted field.
 */
export const zFieldSource = z.object({
    highlights: z.array(zFieldHighlight).optional()
});

/**
 * FootprintSource
 *
 * Which open dataset the footprint polygon came from.
 */
export const zFootprintSource = z.enum(['OVERTURE', 'USA_STRUCTURES']);

/**
 * ForwarderStatus
 *
 * Outcome of evaluating who forwarded the inbound email.
 *
 * Set by ``validate_forwarder_activity`` after the org has been resolved
 * by TO at ingest time.
 */
export const zForwarderStatus = z.enum([
    'ALLOWED_REGISTERED_USER',
    'ALLOWED_EXTERNAL',
    'OPEN_NO_ALLOWLIST',
    'BLOCKED_BY_ALLOWLIST'
]);

/**
 * FreshnessCadence
 *
 * Refresh cadence.
 */
export const zFreshnessCadence = z.enum([
    'DAILY',
    'MONTHLY',
    'QUARTERLY',
    'ON_CREATE'
]);

/**
 * CadencePolicy
 *
 * Human-readable cadence policy used by the periodic scheduler.
 */
export const zCadencePolicy = z.object({
    cadence: zFreshnessCadence,
    dueWhen: z.string(),
    staleAfterHours: z.int().nullable()
});

/**
 * FreshnessCategory
 *
 * Enrichment node category.
 */
export const zFreshnessCategory = z.enum([
    'IDENTITY',
    'SOURCE_SYNC',
    'ANALYSIS',
    'ACCOUNT'
]);

/**
 * FreshnessMode
 *
 * Refresh behavior for existing data.
 */
export const zFreshnessMode = z.enum([
    'SKIP_IF_EXISTS',
    'APPEND',
    'WIPE_AND_REDO'
]);

/**
 * FundingRoundsInfo
 *
 * Funding rounds info model.
 */
export const zFundingRoundsInfo = z.object({
    'Lead investors': z.array(z.string()).nullish(),
    'Announcement date': z.iso.date().nullish(),
    'Money raised value': z.number().nullish(),
    'Money raised currency': z.string().nullish(),
    'Money raised value ($)': z.number().nullish()
});

/**
 * GenerateAccountARRequest
 *
 * Request body for generating a single-account AR from a multi-assignment.
 */
export const zGenerateAccountArRequest = z.object({
    accountId: z.int()
});

/**
 * GenerateAccountARResponse
 *
 * Response for the generate-account-ar endpoint.
 */
export const zGenerateAccountArResponse = z.object({
    status: z.string()
});

/**
 * GenerateTamReportRequest
 *
 * Request body for generating a TAM report on behalf of an org.
 */
export const zGenerateTamReportRequest = z.object({
    organizationId: z.string(),
    displayName: z.string().min(1),
    companyDescription: z.string().min(1).nullish()
});

/**
 * GeneratedAccountStatus
 *
 * Status of a single-account AR generation for one account.
 */
export const zGeneratedAccountStatus = z.object({
    status: z.string(),
    publicId: z.string().nullish()
});

/**
 * GeoJSONMultiPolygonGeometry
 *
 * GeoJSON MultiPolygon geometry (WGS84 lon/lat).
 */
export const zGeoJsonMultiPolygonGeometry = z.object({
    type: z.literal('MultiPolygon').optional().default('MultiPolygon'),
    coordinates: z.array(z.array(z.array(z.array(z.number()))))
});

/**
 * GeoJSONPointGeometry
 *
 * GeoJSON Point geometry with typed coordinates.
 */
export const zGeoJsonPointGeometry = z.object({
    type: z.literal('Point').optional().default('Point'),
    coordinates: z.tuple([z.number(), z.number()])
});

/**
 * GetFeedbackResponse
 *
 * Response for get feedback with user info.
 */
export const zGetFeedbackResponse = z.object({
    feedback: zFeedbackType,
    reasonType: zFeedbackReasonType.nullish(),
    entityType: zFeedbackEntityType,
    entityId: z.int(),
    reasonText: z.string().nullish(),
    feedbackUserFirstName: z.string().nullish(),
    feedbackUserLastName: z.string().nullish(),
    feedbackUserEmail: z.string().nullish()
});

/**
 * ImpersonationSession
 *
 * Information about an active impersonation session.
 */
export const zImpersonationSession = z.object({
    tokenId: z.string(),
    adminUserId: z.string(),
    adminEmail: z.string().nullable(),
    impersonatedUserId: z.string(),
    impersonatedUserEmail: z.string().nullable(),
    createdAt: z.iso.datetime({ offset: true })
});

/**
 * ImpersonationSessionList
 *
 * List of active impersonation sessions.
 */
export const zImpersonationSessionList = z.object({
    sessions: z.array(zImpersonationSession)
});

/**
 * ImpersonationStatus
 *
 * Status of the current impersonation session.
 */
export const zImpersonationStatus = z.object({
    isImpersonating: z.boolean(),
    adminUserId: z.string().nullish(),
    adminEmail: z.string().nullish(),
    impersonatedUserId: z.string().nullish(),
    impersonatedUserEmail: z.string().nullish(),
    startedAt: z.iso.datetime({ offset: true }).nullish()
});

/**
 * InboundAddress
 *
 * The configured inbound forwarding address.
 */
export const zInboundAddress = z.object({
    email: z.string()
});

/**
 * InboundConfigResponse
 *
 * Snapshot of an org's inbound-email configuration.
 */
export const zInboundConfigResponse = z.object({
    address: z.string(),
    slug: z.string(),
    allowlist: z.array(z.string()),
    allowlistEnabled: z.boolean()
});

/**
 * InboundConfigUpdateRequest
 *
 * Partial-update payload for an org's inbound-email configuration.
 *
 * The slug is intentionally not editable here — it's provisioned at org
 * creation and only mutable by SupplyCo staff via direct DB access. Letting
 * org admins rename it would let them grab another org's address, break
 * bookmarks customers have already shared with their forwarders, and create
 * audit-trail headaches in `inbound_messages.to_email`.
 */
export const zInboundConfigUpdateRequest = z.object({
    allowlist: z.array(z.string()).nullish(),
    allowlistEnabled: z.boolean().nullish()
});

/**
 * IntegrationStatusResponse
 *
 * Response containing the current CRM integration status.
 */
export const zIntegrationStatusResponse = z.object({
    isConnected: z.boolean(),
    isSyncInProgress: z.boolean().optional().default(false),
    integrationId: z.int().nullish(),
    integrationName: z.string().nullish(),
    integrationSlug: z.string().nullish(),
    createdAt: z.iso.datetime({ offset: true }).nullish(),
    lastSyncedAt: z.iso.datetime({ offset: true }).nullish(),
    accountsSynced: z.int().optional().default(0),
    contactsSynced: z.int().optional().default(0),
    lastSyncFailedCount: z.int().optional().default(0),
    lastSyncLogId: z.int().nullish()
});

/**
 * InvalidRow
 *
 * Invalid row in the upload.
 */
export const zInvalidRow = z.object({
    rowIndex: z.int(),
    error: z.string()
});

/**
 * JobDepartment
 *
 * Job department values as defined by Explorium API.
 *
 * Automatically normalizes to lowercase when parsing from strings,
 * so both 'engineering' and 'Engineering' parse to the same value.
 *
 * Updated as Explorium's profile taxonomy evolves.
 */
export const zJobDepartment = z.enum([
    'administration',
    'c-suite',
    'creative',
    'customer service',
    'customer success',
    'data',
    'design',
    'education',
    'engineering',
    'finance',
    'healthcare',
    'human resources',
    'it',
    'legal',
    'logistics',
    'manufacturing',
    'marketing',
    'operations',
    'partnerships',
    'procurement',
    'product',
    'public relations',
    'public service',
    'r&d',
    'real estate',
    'retail',
    'sales',
    'security',
    'strategy',
    'support',
    'trade'
]);

/**
 * JobSeniorityLevel
 *
 * Job seniority level values as defined by Explorium API.
 *
 * Automatically normalizes to lowercase when parsing from strings,
 * so both 'manager' and 'Manager' parse to the same value.
 */
export const zJobSeniorityLevel = z.enum([
    'owner',
    'cxo',
    'vp',
    'vice president',
    'director',
    'senior',
    'manager',
    'partner',
    'non-managerial',
    'entry',
    'training',
    'unpaid',
    'unknown',
    'president',
    'senior manager',
    'advisor',
    'freelancer',
    'founder',
    'junior',
    'board member',
    'c-suite',
    'senior non-managerial'
]);

/**
 * ContactDetails
 *
 * Contact information for an account.
 */
export const zContactDetails = z.object({
    personId: z.int(),
    prospectId: z.string().regex(/[0-9a-fA-F]{40}/).nullable(),
    fullName: z.string().nullable(),
    jobTitle: z.string().nullable(),
    jobDepartment: zJobDepartment.nullable(),
    jobSeniorityLevels: z.array(zJobSeniorityLevel).nullable(),
    professionalEmail: z.string().nullable(),
    professionalEmailStatus: z.string().nullable(),
    emails: z.array(z.email()).nullable(),
    phoneNumbers: z.array(z.string()).nullable(),
    linkedinUrl: z.string().min(1).max(2083).nullable(),
    functions: z.array(z.string()).nullable(),
    skills: z.array(z.string()).nullable(),
    experience: z.array(z.string()).nullable(),
    interests: z.array(z.string()).nullable(),
    city: z.string().nullable(),
    regionName: z.string().nullable(),
    countryName: z.string().nullable(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
    companyName: z.string().nullable(),
    companyWebsite: z.string().min(1).max(2083).nullable(),
    companyLinkedin: z.string().min(1).max(2083).nullish(),
    enrichmentEnrichedAt: z.iso.datetime({ offset: true }).nullish(),
    enrichmentStatus: zEnrichmentStatus.optional().default('EMPTY'),
    isPrimaryContact: z.boolean().optional().default(false)
});

/**
 * ContactSummary
 *
 * Lightweight contact summary for table views.
 *
 * Contains only the fields needed by the contacts table, omitting
 * heavy array fields (skills, experience, interests, functions, emails)
 * to reduce HTTP payload size.
 */
export const zContactSummary = z.object({
    personId: z.int(),
    fullName: z.string().nullable(),
    jobTitle: z.string().nullable(),
    jobDepartment: zJobDepartment.nullable(),
    jobSeniorityLevels: z.array(zJobSeniorityLevel).nullable(),
    professionalEmail: z.string().nullable(),
    phoneNumbers: z.array(z.string()).nullable(),
    linkedinUrl: z.string().min(1).max(2083).nullable(),
    city: z.string().nullable(),
    regionName: z.string().nullable(),
    countryName: z.string().nullable(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
    enrichmentStatus: zEnrichmentStatus.optional().default('EMPTY'),
    isPrimaryContact: z.boolean().optional().default(false),
    lastActivityAt: z.iso.datetime({ offset: true }).nullish()
});

/**
 * ExploriumFilters
 *
 * Explorium API filter structure for persona targeting.
 */
export const zExploriumFilters = z.object({
    jobDepartments: z.array(zJobDepartment).nullish(),
    jobLevels: z.array(zJobSeniorityLevel).nullish(),
    jobTitles: z.array(z.string()).nullish()
});

/**
 * LaunchAgentRunRequest
 *
 * Body for ``POST /agent-runs/launch``.
 *
 * Most agents accept a freeform ``prompt`` string. ``enrichment_agent``
 * instead takes structured fields (``company_name`` required, with
 * optional ``location`` / ``website``); the router compiles those into
 * a prompt via the ``agents.enrichment-agent`` Langfuse template
 * before starting the workflow.
 *
 * ``contact_agent`` takes ``company_name`` plus ``ideal_buyer`` and
 * ``products_sold`` (both required) along with optional ``address`` /
 * ``website``; the router compiles those through the
 * ``agents.contact-agent`` Langfuse template.
 */
export const zLaunchAgentRunRequest = z.object({
    agentName: z.string(),
    prompt: z.string().nullish(),
    companyName: z.string().nullish(),
    location: z.string().nullish(),
    website: z.string().nullish(),
    address: z.string().nullish(),
    idealBuyer: z.string().nullish(),
    productsSold: z.string().nullish()
});

/**
 * LeadQualification
 *
 * Qualification data for a lead.
 */
export const zLeadQualification = z.object({
    triageScore: z.int().gte(1).lte(10).nullable(),
    triageScoreJustification: z.array(z.string()).nullable(),
    keyStrengths: z.array(z.string()).nullable(),
    keyConcerns: z.array(z.string()).nullable(),
    recommendedNextSteps: z.array(z.string()).nullable(),
    additionalIntelligenceNeeded: z.array(z.string()).nullable()
});

/**
 * LeadSource
 *
 * Source of the lead - how the account was originally acquired.
 */
export const zLeadSource = z.enum([
    'SCOUT',
    'USER_UPLOAD',
    'CRM',
    'COMPETITORS',
    'ADMIN_UPLOAD',
    'SUPPLIERS'
]);

/**
 * AccountLocation
 *
 * Location data for map view.
 *
 * When account_id is None, this represents a company location without
 * an account in the current organization (can be added via quick-create).
 */
export const zAccountLocation = z.object({
    companyId: z.int(),
    accountId: z.int().nullish(),
    name: z.string(),
    description: z.string(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
    address: z.string().nullish(),
    triageScore: z.int().nullish(),
    inCrm: z.boolean().nullish(),
    ownerName: z.string().nullish(),
    logoUrl: z.string().nullish(),
    leadSource: zLeadSource.nullish()
});

/**
 * AccountModel
 *
 * Account model.
 */
export const zAccountModel = z.object({
    id_: z.int(),
    companyId: z.int().nullable(),
    ownerUserId: z.string(),
    organizationId: z.string(),
    accountName: z.string().nullable(),
    accountDescription: z.string().nullable(),
    createdAt: z.iso.datetime({ offset: true }).nullable(),
    updatedAt: z.iso.datetime({ offset: true }).nullable(),
    accountLocation: z.string().nullable(),
    inCrm: z.boolean().nullable(),
    leadQualification: zLeadQualification.nullable(),
    feedback: zFeedback.nullish(),
    assigneeIds: z.array(z.string()).optional(),
    leadSource: zLeadSource.nullish(),
    logoUrl: z.string().nullish()
});

/**
 * GeoJSONFeatureProperties
 *
 * Properties for a map point GeoJSON feature.
 *
 * account_id is nullable because the locations-tab map also plots sibling
 * company locations that don't yet have an account in the current org
 * (offered via an "Add to Accounts" quick-create action). company_id is
 * carried so the frontend can tie a clicked pin back to its sidebar entry
 * and the quick-create mutation.
 */
export const zGeoJsonFeatureProperties = z.object({
    accountId: z.int().nullish(),
    companyId: z.int().nullish(),
    triageScore: z.int().nullish(),
    name: z.string().nullish(),
    address: z.string().nullish(),
    description: z.string().nullish(),
    ownerName: z.string().nullish(),
    organizationId: z.string().nullish(),
    sourceOrganizationName: z.string().nullish(),
    sharedWithDistributorNames: z.array(z.string()).nullish(),
    leadSource: zLeadSource.nullish()
});

/**
 * GeoJSONFeature
 *
 * A complete GeoJSON Feature with typed geometry and properties.
 */
export const zGeoJsonFeature = z.object({
    type: z.literal('Feature').optional().default('Feature'),
    geometry: zGeoJsonPointGeometry,
    properties: zGeoJsonFeatureProperties
});

/**
 * LeaderboardEntry
 *
 * Leaderboard entry for a user or distributor organization.
 */
export const zLeaderboardEntry = z.object({
    id: z.string(),
    entryType: zEntryType.optional().default('USER'),
    representative: z.string(),
    organizationId: z.string().nullish(),
    organizationName: z.string().nullish(),
    locations: z.array(z.string()).optional(),
    assigned: z.int(),
    contacted: z.int(),
    contactedAssigned: z.int(),
    exported: z.int(),
    positiveAccounts: z.int(),
    lastActivity: z.iso.datetime({ offset: true }).nullable(),
    distributorUsers: z.array(z.lazy((): any => zLeaderboardEntry)).nullish(),
    isDeleted: z.boolean().optional().default(false)
});

/**
 * LeaderboardResponse
 *
 * Response for activity leaderboard.
 */
export const zLeaderboardResponse = z.object({
    leaderboardData: z.array(zLeaderboardEntry)
});

/**
 * LinkedInContactDetails
 *
 * Contact details returned from LinkedIn URL lookup.
 *
 * Same fields as ContactDetails but without internal IDs or enrichment metadata,
 * since this is a user-facing lookup by LinkedIn URL.
 */
export const zLinkedInContactDetails = z.object({
    fullName: z.string().nullable(),
    jobTitle: z.string().nullable(),
    jobDepartment: zJobDepartment.nullable(),
    jobSeniorityLevels: z.array(zJobSeniorityLevel).nullable(),
    professionalEmail: z.string().nullable(),
    emails: z.array(z.email()).nullable(),
    phoneNumbers: z.array(z.string()).nullable(),
    linkedinUrl: z.string().min(1).max(2083).nullable(),
    companyName: z.string().nullable()
});

/**
 * LinkedInEnrichRequest
 *
 * Request body for LinkedIn contact lookup.
 */
export const zLinkedInEnrichRequest = z.object({
    linkedinUrl: z.string()
});

/**
 * MapPointsResponse
 *
 * Response containing all map points as GeoJSON FeatureCollection.
 */
export const zMapPointsResponse = z.object({
    type: z.literal('FeatureCollection').optional().default('FeatureCollection'),
    features: z.array(zGeoJsonFeature),
    totalCount: z.int().optional().default(0),
    truncated: z.boolean().optional().default(false)
});

/**
 * MarketMetric
 *
 * Model representing a key market metric with explanation.
 */
export const zMarketMetric = z.object({
    name: z.string(),
    valueType: z.enum([
        'currency',
        'percentage',
        'rating',
        'number',
        'text'
    ]),
    value: z.string(),
    displayValue: z.string(),
    reasoning: z.string()
});

/**
 * MarketResearchReportListItem
 *
 * Lightweight market research report for list views (excludes full_report and sources).
 */
export const zMarketResearchReportListItem = z.object({
    id: z.int(),
    createdAt: z.iso.datetime({ offset: true }),
    lastRefreshedAt: z.iso.datetime({ offset: true }).nullish(),
    profileName: z.string(),
    profileDescription: z.string().nullish(),
    industry: z.string().nullish(),
    processingStatus: z.string().nullish(),
    summary: z.string().nullish(),
    indicators: z.array(zMarketMetric).nullish(),
    overallAssessment: z.string().nullish()
});

/**
 * MatchWithExistingCompany
 *
 * Match with existing company.
 */
export const zMatchWithExistingCompany = z.object({
    rowIndex: z.int(),
    name: z.string(),
    matchedCompanyId: z.int()
});

/**
 * MatchedAccountSummary
 *
 * Minimal account snapshot embedded on OrderDetail when we linked a buyer.
 *
 * Lets the detail panel render a one-line "Account" row (name + link) without
 * a second round-trip to the accounts API.
 */
export const zMatchedAccountSummary = z.object({
    id: z.int(),
    accountName: z.string().nullable()
});

/**
 * MatchedUserInfo
 *
 * Info about a matched SUPPLYCO user.
 */
export const zMatchedUserInfo = z.object({
    id: z.string(),
    name: z.string().nullable(),
    email: z.string()
});

/**
 * CrmUserWithMatch
 *
 * CRM user with matched SUPPLYCO user info.
 */
export const zCrmUserWithMatch = z.object({
    id: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    isActive: z.boolean().nullable(),
    matchedUser: zMatchedUserInfo.nullable()
});

/**
 * CrmUsersResponse
 *
 * Response containing CRM users with their matched SUPPLYCO users.
 */
export const zCrmUsersResponse = z.object({
    crmUsers: z.array(zCrmUserWithMatch)
});

/**
 * MergeLinkTokenRequest
 *
 * Request body for creating a Merge link token.
 */
export const zMergeLinkTokenRequest = z.object({
    integration: z.string().nullish()
});

/**
 * MergeLinkTokenResponse
 *
 * Response containing a Merge link token.
 */
export const zMergeLinkTokenResponse = z.object({
    linkToken: z.string()
});

/**
 * NewsSummary
 *
 * Summary of news articles about a company.
 */
export const zNewsSummary = z.object({
    discriminator: z.literal('NEWS').optional().default('NEWS'),
    summary: z.string(),
    implications: z.string()
});

/**
 * NewsfeedItem
 *
 * Individual newsfeed item.
 */
export const zNewsfeedItem = z.object({
    id: z.int(),
    companyLogo: z.string().nullable(),
    companyName: z.string(),
    companyId: z.int(),
    accountId: z.int(),
    event: zExploriumEvent
});

/**
 * ObservabilityLevel
 *
 * How directly the node's output is tracked in Postgres.
 */
export const zObservabilityLevel = z.enum(['TRACKED', 'NOT_TRACKED']);

/**
 * Offering
 *
 * Model representing a product or service of a company.
 */
export const zOffering = z.object({
    name: z.string(),
    description: z.string(),
    url: z.string().nullish(),
    category: z.string(),
    imgUrl: z.string().nullish()
});

/**
 * OfferingList
 *
 * Container for a list of company offerings with support for len().
 */
export const zOfferingList = z.object({
    discriminator: z.literal('PROJECTS').optional().default('PROJECTS'),
    offerings: z.array(zOffering)
});

/**
 * OnboardingCompleteResponse
 *
 * Response after onboarding completion.
 */
export const zOnboardingCompleteResponse = z.object({
    status: z.literal('sent')
});

/**
 * OrderAttachment
 *
 * Attachment metadata with a short-lived signed S3 URL.
 */
export const zOrderAttachment = z.object({
    filename: z.string(),
    signedUrl: z.string(),
    mimeType: z.string(),
    classification: z.enum([
        'PO',
        'RFQ',
        'QUOTE',
        'OTHER'
    ]).nullable(),
    classificationConfidence: z.number().nullable(),
    isChosen: z.boolean(),
    support: z.enum(['supported', 'unsupported']),
    unsupportedReason: z.string().nullable(),
    pageCount: z.int().nullish()
});

/**
 * OrderBucket
 *
 * Frontend-facing filter buckets (hides the wire enum from the UI).
 */
export const zOrderBucket = z.enum(['pending', 'errors']);

/**
 * OrderLineItemCreate
 *
 * New line item to append.
 */
export const zOrderLineItemCreate = z.object({
    lineNumber: z.int(),
    partNumber: z.string().nullish(),
    description: z.string(),
    quantity: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    unit: z.string().nullish(),
    unitPriceCents: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    totalPriceCents: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish()
});

/**
 * OrderLineItemPatch
 *
 * Sparse update to an existing line item, identified by line_number.
 */
export const zOrderLineItemPatch = z.object({
    lineNumber: z.int(),
    partNumber: z.string().nullish(),
    description: z.string().nullish(),
    quantity: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    unit: z.string().nullish(),
    unitPriceCents: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    totalPriceCents: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish()
});

/**
 * OrderLineItemsUpdate
 *
 * Line-item mutations included in an order update.
 */
export const zOrderLineItemsUpdate = z.object({
    update: z.array(zOrderLineItemPatch).optional(),
    add: z.array(zOrderLineItemCreate).optional(),
    remove: z.array(z.int()).optional()
});

/**
 * OrderReviewIssueCode
 *
 * Machine-readable review issue codes surfaced on order detail.
 */
export const zOrderReviewIssueCode = z.enum([
    'MISSING_PO_NUMBER',
    'LOW_CONFIDENCE_PO_NUMBER',
    'MISSING_REQUIRED_FIELD',
    'MISSING_SHIP_METHOD',
    'AMBIGUOUS_SHIP_METHOD',
    'REQUESTED_DATE_BEFORE_RECEIPT',
    'REQUESTED_DATE_INSIDE_LEAD_TIME',
    'SERVICE_CALL_NO_PRODUCT',
    'LINE_TOTAL_MISMATCH',
    'ORDER_TOTAL_MISMATCH'
]);

/**
 * OrderReviewIssueSeverity
 *
 * How urgently the order desk should handle a review issue.
 */
export const zOrderReviewIssueSeverity = z.enum([
    'info',
    'warning',
    'error'
]);

/**
 * OrderReviewIssue
 *
 * One issue the AI/review layer wants the order desk to resolve.
 */
export const zOrderReviewIssue = z.object({
    code: zOrderReviewIssueCode,
    severity: zOrderReviewIssueSeverity,
    title: z.string(),
    detail: z.string(),
    fieldId: z.string().nullish(),
    lineNumber: z.int().nullish()
});

/**
 * OrderStats
 *
 * Bucket counts for the orders tab badges.
 */
export const zOrderStats = z.object({
    all: z.int(),
    pending: z.int(),
    errors: z.int()
});

/**
 * OrderStatus
 *
 * Lifecycle state of an Order row.
 */
export const zOrderStatus = z.enum([
    'RECEIVED',
    'PARSED',
    'READY_FOR_REVIEW',
    'CONFIRMED',
    'REJECTED',
    'ERROR'
]);

/**
 * OrderListItem
 *
 * Summary row for the orders list view.
 */
export const zOrderListItem = z.object({
    kind: z.literal('order'),
    id: z.string(),
    organizationId: z.string(),
    dealId: z.int().nullable(),
    poNumber: z.string().nullable(),
    customerName: z.string().nullable(),
    shipTo: z.string().nullable(),
    totalValueCents: z.string().nullable(),
    currency: z.string(),
    status: zOrderStatus,
    documentType: zDocumentType,
    referencedQuoteNumber: z.string().nullable(),
    lineItemCount: z.int(),
    extractionConfidence: z.int().nullable(),
    receivedAt: z.iso.datetime({ offset: true }),
    requestedDelivery: z.iso.date().nullable(),
    senderEmail: z.string(),
    senderName: z.string().nullable(),
    reviewIssues: z.array(zOrderReviewIssue).optional()
});

/**
 * OrderListResponse
 *
 * Paginated list of orders.
 */
export const zOrderListResponse = z.object({
    items: z.array(zOrderListItem),
    total: z.int()
});

/**
 * OrderUpdateRequest
 *
 * Sparse header + line-item update for an order.
 */
export const zOrderUpdateRequest = z.object({
    poNumber: z.string().nullish(),
    customerName: z.string().nullish(),
    projectName: z.string().nullish(),
    authorizedBy: z.string().nullish(),
    referencedQuoteNumber: z.string().nullish(),
    documentDate: z.iso.date().nullish(),
    shipTo: z.string().nullish(),
    billTo: z.string().nullish(),
    shipMethod: z.string().nullish(),
    requestedDelivery: z.iso.date().nullish(),
    specialInstructions: z.string().nullish(),
    totalValueCents: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    currency: z.string().nullish(),
    isService: z.boolean().nullish(),
    lineItems: zOrderLineItemsUpdate.nullish()
});

/**
 * OrganizationAccountAdmin
 *
 * Account information for admin account sharing view.
 */
export const zOrganizationAccountAdmin = z.object({
    accountId: z.int(),
    accountName: z.string(),
    companyId: z.int(),
    companyName: z.string(),
    ownerUserId: z.string().nullable(),
    ownerName: z.string().nullable(),
    assigneeIds: z.array(z.string()),
    assigneeNames: z.array(z.string()),
    directlySharedWithCount: z.int()
});

/**
 * OrganizationUserRole
 *
 * User role options for organizations.
 *
 * Role hierarchy: ADMIN > USER > VIEWER
 */
export const zOrganizationUserRole = z.enum([
    'ADMIN',
    'USER',
    'VIEWER'
]);

/**
 * AdminBaseUser
 *
 * Base user for admin endpoints.
 */
export const zAdminBaseUser = z.object({
    id: z.string(),
    email: z.email(),
    is_active: z.boolean().optional().default(true),
    is_superuser: z.boolean().optional().default(false),
    is_verified: z.boolean().optional().default(false),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    initials: z.string().nullish(),
    email_system_prompt: z.string().nullish(),
    organization_id: z.string().nullish(),
    role: zOrganizationUserRole.nullish(),
    role_label: z.string().nullable()
});

/**
 * OrganizationMember
 *
 * Model for organization member responses.
 */
export const zOrganizationMember = z.object({
    id: z.string(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    avatarUrl: z.string().nullish(),
    email: z.string().nullish(),
    role: zOrganizationUserRole,
    isVerified: z.boolean(),
    createdAt: z.iso.datetime({ offset: true }),
    lastLoginAt: z.iso.datetime({ offset: true }).nullish(),
    roleLabel: z.string().readonly()
});

/**
 * CreateMemberResponse
 *
 * Response after creating a member.
 */
export const zCreateMemberResponse = z.object({
    member: zOrganizationMember,
    inviteSent: z.boolean()
});

/**
 * OriginalCompanyData
 *
 * Original company data from CSV/Excel/pasted text/manual input.
 *
 * Does minimal validation, but strips whitespace and adds https to website URLs that need it.
 */
export const zOriginalCompanyData = z.object({
    name: z.string().nullable(),
    websiteUrl: z.string().nullable(),
    address: z.string().nullable()
});

/**
 * AccountsUploadContent
 *
 * Utility class for handling different ways users upload accounts data.
 *
 * Can take a file, a pasted CSV text, or a list of companies.
 * Handles computing hashes, sizes, and content types.
 * Validates that exactly one input is provided.
 *
 * Caution: this will load the content of the file, csv_text, or companies_data into memory.
 * Not good for very large files.
 *
 * Fortunately we limit the size of the file to 10MB in the middleware.
 *
 * Has a method to convert the upload content to a list of original companies.
 * This class's methods will only raise if there's egregiously wrong data in the upload content,
 * such that we can't parse *any* rows into anything resembling a company.
 * Problems with individual rows should be raised downstream in the validation process.
 */
export const zAccountsUploadContent = z.object({
    file: z.string().nullish(),
    csvText: z.string().nullish(),
    companiesData: z.array(zOriginalCompanyData).nullish(),
    content: z.string().nullish()
});

/**
 * OtherLocationsResponse
 *
 * Paginated response for other locations.
 */
export const zOtherLocationsResponse = z.object({
    locations: z.array(zAccountLocation),
    totalCount: z.int(),
    offset: z.int(),
    limit: z.int(),
    hasMore: z.boolean()
});

/**
 * OvertureAttrs
 *
 * Extra Overture building attributes (sparse — most fields often null).
 */
export const zOvertureAttrs = z.object({
    name: z.string().nullish(),
    subtype: z.string().nullish(),
    class: z.string().nullish(),
    numFloors: z.int().nullish(),
    level: z.int().nullish(),
    minHeight: z.number().nullish(),
    roofShape: z.string().nullish(),
    roofMaterial: z.string().nullish(),
    facadeMaterial: z.string().nullish(),
    dataset: z.string().nullish(),
    hasParts: z.boolean().nullish(),
    version: z.int().nullish()
});

/**
 * OwnershipEntry
 *
 * Model representing an ownership entry for a company.
 */
export const zOwnershipEntry = z.object({
    owner: z.string(),
    ownershipPercentage: z.number()
});

/**
 * ExploriumFinanceModel
 *
 * Explorium finance model.
 */
export const zExploriumFinanceModel = z.object({
    domain: z.string().nullable(),
    companyName: z.string().nullable(),
    investors: z.array(z.string()).nullable(),
    currentAdvisors: z.array(zAdvisorPayload).nullable(),
    numOfNews: z.int().nullable(),
    ipoSizeUsd: z.number().nullable(),
    ipoDate: z.iso.datetime({ offset: true }).nullable(),
    isIpo: z.boolean().nullable(),
    numberOfFundingRounds: z.int().nullable(),
    numberOfInvestorsForFirstFundingRound: z.int().nullable(),
    firstFundingRoundValueUsd: z.number().nullable(),
    firstFundingRoundType: z.string().nullable(),
    firstFundingRoundDate: z.iso.datetime({ offset: true }).nullable(),
    latestFundingIncreaseInPercents: z.number().nullable(),
    lastFundingRoundDate: z.iso.datetime({ offset: true }).nullable(),
    lastFundingRoundValueUsd: z.number().nullable(),
    lastFundingRoundType: z.string().nullable(),
    numberOfInvestorsForLastFundingRound: z.int().nullable(),
    acquiredBy: zAcquiredBy.nullable(),
    fundingRoundsInfo: z.array(zFundingRoundsInfo).nullable(),
    knownFundingTotalValue: z.number().nullable(),
    latestFundingAmount: z.number().nullable(),
    latestFundingDate: z.iso.date().nullable(),
    latestFundingType: z.string().nullable(),
    ownershipStructure: z.array(zOwnershipEntry).nullable(),
    totalFunding: z.number().nullable()
});

/**
 * Page[ContactDetails]
 */
export const zPageContactDetails = z.object({
    items: z.array(zContactDetails),
    total: z.int().gte(0),
    page: z.int().gte(1),
    size: z.int().gte(1),
    pages: z.int().gte(0)
});

/**
 * Page[ContactSummary]
 */
export const zPageContactSummary = z.object({
    items: z.array(zContactSummary),
    total: z.int().gte(0),
    page: z.int().gte(1),
    size: z.int().gte(1),
    pages: z.int().gte(0)
});

/**
 * Page[CrmContactDetails]
 */
export const zPageCrmContactDetails = z.object({
    items: z.array(zCrmContactDetails),
    total: z.int().gte(0),
    page: z.int().gte(1),
    size: z.int().gte(1),
    pages: z.int().gte(0)
});

/**
 * Page[NewsfeedItem]
 */
export const zPageNewsfeedItem = z.object({
    items: z.array(zNewsfeedItem),
    total: z.int().gte(0),
    page: z.int().gte(1),
    size: z.int().gte(1),
    pages: z.int().gte(0)
});

/**
 * Page[OrganizationAccountAdmin]
 */
export const zPageOrganizationAccountAdmin = z.object({
    items: z.array(zOrganizationAccountAdmin),
    total: z.int().gte(0),
    page: z.int().gte(1),
    size: z.int().gte(1),
    pages: z.int().gte(0)
});

/**
 * ParseFiltersRequest
 *
 * API request to parse natural language into filters.
 */
export const zParseFiltersRequest = z.object({
    query: z.string().min(1).max(500)
});

/**
 * ParseFiltersResponse
 *
 * API response — inherits filter fields, adds has_filters and camelCase serialization.
 */
export const zParseFiltersResponse = z.object({
    searchTerm: z.string().nullish(),
    selectedRegions: z.array(z.string()).nullish(),
    triageScoreMin: z.int().gte(0).lte(10).nullish(),
    triageScoreMax: z.int().gte(0).lte(10).nullish(),
    selectedIndustryCategories: z.array(z.string()).nullish(),
    selectedEmployeeRanges: z.array(z.string()).nullish(),
    selectedRevenueRanges: z.array(z.string()).nullish(),
    selectedLabels: z.array(z.string()).nullish(),
    selectedSpecialties: z.array(z.string()).nullish(),
    selectedLeadSources: z.array(z.string()).nullish(),
    selectedSharedWithOrgs: z.array(z.string()).nullish(),
    inCrm: z.boolean().nullish(),
    isNew: z.boolean().nullish(),
    feedbackFilter: z.string().nullish(),
    yearFoundedMin: z.int().nullish(),
    yearFoundedMax: z.int().nullish(),
    createdDateFrom: z.string().nullish(),
    createdDateTo: z.string().nullish(),
    hasFilters: z.boolean()
});

/**
 * PartnerDirectoryEntry
 *
 * A single entry in the partner directory.
 */
export const zPartnerDirectoryEntry = z.object({
    id: z.string(),
    name: z.string(),
    logo: z.string(),
    domain: z.string(),
    status: z.string()
});

/**
 * PartnerInviteCreateRequest
 *
 * Request to create a partner invite with bundled accounts.
 */
export const zPartnerInviteCreateRequest = z.object({
    inviteeEmail: z.email(),
    inviteeFirstName: z.string(),
    inviteeLastName: z.string(),
    accountIds: z.array(z.int()).min(1),
    message: z.string().nullish()
});

/**
 * PartnerInviteCreateResponse
 *
 * Response after creating a partner invite.
 */
export const zPartnerInviteCreateResponse = z.object({
    inviteSent: z.boolean(),
    inviteeEmail: z.string(),
    accountCount: z.int()
});

/**
 * PartnerInviteInfoResponse
 *
 * Context returned when a partner clicks an invite link.
 */
export const zPartnerInviteInfoResponse = z.object({
    inviteeEmail: z.string(),
    inviterOrgName: z.string(),
    accountCount: z.int(),
    inviteeFirstName: z.string(),
    inviteeLastName: z.string()
});

/**
 * PartnerSelection
 *
 * A partner selected during onboarding.
 */
export const zPartnerSelection = z.object({
    partnerId: z.string(),
    partnerName: z.string(),
    domain: z.string().nullish()
});

/**
 * PersonaStatus
 *
 * Processing status options for buyer personas.
 *
 * NOTE: Status is now tracked by Temporal workflows, not in the database.
 * This enum is retained for backward compatibility with API responses.
 * The status is computed based on processed_at and filter_json fields.
 */
export const zPersonaStatus = z.enum([
    'PENDING',
    'PROCESSING',
    'READY',
    'ERROR'
]);

/**
 * PersonaResponse
 *
 * Response model for persona with Temporal-derived status.
 */
export const zPersonaResponse = z.object({
    id: z.string(),
    organizationId: z.string(),
    descriptionNl: z.string(),
    hidden: z.boolean(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
    processedAt: z.iso.datetime({ offset: true }).nullish(),
    filterJson: zExploriumFilters.nullish(),
    status: zPersonaStatus
});

/**
 * ProcessingStatus
 *
 * Status of workflow processing for an entity.
 *
 * NULL in the database means not yet started.
 * PENDING means the row exists but the workflow has not started yet.
 * RUNNING means workflow is in progress.
 * COMPLETED/FAILED/COMPLETED_INSUFFICIENT_DATA are terminal states.
 */
export const zProcessingStatus = z.enum([
    'PENDING',
    'RUNNING',
    'COMPLETED',
    'FAILED',
    'COMPLETED_INSUFFICIENT_DATA'
]);

/**
 * AccountGroupSummary
 *
 * Summary row for a domain group in the grouped accounts view.
 */
export const zAccountGroupSummary = z.object({
    domain: z.string(),
    displayName: z.string(),
    companyLogo: z.string().nullish(),
    accountCount: z.int(),
    singleAccountId: z.int().nullish(),
    organizationId: z.string().nullish(),
    tags: z.array(zAccountGroupTag).optional(),
    leadSource: zLeadSource.nullish(),
    feedback: z.string().nullish(),
    reasonType: z.string().nullish(),
    reasonText: z.string().nullish(),
    hasWonOpportunity: z.boolean().nullish(),
    sourceOrganizationName: z.string().nullish(),
    sharedWithDistributorNames: z.array(z.string()).nullish(),
    bestFitScore: z.number().nullish(),
    avgFitScore: z.number().nullish(),
    processingStatus: zProcessingStatus.nullish(),
    participantCount: z.int().optional().default(0),
    locations: z.array(z.string()).optional(),
    contactsCount: z.int().optional().default(0),
    lastContactedAt: z.iso.datetime({ offset: true }).nullish(),
    createdAt: z.iso.datetime({ offset: true }).nullish()
});

/**
 * AccountListItem
 *
 * Account data for list/table view.
 *
 * Slim payload tailored to the fields rendered by the accounts table,
 * grouped table, inbox lists, and their badges. Rich fields (triage
 * rationale, explorium enrichment, full address, etc.) belong on the
 * account detail endpoint, not here.
 */
export const zAccountListItem = z.object({
    id: z.int(),
    accountName: z.string().nullish(),
    accountLocation: z.string().nullish(),
    createdAt: z.iso.datetime({ offset: true }).nullish(),
    inCrm: z.boolean().nullish(),
    hasWonOpportunity: z.boolean().nullish(),
    triageScore: z.int().nullish(),
    assignees: z.array(zAssigneeInfo).optional(),
    organizationId: z.string(),
    feedback: z.string().nullish(),
    reasonType: z.string().nullish(),
    reasonText: z.string().nullish(),
    leadSource: zLeadSource.nullish(),
    processingStatus: zProcessingStatus.nullish(),
    isNew: z.boolean().optional().default(false),
    addressRegion: z.string().nullish(),
    addressCity: z.string().nullish(),
    addressCountry: z.string().nullish(),
    address: z.string().nullish(),
    companyLogo: z.string().nullish(),
    website: z.string().nullish(),
    sourceOrganizationName: z.string().nullish(),
    sharedWithDistributorNames: z.array(z.string()).nullish(),
    buyerProfiles: z.array(zBuyerProfileSummary).nullish(),
    lastContactedAt: z.iso.datetime({ offset: true }).nullish(),
    contactsCount: z.int().optional().default(0)
});

/**
 * GenerateTamReportResponse
 *
 * Response after starting a TAM report generation workflow.
 */
export const zGenerateTamReportResponse = z.object({
    id: z.int(),
    processingStatus: zProcessingStatus
});

/**
 * GroupedAccountsPage
 *
 * Paginated response for grouped accounts view.
 */
export const zGroupedAccountsPage = z.object({
    groups: z.array(zAccountGroupSummary),
    totalGroups: z.int(),
    totalAccounts: z.int(),
    page: z.int(),
    size: z.int()
});

/**
 * Page[AccountListItem]
 */
export const zPageAccountListItem = z.object({
    items: z.array(zAccountListItem),
    total: z.int().gte(0),
    page: z.int().gte(1),
    size: z.int().gte(1),
    pages: z.int().gte(0)
});

/**
 * ProductInput
 *
 * Payload accepted by POST /app/products.
 *
 * All writable product fields — the create endpoint requires sku + name;
 * everything else is optional.
 */
export const zProductInput = z.object({
    sku: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    unitPriceCents: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    unit: z.string().nullish(),
    category: z.string().nullish()
});

/**
 * ProductOutput
 *
 * Product as returned by the API.
 */
export const zProductOutput = z.object({
    sku: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    unitPriceCents: z.string().nullish(),
    unit: z.string().nullish(),
    category: z.string().nullish(),
    id: z.int(),
    organizationId: z.string()
});

/**
 * ProductListResponse
 *
 * Paginated catalog response (also used for full-catalog list).
 */
export const zProductListResponse = z.object({
    items: z.array(zProductOutput),
    total: z.int()
});

/**
 * ProductPatch
 *
 * Partial update payload for PATCH /app/products/{id}.
 *
 * Every field is optional; absent fields are left unchanged. ``sku`` and
 * ``name`` map to NOT NULL columns, so an explicit ``null`` is rejected
 * (otherwise we'd hit an IntegrityError and return a misleading message).
 */
export const zProductPatch = z.object({
    sku: z.string().nullish(),
    name: z.string().nullish(),
    description: z.string().nullish(),
    unitPriceCents: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    unit: z.string().nullish(),
    category: z.string().nullish()
});

/**
 * ProductSummary
 *
 * Minimal product snapshot embedded on a matched OrderLineItem.
 */
export const zProductSummary = z.object({
    id: z.int(),
    sku: z.string(),
    name: z.string(),
    unitPriceCents: z.string().nullable(),
    unit: z.string().nullable()
});

/**
 * OrderLineItemSchema
 *
 * Serialized OrderLineItem for API responses.
 */
export const zOrderLineItemSchema = z.object({
    lineNumber: z.int(),
    partNumber: z.string().nullable(),
    description: z.string(),
    quantity: z.string().nullable(),
    unit: z.string().nullable(),
    unitPriceCents: z.string().nullable(),
    totalPriceCents: z.string().nullable(),
    matchedProductId: z.int().nullable(),
    matchScore: z.string().nullable(),
    matchedProduct: zProductSummary.nullable(),
    sources: z.record(z.string(), zFieldSource).optional(),
    fieldEdits: z.record(z.string(), zFieldEdit).optional()
});

/**
 * OrderDetail
 *
 * Full order detail view (email body + line items + attachments).
 */
export const zOrderDetail = z.object({
    id: z.string(),
    organizationId: z.string(),
    dealId: z.int().nullable(),
    accountId: z.int().nullable(),
    matchedAccount: zMatchedAccountSummary.nullable(),
    poNumber: z.string().nullable(),
    documentType: zDocumentType,
    referencedQuoteNumber: z.string().nullable(),
    projectName: z.string().nullable(),
    authorizedBy: z.string().nullable(),
    customerName: z.string().nullable(),
    shipTo: z.string().nullable(),
    billTo: z.string().nullable(),
    shipMethod: z.string().nullable(),
    requestedDelivery: z.iso.date().nullable(),
    documentDate: z.iso.date().nullable(),
    specialInstructions: z.string().nullable(),
    totalValueCents: z.string().nullable(),
    currency: z.string(),
    isService: z.boolean(),
    extractionConfidence: z.int().nullable(),
    status: zOrderStatus,
    errorMessage: z.string().nullable(),
    erpOrderNumber: z.string().nullable(),
    erpLink: z.string().nullable(),
    receivedAt: z.iso.datetime({ offset: true }),
    processedAt: z.iso.datetime({ offset: true }).nullable(),
    senderEmail: z.string(),
    senderName: z.string().nullable(),
    forwarderStatus: zForwarderStatus.nullable(),
    resolvedUserId: z.string().nullable(),
    subject: z.string().nullable(),
    bodyText: z.string().nullable(),
    bodyHtml: z.string().nullable(),
    previewImageUrl: z.string().nullable(),
    attachments: z.array(zOrderAttachment),
    lineItems: z.array(zOrderLineItemSchema),
    reviewIssues: z.array(zOrderReviewIssue).optional(),
    sources: z.record(z.string(), zFieldSource).optional(),
    fieldEdits: z.record(z.string(), zFieldEdit).optional(),
    requiredFields: z.array(z.string()).optional()
});

/**
 * QuickTrialCreateRequest
 *
 * Request model for quick trial organization creation from email alone.
 */
export const zQuickTrialCreateRequest = z.object({
    email: z.email(),
    firstName: z.string(),
    lastName: z.string()
});

/**
 * QuickTrialCreateResponse
 *
 * Response model for quick trial organization creation.
 */
export const zQuickTrialCreateResponse = z.object({
    organizationId: z.string(),
    message: z.string(),
    companiesCreated: z.int().optional().default(0),
    accountsCreated: z.int().optional().default(0)
});

/**
 * RecommendationCountResponse
 *
 * Response model for recommendation counts.
 */
export const zRecommendationCountResponse = z.object({
    pending: z.int().optional().default(0),
    actioned: z.int().optional().default(0),
    dismissed: z.int().optional().default(0),
    unread: z.int().optional().default(0),
    bySource: z.record(z.string(), z.int()).optional()
});

/**
 * RefinementRequest
 *
 * Request for refining a previously generated email.
 */
export const zRefinementRequest = z.object({
    emailContent: z.string(),
    refinementInstruction: z.string()
});

/**
 * GenerateEmailRequest
 *
 * Request for generating a personalized outreach email.
 */
export const zGenerateEmailRequest = z.object({
    accountId: z.int(),
    recipientName: z.string(),
    recipientTitle: z.string().nullish(),
    recipientEmail: z.string().nullish(),
    companyName: z.string(),
    refinementRequest: zRefinementRequest.nullish()
});

/**
 * RegionResponse
 *
 * A single parsed region (US state or country).
 */
export const zRegionResponse = z.object({
    type: z.string(),
    code: z.string(),
    name: z.string()
});

/**
 * JoinInfoResponse
 *
 * Response with hydration data for invited team members.
 */
export const zJoinInfoResponse = z.object({
    email: z.string(),
    organizationName: z.string(),
    companyTerritory: z.array(zRegionResponse).optional()
});

/**
 * RenewalReportQueuedResponse
 *
 * Response body for a successfully dispatched renewal report.
 *
 * ``user_email`` is echoed back so the frontend can show a "we'll email it
 * to <address>" toast without having to re-read the authenticated user.
 */
export const zRenewalReportQueuedResponse = z.object({
    status: z.string(),
    userEmail: z.string()
});

/**
 * RenewalReportReadyContent
 *
 * Typed content for RENEWAL_REPORT_READY inbox items.
 *
 * Two representations of the report are stored on the row:
 * - ``renewal_report_data``: the raw template_data dict (stats, window
 * dates, org name). Used by the "Email report" inbox action — the
 * send endpoint re-renders + dispatches via EmailService.
 * - ``renewal_report_html``: the pre-rendered email HTML, computed once
 * at generation time. The inbox detail panel reads this directly and
 * drops it in an iframe — no extra round-trip, no second render.
 */
export const zRenewalReportReadyContent = z.object({
    renewalReportTimeframeLabel: z.string().optional().default(''),
    message: z.string().optional().default(''),
    renewalReportData: z.record(z.string(), z.unknown()).optional(),
    renewalReportHtml: z.string().optional().default('')
});

/**
 * RenewalReportTimeframe
 *
 * Admin-selected report window.
 *
 * Kept as a bounded StrEnum so the UI radio-button and backend share one
 * source of truth. Values map to relativedelta deltas in ``resolve_window``.
 */
export const zRenewalReportTimeframe = z.enum([
    '3mo',
    '6mo',
    '1y'
]);

/**
 * RenewalReportRequest
 *
 * Request body for kicking off a renewal report.
 */
export const zRenewalReportRequest = z.object({
    timeframe: zRenewalReportTimeframe
});

/**
 * ReportDataIssueRequest
 *
 * Request to report a data issue.
 */
export const zReportDataIssueRequest = z.object({
    category: zDataIssueCategory,
    description: z.string().nullish(),
    widgetType: z.string().nullish(),
    entityId: z.int().nullish()
});

/**
 * ReportDataIssueResponse
 *
 * Response for data issue report.
 */
export const zReportDataIssueResponse = z.object({
    success: z.boolean(),
    plainThreadId: z.string().nullish()
});

/**
 * ReprocessOrderResponse
 *
 * Response returned after an order is force-reprocessed.
 */
export const zReprocessOrderResponse = z.object({
    orderId: z.string(),
    workflowId: z.string()
});

/**
 * RequiredOrderFieldUpdateRequest
 *
 * Toggle one field required (or not) for a single document type.
 *
 * ``apply_to_all_document_types`` mirrors the dialog's "apply to all"
 * checkbox — when set, the same toggle is applied to every supported
 * document type in one call so the user doesn't have to flip POs and
 * QUOTEs separately.
 */
export const zRequiredOrderFieldUpdateRequest = z.object({
    required: z.boolean(),
    applyToAllDocumentTypes: z.boolean().optional().default(false)
});

/**
 * RequiredOrderFieldsResponse
 *
 * Snapshot of the org's per-document-type required-field config.
 */
export const zRequiredOrderFieldsResponse = z.object({
    fieldsByDocumentType: z.record(z.string(), z.array(z.string()))
});

/**
 * RerunTriageResponse
 *
 * Response model for re-running triage analysis.
 */
export const zRerunTriageResponse = z.object({
    success: z.boolean(),
    message: z.string(),
    workflowsStarted: z.int().optional().default(0),
    accountsCount: z.int().optional().default(0),
    clearedAccountsCount: z.int().optional().default(0)
});

/**
 * ResearchingAccountsResponse
 *
 * Response for count of accounts currently being researched.
 */
export const zResearchingAccountsResponse = z.object({
    count: z.int()
});

/**
 * RespondToAgentRunRequest
 *
 * Body for ``POST /agent-runs/{run_id}/respond``.
 *
 * Used by interactive scripted demos (e.g. demo_2) to signal a paused
 * workflow that the human-in-the-loop has approved/denied the current
 * request. The string is forwarded verbatim to the workflow's
 * ``respond`` signal handler.
 */
export const zRespondToAgentRunRequest = z.object({
    decision: z.string()
});

/**
 * RetryOrderResponse
 *
 * Response returned after an errored order is requeued.
 */
export const zRetryOrderResponse = z.object({
    orderId: z.string(),
    workflowId: z.string()
});

/**
 * RobotApplication
 *
 * Robot applications an account could buy for (Kawasaki taxonomy, v0).
 */
export const zRobotApplication = z.enum([
    'PALLETIZING',
    'PACKAGING',
    'PICK_AND_PLACE',
    'MACHINE_TENDING',
    'ARC_WELDING',
    'SPOT_WELDING',
    'PAINTING_COATING',
    'DISPENSING_SEALING',
    'FINISHING',
    'ASSEMBLY',
    'HEAVY_PAYLOAD_HANDLING',
    'CLEANROOM_WAFER'
]);

/**
 * AccountsFilterState
 *
 * Filter state for accounts - shared between Field and export.
 */
export const zAccountsFilterState = z.object({
    searchTerm: z.string().nullish(),
    searchScope: z.enum(['names', 'everything']).optional().default('names'),
    ownerFilter: z.string().nullish(),
    assigneeFilter: z.array(z.string()).nullish(),
    triageScoreMin: z.int().gte(0).lte(10).nullish(),
    triageScoreMax: z.int().gte(0).lte(10).nullish(),
    inCrm: z.boolean().nullish(),
    createdDateFrom: z.iso.datetime({ offset: true }).nullish(),
    createdDateTo: z.iso.datetime({ offset: true }).nullish(),
    selectedBusinessCategories: z.array(z.string()).nullish(),
    selectedIndustryCategories: z.array(z.string()).nullish(),
    selectedIndustriesServed: z.array(z.string()).nullish(),
    selectedEmployeeRanges: z.array(z.string()).nullish(),
    selectedRevenueRanges: z.array(z.string()).nullish(),
    selectedNaicsCodes: z.array(z.string()).nullish(),
    selectedNaicsPrefixes: z.array(z.string()).nullish(),
    selectedApplications: z.array(zRobotApplication).nullish(),
    selectedSicCodes: z.array(z.string()).nullish(),
    selectedRegions: z.array(z.string()).nullish(),
    selectedCountries: z.array(z.string()).nullish(),
    selectedCommercialStructures: z.array(z.string()).nullish(),
    selectedLabels: z.array(z.string()).nullish(),
    selectedSpecialties: z.array(z.string()).nullish(),
    yearFoundedMin: z.int().nullish(),
    yearFoundedMax: z.int().nullish(),
    selectedProfileIds: z.array(z.int()).nullish(),
    feedbackFilter: z.enum([
        'GOOD',
        'BAD',
        'NONE'
    ]).nullish(),
    isNew: z.boolean().nullish(),
    selectedLeadSources: z.array(z.string()).nullish(),
    selectedSharedWithOrgs: z.array(z.string()).nullish(),
    selectedSharedByOrgs: z.array(z.string()).nullish(),
    accountIds: z.array(z.int()).nullish(),
    excludedFilterFields: z.array(z.enum([
        'assigneeFilter',
        'feedbackFilter',
        'isNew',
        'selectedBusinessCategories',
        'selectedIndustryCategories',
        'selectedIndustriesServed',
        'selectedEmployeeRanges',
        'selectedRevenueRanges',
        'selectedNaicsCodes',
        'selectedSicCodes',
        'selectedRegions',
        'selectedCountries',
        'selectedCommercialStructures',
        'selectedLabels',
        'selectedSpecialties',
        'selectedProfileIds',
        'selectedLeadSources',
        'selectedSharedWithOrgs',
        'selectedSharedByOrgs'
    ])).nullish(),
    includePregenerated: z.boolean().optional().default(false)
});

/**
 * AccountFacetOptionsRequest
 *
 * Request for one account facet's selectable values.
 */
export const zAccountFacetOptionsRequest = z.object({
    filters: zAccountsFilterState.optional(),
    search: z.string().max(100).nullish(),
    limit: z.int().gte(1).lte(100).optional().default(50)
});

/**
 * AccountsTableFilterState
 *
 * Filter state for accounts table view.
 */
export const zAccountsTableFilterState = z.object({
    searchTerm: z.string().nullish(),
    searchScope: z.enum(['names', 'everything']).optional().default('names'),
    ownerFilter: z.string().nullish(),
    assigneeFilter: z.array(z.string()).nullish(),
    triageScoreMin: z.int().gte(0).lte(10).nullish(),
    triageScoreMax: z.int().gte(0).lte(10).nullish(),
    inCrm: z.boolean().nullish(),
    createdDateFrom: z.iso.datetime({ offset: true }).nullish(),
    createdDateTo: z.iso.datetime({ offset: true }).nullish(),
    selectedBusinessCategories: z.array(z.string()).nullish(),
    selectedIndustryCategories: z.array(z.string()).nullish(),
    selectedIndustriesServed: z.array(z.string()).nullish(),
    selectedEmployeeRanges: z.array(z.string()).nullish(),
    selectedRevenueRanges: z.array(z.string()).nullish(),
    selectedNaicsCodes: z.array(z.string()).nullish(),
    selectedNaicsPrefixes: z.array(z.string()).nullish(),
    selectedApplications: z.array(zRobotApplication).nullish(),
    selectedSicCodes: z.array(z.string()).nullish(),
    selectedRegions: z.array(z.string()).nullish(),
    selectedCountries: z.array(z.string()).nullish(),
    selectedCommercialStructures: z.array(z.string()).nullish(),
    selectedLabels: z.array(z.string()).nullish(),
    selectedSpecialties: z.array(z.string()).nullish(),
    yearFoundedMin: z.int().nullish(),
    yearFoundedMax: z.int().nullish(),
    selectedProfileIds: z.array(z.int()).nullish(),
    feedbackFilter: z.enum([
        'GOOD',
        'BAD',
        'NONE'
    ]).nullish(),
    isNew: z.boolean().nullish(),
    selectedLeadSources: z.array(z.string()).nullish(),
    selectedSharedWithOrgs: z.array(z.string()).nullish(),
    selectedSharedByOrgs: z.array(z.string()).nullish(),
    accountIds: z.array(z.int()).nullish(),
    excludedFilterFields: z.array(z.enum([
        'assigneeFilter',
        'feedbackFilter',
        'isNew',
        'selectedBusinessCategories',
        'selectedIndustryCategories',
        'selectedIndustriesServed',
        'selectedEmployeeRanges',
        'selectedRevenueRanges',
        'selectedNaicsCodes',
        'selectedSicCodes',
        'selectedRegions',
        'selectedCountries',
        'selectedCommercialStructures',
        'selectedLabels',
        'selectedSpecialties',
        'selectedProfileIds',
        'selectedLeadSources',
        'selectedSharedWithOrgs',
        'selectedSharedByOrgs'
    ])).nullish(),
    includePregenerated: z.boolean().optional().default(false),
    sortColumn: z.enum([
        'triage_score',
        'account_name',
        'address_country',
        'assignees',
        'created_at',
        'last_contacted_at',
        'contacts_count',
        'location',
        'distance',
        'sharing',
        'tags',
        'website'
    ]).optional().default('triage_score'),
    sortAscending: z.boolean().optional().default(false),
    userLatitude: z.number().gte(-90).lte(90).nullish(),
    userLongitude: z.number().gte(-180).lte(180).nullish(),
    groupByDomain: z.boolean().optional().default(false)
});

/**
 * Body_unassign_accounts_app_accounts_assign__delete
 */
export const zBodyUnassignAccountsAppAccountsAssignDelete = z.object({
    accounts: z.union([
        z.array(z.int()),
        zAccountsFilterState
    ]),
    target_user_ids: z.array(z.string())
});

/**
 * HeatMapCell
 *
 * One application x industry cell.
 */
export const zHeatMapCell = z.object({
    application: zRobotApplication,
    naics3: z.string(),
    naicsLabel: z.string(),
    weightedCount: z.number(),
    accountCount: z.int(),
    statedCount: z.int()
});

/**
 * HeatMapRequest
 *
 * Heat map request: the Accounts page filters plus the high-fit cutoff.
 */
export const zHeatMapRequest = z.object({
    filters: zAccountsFilterState.nullish(),
    minFitScore: z.int().gte(0).lte(10).optional().default(7)
});

/**
 * HeatMapResponse
 *
 * All non-empty cells of the heat map.
 */
export const zHeatMapResponse = z.object({
    cells: z.array(zHeatMapCell)
});

/**
 * RollbackLastScoutClaimResponse
 *
 * Response after rolling back the latest SCOUT claim.
 */
export const zRollbackLastScoutClaimResponse = z.object({
    actionId: z.int(),
    rolledBackCount: z.int(),
    scoutRunIds: z.array(z.int()),
    recommendationsUpdated: z.int(),
    message: z.string()
});

/**
 * RunStatusResponse
 *
 * Status of a scout discovery run.
 */
export const zRunStatusResponse = z.object({
    runId: z.int(),
    status: z.string(),
    leadsDiscovered: z.int(),
    isComplete: z.boolean(),
    errorMessage: z.string().nullish(),
    startedAt: z.iso.datetime({ offset: true }),
    completedAt: z.iso.datetime({ offset: true }).nullish()
});

/**
 * DashboardResponse
 *
 * Scout dashboard stats.
 */
export const zDashboardResponse = z.object({
    pregeneratedCount: z.int(),
    latestRun: zRunStatusResponse.nullish(),
    batchFrequencyDays: z.int().optional().default(7),
    nextBatchAt: z.iso.datetime({ offset: true }).nullish(),
    scoutLeadsItemPublicId: z.string().nullish()
});

/**
 * SaveDebriefRequest
 *
 * Request to save a completed debrief.
 */
export const zSaveDebriefRequest = z.object({
    transcript: z.string(),
    extracted_fields: zExtractedDebriefFields,
    company_name: z.string().nullish(),
    contact_name: z.string().nullish(),
    recorded_at: z.iso.datetime({ offset: true }).nullish()
});

/**
 * SchedulerInfo
 *
 * Periodic scheduler metadata.
 */
export const zSchedulerInfo = z.object({
    scheduleId: z.string(),
    workflowName: z.string(),
    timezone: z.string(),
    runTimeUtc: z.string(),
    overlapPolicy: z.string()
});

/**
 * ScoutLeadItem
 *
 * A single lead from scout discovery.
 */
export const zScoutLeadItem = z.object({
    accountId: z.int(),
    scoutRunId: z.int().nullish(),
    companyName: z.string().nullish(),
    websiteUrl: z.string().nullish(),
    country: z.string().nullish(),
    city: z.string().nullish(),
    region: z.string().nullish(),
    leadStatus: z.string().nullish(),
    createdAt: z.iso.datetime({ offset: true })
});

/**
 * ClaimLeadsResponse
 *
 * Response from claiming pre-generated leads.
 */
export const zClaimLeadsResponse = z.object({
    claimedCount: z.int(),
    items: z.array(zScoutLeadItem)
});

/**
 * ScoutLeadsReadyContent
 *
 * Typed content for SCOUT_LEADS_READY inbox items.
 */
export const zScoutLeadsReadyContent = z.object({
    totalLeads: z.int().optional().default(0),
    scoutRunId: z.int().optional().default(0),
    message: z.string().optional().default('')
});

/**
 * ShareAccountViaEmailResponse
 *
 * Response containing rendered email content.
 */
export const zShareAccountViaEmailResponse = z.object({
    subject: z.string(),
    text: z.string(),
    html: z.string(),
    recipientEmails: z.array(z.string())
});

/**
 * ShareAccountsRequest
 *
 * Request for share accounts operation.
 */
export const zShareAccountsRequest = z.object({
    accountIds: z.union([
        z.array(z.int()),
        zAccountsFilterState
    ]),
    targetOrganizationId: z.string(),
    message: z.string().nullish()
});

/**
 * ShareAccountsResponse
 *
 * Response for share accounts operation.
 */
export const zShareAccountsResponse = z.object({
    message: z.string(),
    sharedCount: z.int().nullish(),
    emailSent: z.boolean().optional().default(false)
});

/**
 * ShareSectionKey
 *
 * Optional sections the user can include in the shared account email.
 *
 * The set of sections shown by default is a UX decision owned by the frontend;
 * this endpoint renders exactly the sections the client requests. Company
 * overview (fit score / revenue / size) and personal message are always on.
 */
export const zShareSectionKey = z.enum([
    'triageJustification',
    'productionStatus',
    'productionSummary',
    'productionReport',
    'keyStrengths',
    'keyConcerns',
    'competitors'
]);

/**
 * ShareAccountViaEmailRequest
 *
 * Request body for rendering account share email template.
 */
export const zShareAccountViaEmailRequest = z.object({
    recipientNames: z.array(z.string()).min(1),
    recipientEmails: z.array(z.string()).min(1),
    senderName: z.string(),
    personalMessage: z.string().nullish(),
    selectedContactIds: z.array(z.int()).nullish(),
    includedSections: z.array(zShareSectionKey).optional()
});

/**
 * SkippedAccount
 *
 * Represents an account that was skipped during deletion.
 */
export const zSkippedAccount = z.object({
    id: z.int(),
    accountName: z.string().nullable(),
    reason: z.string()
});

/**
 * DeleteAccountsResponse
 *
 * Response for delete accounts operation.
 */
export const zDeleteAccountsResponse = z.object({
    message: z.string(),
    deletedAccounts: z.array(zDeletedAccount),
    deletedCount: z.int().optional().default(0),
    skippedAccounts: z.array(zSkippedAccount).nullish()
});

/**
 * Source
 *
 * Model representing a research source (URL citation).
 */
export const zSource = z.object({
    url: z.string(),
    title: z.string()
});

/**
 * MarketResearchReportResponse
 *
 * Market research report response.
 */
export const zMarketResearchReportResponse = z.object({
    id: z.int(),
    createdAt: z.iso.datetime({ offset: true }),
    lastRefreshedAt: z.iso.datetime({ offset: true }).nullish(),
    profileName: z.string(),
    profileDescription: z.string().nullish(),
    industry: z.string().nullish(),
    processingStatus: z.string().nullish(),
    fullReport: z.string().nullish(),
    summary: z.string().nullish(),
    indicators: z.array(zMarketMetric).nullish(),
    overallAssessment: z.string().nullish(),
    sources: z.array(zSource).nullish()
});

/**
 * RampingProductionModel
 *
 * Model for analyzing if a company is ramping up production.
 */
export const zRampingProductionModel = z.object({
    discriminator: z.literal('PRODUCTION_REPORT').optional().default('PRODUCTION_REPORT'),
    detailedReport: z.string(),
    summary: z.string(),
    isRampingUp: z.boolean(),
    confidence: z.enum([
        'high',
        'medium',
        'low'
    ]),
    sources: z.array(zSource).nullish()
});

/**
 * AccountDetailsResponse
 *
 * Comprehensive account details response.
 */
export const zAccountDetailsResponse = z.object({
    account: zAccountModel,
    company: zCompanyModel,
    finance: zExploriumFinanceModel.nullable(),
    misc: zAccountDetailsMiscModel.nullable(),
    firmographics: zExploriumFirmographicsModel.nullable(),
    projects: zOfferingList.nullish(),
    productionReport: zRampingProductionModel.nullish(),
    competitors: zCompetitorList.nullish(),
    news: zNewsSummary.nullish(),
    logoUrl: z.string().nullish(),
    hasWonOpportunity: z.boolean().nullish(),
    location: zAccountLocation.nullish(),
    buyerProfiles: z.array(zBuyerProfileReturn),
    contactsCount: z.int(),
    hasBusinessId: z.boolean(),
    sourceOrganizationName: z.string().nullish(),
    sharedWithDistributorNames: z.array(z.string()).nullish(),
    dealParticipants: z.array(zDealParticipantInfo).optional()
});

/**
 * StartImpersonationRequest
 *
 * Request to start impersonation.
 */
export const zStartImpersonationRequest = z.object({
    organizationId: z.string(),
    userId: z.string()
});

/**
 * StatusBreakdown
 *
 * Count and oldest account date for one processing status.
 */
export const zStatusBreakdown = z.object({
    status: z.string().nullable(),
    count: z.int(),
    oldestAccountAt: z.iso.datetime({ offset: true }).nullable()
});

/**
 * OrgEnrichmentStatus
 *
 * Per-org enrichment status breakdown.
 */
export const zOrgEnrichmentStatus = z.object({
    orgId: z.string(),
    orgName: z.string(),
    totalAccounts: z.int(),
    statuses: z.array(zStatusBreakdown)
});

/**
 * EnrichmentStatusResponse
 *
 * Top-level payload for the enrichment status dashboard.
 */
export const zEnrichmentStatusResponse = z.object({
    generatedAt: z.iso.datetime({ offset: true }),
    totalAccounts: z.int(),
    statusSummary: z.array(zStatusBreakdown),
    organizations: z.array(zOrgEnrichmentStatus)
});

/**
 * SubscriptionTier
 *
 * Subscription tier options for organizations.
 */
export const zSubscriptionTier = z.enum([
    'TRIAL',
    'PAID',
    'DISTRIBUTOR'
]);

/**
 * OrganizationBase
 *
 * Model for updating an organization.
 */
export const zOrganizationBase = z.object({
    name: z.string().min(1).max(255),
    description: z.string().nullish(),
    subscriptionTier: zSubscriptionTier.nullish(),
    maxAccounts: z.int().gte(0).nullish(),
    maxSeats: z.int().gte(0).nullish()
});

/**
 * OrganizationInfo
 *
 * Organization information.
 */
export const zOrganizationInfo = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    maxAccounts: z.int().nullish(),
    maxSeats: z.int().nullish(),
    maxContactEnrichmentsPerMonth: z.int().nullish(),
    contactEnrichmentsUsedThisMonth: z.int().optional().default(0),
    scoutLeadsPerBatch: z.int().nullish(),
    scoutBatchFrequencyDays: z.int().nullish(),
    subscriptionTier: zSubscriptionTier.nullish(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true })
});

/**
 * AdminOrganizationsResponse
 *
 * Response with all organizations and their users.
 */
export const zAdminOrganizationsResponse = z.object({
    organizations: z.array(zOrganizationInfo),
    usersByOrg: z.record(z.string(), z.array(zAdminBaseUser)),
    integrationsByOrg: z.record(z.string(), zActiveMergeIntegrationInfo.nullable())
});

/**
 * OrganizationInfoResponse
 *
 * Response model for organization info endpoint.
 */
export const zOrganizationInfoResponse = z.object({
    organization: zOrganizationBase,
    companyInfo: zCompanyInfoBase.nullable(),
    members: z.array(zOrganizationMember),
    entitlements: zEntitlementsInfo
});

/**
 * SuperuserImpersonationStatus
 *
 * Status of the current superuser impersonation session.
 */
export const zSuperuserImpersonationStatus = z.object({
    isImpersonating: z.boolean(),
    adminUserId: z.string().nullish(),
    adminEmail: z.string().nullish(),
    impersonatedUserId: z.string().nullish(),
    impersonatedUserEmail: z.string().nullish(),
    startedAt: z.iso.datetime({ offset: true }).nullish()
});

/**
 * SuperuserUserResponse
 *
 * User response for superuser endpoints.
 */
export const zSuperuserUserResponse = z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    initials: z.string().nullable(),
    emailSystemPrompt: z.string().nullable(),
    isActive: z.boolean(),
    isSuperuser: z.boolean(),
    isVerified: z.boolean(),
    role: zOrganizationUserRole.nullable(),
    organizationId: z.string().nullable()
});

/**
 * SuperuserUserUpdate
 *
 * Full user update schema for superusers.
 *
 * Superusers can update any field on a user.
 */
export const zSuperuserUserUpdate = z.object({
    email: z.email().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    avatarUrl: z.string().nullish(),
    initials: z.string().nullish(),
    emailSystemPrompt: z.string().nullish(),
    isActive: z.boolean().nullish(),
    isVerified: z.boolean().nullish(),
    role: zOrganizationUserRole.nullish(),
    organizationId: z.string().nullish()
});

/**
 * SupplierCreateRequest
 *
 * Request body for creating an account from supplier lookup data.
 */
export const zSupplierCreateRequest = z.object({
    name: z.string(),
    websiteUrl: z.string().nullish(),
    address: z.string().nullish(),
    sourceAccountId: z.int()
});

/**
 * SupplierResultResponse
 *
 * A single supplier result.
 */
export const zSupplierResultResponse = z.object({
    name: z.string(),
    description: z.string(),
    website: z.string().nullish(),
    productsSupplied: z.array(z.string()).optional(),
    location: z.string().nullish(),
    isAccount: z.boolean().optional().default(false),
    accountId: z.int().nullish()
});

/**
 * SupplierLookupResponse
 *
 * Response for a supplier lookup.
 */
export const zSupplierLookupResponse = z.object({
    id: z.int(),
    accountId: z.int(),
    status: z.string(),
    companyNameQueried: z.string(),
    suppliers: z.array(zSupplierResultResponse).optional(),
    errorMessage: z.string().nullish(),
    createdAt: z.string()
});

/**
 * SyncCrmResponse
 *
 * Response after starting a CRM sync.
 */
export const zSyncCrmResponse = z.object({
    workflowId: z.string(),
    message: z.string()
});

/**
 * SyncRequest
 *
 * Request to sync accounts from a Merge integration.
 */
export const zSyncRequest = z.object({
    integrationId: z.int()
});

/**
 * SyncResponse
 *
 * Response after starting a Merge sync workflow.
 */
export const zSyncResponse = z.object({
    workflowId: z.string(),
    message: z.string()
});

/**
 * TagAssignment
 *
 * Tag assignment for a company.
 */
export const zTagAssignment = z.object({
    companyIndex: z.int(),
    tagId: z.int()
});

/**
 * TagAssociation
 *
 * Represents a single tag association between a buyer profile and an account.
 *
 * In select-all mode, account_id can be omitted - the tag will be applied
 * to all accounts resolved from the filter.
 *
 * Use remove=True to remove the tag instead of adding it.
 */
export const zTagAssociation = z.object({
    buyerProfileId: z.int(),
    accountId: z.int().nullish(),
    remove: z.boolean().optional().default(false)
});

/**
 * Body_update_tag_associations_app_buyer_profiles_associations_patch
 */
export const zBodyUpdateTagAssociationsAppBuyerProfilesAssociationsPatch = z.object({
    accounts: z.union([
        z.array(z.int()),
        zAccountsFilterState
    ]),
    associations: z.array(zTagAssociation).nullish()
});

/**
 * TagAssociationsResponse
 *
 * Response for tag association updates.
 */
export const zTagAssociationsResponse = z.object({
    message: z.string(),
    accountsUpdated: z.int(),
    associationsCreated: z.int()
});

/**
 * TamReportDetail
 *
 * Full TAM report response including tam_data.
 */
export const zTamReportDetail = z.object({
    id: z.int(),
    displayName: z.string(),
    companyDescription: z.string(),
    processingStatus: z.string().nullish(),
    config: z.record(z.string(), z.unknown()).nullish(),
    tamData: z.record(z.string(), z.unknown()).nullish(),
    createdAt: z.iso.datetime({ offset: true })
});

/**
 * TamReportReadyContent
 *
 * Typed content for TAM_REPORT_READY inbox items.
 */
export const zTamReportReadyContent = z.object({
    tamReportId: z.int().optional().default(0),
    tamDisplayName: z.string().optional().default(''),
    message: z.string().optional().default('')
});

/**
 * RecommendationResponse
 *
 * Response model for a single action recommendation.
 */
export const zRecommendationResponse = z.object({
    id: z.int(),
    publicId: z.string(),
    status: z.string(),
    source: z.string(),
    accountIds: z.array(z.int()),
    content: z.union([
        zCompanyEventContent,
        zAssignmentContent,
        zEnrichmentCompleteContent,
        zScoutLeadsReadyContent,
        zTamReportReadyContent,
        zRenewalReportReadyContent,
        zCrmCleanupContent
    ]).nullish(),
    accountName: z.string().nullish(),
    isMultiAccountActionable: z.boolean().optional().default(false),
    isRead: z.boolean().optional().default(false),
    readAt: z.iso.datetime({ offset: true }).nullish(),
    dismissed: z.boolean().optional().default(false),
    actionedByUserId: z.string().nullish(),
    actionedByUserName: z.string().nullish(),
    actionedAt: z.iso.datetime({ offset: true }).nullish(),
    createdAt: z.iso.datetime({ offset: true })
});

/**
 * PaginatedRecommendationsResponse
 *
 * Paginated response for listing recommendations.
 */
export const zPaginatedRecommendationsResponse = z.object({
    items: z.array(zRecommendationResponse),
    nextCursor: z.string().nullish()
});

/**
 * TamReportSummary
 *
 * Lightweight TAM report for list views.
 */
export const zTamReportSummary = z.object({
    id: z.int(),
    displayName: z.string(),
    processingStatus: z.string().nullish(),
    hasConfig: z.boolean().optional().default(false),
    hasTamData: z.boolean().optional().default(false),
    createdAt: z.iso.datetime({ offset: true })
});

/**
 * TerritoryParseRequest
 *
 * Request to parse free-text territory input into structured regions.
 */
export const zTerritoryParseRequest = z.object({
    text: z.string().max(500)
});

/**
 * TerritoryParseResponse
 *
 * Response with parsed regions from territory text.
 */
export const zTerritoryParseResponse = z.object({
    regions: z.array(zRegionResponse)
});

/**
 * TimelineDataPoint
 *
 * A single data point in the timeline chart.
 */
export const zTimelineDataPoint = z.object({
    date: z.iso.date(),
    count: z.int()
});

/**
 * AccountsReviewedTimelineResponse
 *
 * Response for accounts reviewed timeline chart data.
 */
export const zAccountsReviewedTimelineResponse = z.object({
    data: z.array(zTimelineDataPoint),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    total: z.int()
});

/**
 * TimelineSeriesEntry
 *
 * A series of data points for a single rep/distributor.
 */
export const zTimelineSeriesEntry = z.object({
    id: z.string(),
    name: z.string(),
    entryType: zEntryType.optional().default('USER'),
    data: z.array(zTimelineDataPoint)
});

/**
 * ActivityTimelineResponse
 *
 * Response for activity timeline chart data.
 */
export const zActivityTimelineResponse = z.object({
    series: z.array(zTimelineSeriesEntry),
    startDate: z.iso.date(),
    endDate: z.iso.date()
});

/**
 * TranscribeResponse
 *
 * Response from the transcribe endpoint.
 */
export const zTranscribeResponse = z.object({
    transcript: z.string(),
    extracted_fields: zExtractedDebriefFields
});

/**
 * TriggerPath
 *
 * How a node is normally triggered.
 */
export const zTriggerPath = z.enum(['ON_CREATE_WORKFLOW', 'PERIODIC_SCHEDULER']);

/**
 * FreshnessNodeMetric
 *
 * Freshness metrics for one enrichment node.
 */
export const zFreshnessNodeMetric = z.object({
    nodeName: z.string(),
    displayName: z.string(),
    category: zFreshnessCategory,
    cadence: zFreshnessCadence,
    refreshMode: zFreshnessMode,
    triggerPath: zTriggerPath,
    sourceTable: z.string(),
    entityScope: z.string(),
    observability: zObservabilityLevel,
    eligibleEntities: z.int().nullable(),
    entitiesWithData: z.int().nullable(),
    missingEntities: z.int().nullable(),
    staleEntities: z.int().nullable(),
    coveragePct: z.number().nullable(),
    rawRecordsCount: z.int().nullable(),
    latestRecordAt: z.iso.datetime({ offset: true }).nullable(),
    staleAfterHours: z.int().nullable(),
    note: z.string().nullish()
});

/**
 * FreshnessDashboardResponse
 *
 * Top-level payload for the superuser freshness dashboard.
 */
export const zFreshnessDashboardResponse = z.object({
    generatedAt: z.iso.datetime({ offset: true }),
    periodicScheduler: zSchedulerInfo,
    cadencePolicies: z.array(zCadencePolicy),
    nodes: z.array(zFreshnessNodeMetric)
});

/**
 * TriggerScoutLeadGenerationRequest
 *
 * Request to trigger manual scout lead generation.
 */
export const zTriggerScoutLeadGenerationRequest = z.object({
    leadsNeeded: z.int().gte(1).lte(500)
});

/**
 * TriggerScoutLeadGenerationResponse
 *
 * Response after triggering scout lead generation.
 */
export const zTriggerScoutLeadGenerationResponse = z.object({
    workflowId: z.string(),
    runId: z.int(),
    message: z.string()
});

/**
 * TruncationInfo
 *
 * Information about truncated records for trial organizations.
 */
export const zTruncationInfo = z.object({
    originalCount: z.int(),
    processedCount: z.int(),
    truncated: z.int(),
    remainingSlots: z.int(),
    currentCount: z.int(),
    maxAccounts: z.int()
});

/**
 * TurnAcceptedResponse
 *
 * Response from ``POST /lead-discovery/turn``.
 *
 * Returns immediately once Temporal has durably accepted the message. The
 * frontend's polling ``/state`` query is the source of truth for the
 * transcript, payload, and processing flag — this response carries no
 * transcript data because the turn hasn't run yet.
 */
export const zTurnAcceptedResponse = z.object({
    conversationId: z.string()
});

/**
 * TurnRequest
 *
 * Body for ``POST /lead-discovery/turn`` — send a user message.
 *
 * If ``conversation_id`` is omitted, the endpoint allocates a fresh id and
 * opens a new conversation atomically with the first message. If supplied,
 * the endpoint reuses the existing workflow.
 */
export const zTurnRequest = z.object({
    conversationId: z.string().nullish(),
    userMessage: z.string()
});

/**
 * UpdateDistributorRelationshipRequest
 *
 * Request model for updating a distributor relationship.
 */
export const zUpdateDistributorRelationshipRequest = z.object({
    defaultPocUserId: z.string().nullish()
});

/**
 * UpdateEmailPreferencesRequest
 *
 * Request body for updating email preferences.
 */
export const zUpdateEmailPreferencesRequest = z.object({
    preferences: z.array(zEmailPreference)
});

/**
 * UpdateMemberRoleRequest
 *
 * Request body for updating a member's role.
 */
export const zUpdateMemberRoleRequest = z.object({
    role: zOrganizationUserRole
});

/**
 * UpdateOrganizationRequest
 *
 * Request to update organization settings.
 */
export const zUpdateOrganizationRequest = z.object({
    maxAccounts: z.int().nullish(),
    maxSeats: z.int().nullish(),
    maxContactEnrichmentsPerMonth: z.int().gte(0).nullish(),
    scoutLeadsPerBatch: z.int().gte(1).nullish(),
    scoutBatchFrequencyDays: z.int().gte(1).nullish(),
    name: z.string().nullish(),
    description: z.string().nullish(),
    subscriptionTier: zSubscriptionTier.nullish()
});

/**
 * UsaStructuresAttrs
 *
 * Extra USA Structures attributes (address-attributed footprints).
 */
export const zUsaStructuresAttrs = z.object({
    propAddr: z.string().nullish(),
    propCity: z.string().nullish(),
    propZip: z.string().nullish(),
    sqmeters: z.number().nullish(),
    src: z.string().nullish(),
    imageDate: z.string().nullish()
});

/**
 * FootprintSourceAttrs
 *
 * Per-source extra attributes mirrored from the source_attrs JSONB.
 */
export const zFootprintSourceAttrs = z.object({
    overture: zOvertureAttrs.nullish(),
    usaStructures: zUsaStructuresAttrs.nullish(),
    epa: zEpaAttrs.nullish()
});

/**
 * BuildingFootprintResponse
 *
 * Building footprint polygon and display attributes for a company.
 */
export const zBuildingFootprintResponse = z.object({
    geometry: zGeoJsonMultiPolygonGeometry,
    areaSqm: z.number(),
    source: zFootprintSource,
    sourceRelease: z.string(),
    heightM: z.number().nullish(),
    occCls: z.string().nullish(),
    primOcc: z.string().nullish(),
    sourceAttrs: zFootprintSourceAttrs.nullish()
});

/**
 * UserCreationRequest
 *
 * Model for user data in organization creation.
 */
export const zUserCreationRequest = z.object({
    email: z.email(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    role: zOrganizationUserRole.optional().default('USER'),
    sendInvite: z.boolean().optional().default(true)
});

/**
 * AdminOrganizationCreateRequest
 *
 * Request model for admin organization creation.
 */
export const zAdminOrganizationCreateRequest = z.object({
    organizationName: z.string(),
    companyInfo: zCompanyInfoBase,
    users: z.array(zUserCreationRequest),
    subscriptionTier: zSubscriptionTier,
    maxAccounts: z.int(),
    maxSeats: z.int().nullish(),
    buyerProfiles: z.array(zBuyerProfileUpload).optional(),
    leadSource: zLeadSource.optional().default('USER_UPLOAD')
});

/**
 * Body_create_organization_superuser_create__post
 */
export const zBodyCreateOrganizationSuperuserCreatePost = z.object({
    request: zAdminOrganizationCreateRequest,
    contents: zAccountsUploadContent.nullish()
});

/**
 * UserSummary
 *
 * Minimal user representation embedded on deal rows + events.
 */
export const zUserSummary = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string()
});

/**
 * DealDetail
 *
 * Hero block on the deal-detail page.
 *
 * Same fields the list row carries plus the latest order's public_id so
 * the left column can preview a document by default.
 */
export const zDealDetail = z.object({
    id: z.int(),
    title: z.string().nullable(),
    customerName: z.string().nullable(),
    customerEmail: z.string().nullable(),
    shipTo: z.string().nullable(),
    assignee: zUserSummary.nullable(),
    stage: zDealStage,
    requestedDelivery: z.iso.date().nullable(),
    totalValueCents: z.string().nullable(),
    lastActivityAt: z.iso.datetime({ offset: true }),
    closedAt: z.iso.datetime({ offset: true }).nullable(),
    documentCount: z.int(),
    allowedTransitions: z.array(zDealStage),
    latestOrderPublicId: z.string().nullable()
});

/**
 * DealEvent
 *
 * One entry on a deal's timeline.
 *
 * Events are derived (not stored) — see ``timeline.py`` for the source
 * rows that produce each kind. ``payload_id`` + ``payload_kind``
 * together let the frontend build a deep-link to the originating row.
 */
export const zDealEvent = z.object({
    kind: z.enum([
        'RFQ_IN',
        'QUOTE_OUT',
        'PO_IN',
        'EMAIL_IN',
        'EMAIL_OUT',
        'STAGE_CHANGE',
        'ERROR'
    ]),
    occurredAt: z.iso.datetime({ offset: true }),
    actor: zUserSummary.nullable(),
    summary: z.string(),
    payloadId: z.string().nullable(),
    payloadKind: z.enum([
        'order',
        'email',
        'stage_change'
    ]).nullable(),
    fromStage: zDealStage.nullish(),
    toStage: zDealStage.nullish(),
    note: z.string().nullish()
});

/**
 * DealEventsResponse
 *
 * Detail + ordered timeline for a single deal (newest first).
 */
export const zDealEventsResponse = z.object({
    deal: zDealDetail,
    events: z.array(zDealEvent)
});

/**
 * DealListItem
 *
 * One row in the deal pipeline table.
 *
 * Fields are picked to populate the entire pipeline-table row without
 * a side-fetch per row (mirrors the pattern used by ``OrderListItem``).
 */
export const zDealListItem = z.object({
    id: z.int(),
    title: z.string().nullable(),
    customerName: z.string().nullable(),
    customerEmail: z.string().nullable(),
    shipTo: z.string().nullable(),
    assignee: zUserSummary.nullable(),
    stage: zDealStage,
    requestedDelivery: z.iso.date().nullable(),
    totalValueCents: z.string().nullable(),
    lastActivityAt: z.iso.datetime({ offset: true }),
    documentCount: z.int(),
    lastEventKind: z.enum([
        'RFQ_IN',
        'QUOTE_OUT',
        'PO_IN',
        'EMAIL_IN',
        'EMAIL_OUT',
        'STAGE_CHANGE',
        'ERROR'
    ]),
    lastEventAt: z.iso.datetime({ offset: true }),
    allowedTransitions: z.array(zDealStage)
});

/**
 * DealListResponse
 *
 * Paginated deal list.
 */
export const zDealListResponse = z.object({
    items: z.array(zDealListItem),
    total: z.int()
});

/**
 * UserTerritoryRequest
 *
 * A named territory with its constituent regions.
 */
export const zUserTerritoryRequest = z.object({
    name: z.string(),
    regions: z.array(zRegionResponse)
});

/**
 * JoinCompleteRequest
 *
 * Request to complete the join flow for invited team members.
 */
export const zJoinCompleteRequest = z.object({
    token: z.string(),
    password: z.string().min(8),
    firstName: z.string(),
    lastName: z.string(),
    userTerritories: z.array(zUserTerritoryRequest).optional()
});

/**
 * OnboardingCompleteRequest
 *
 * Request to finalize onboarding and create pending org.
 */
export const zOnboardingCompleteRequest = z.object({
    email: z.email(),
    password: z.string().min(8),
    firstName: z.string(),
    lastName: z.string(),
    organizationName: z.string(),
    companyDomain: z.string(),
    companyDescription: z.string().nullish(),
    qualificationArguments: z.string().nullish(),
    qualificationLogic: z.string().nullish(),
    countryName: z.string().nullish(),
    regionName: z.string().nullish(),
    companyTerritory: z.array(zRegionResponse).optional(),
    userTerritories: z.array(zUserTerritoryRequest).optional(),
    teamInvites: z.array(z.email()).optional(),
    selectedPartners: z.array(zPartnerSelection).optional(),
    partnerInviteToken: z.string().nullish()
});

/**
 * ValidatedCompany
 *
 * Validated company.
 */
export const zValidatedCompanyInput = z.object({
    name: z.string(),
    websiteUrl: z.string().min(1).max(2083),
    address: z.string().optional().default(''),
    rowIndex: z.int()
});

/**
 * ExecuteBatchUploadRequest
 *
 * HTTP body for /execute; includes validation token from parse-validate.
 */
export const zExecuteBatchUploadRequest = z.object({
    validationToken: z.string().min(8),
    newLeads: z.array(zValidatedCompanyInput),
    matchesWithExistingCompanies: z.array(zMatchWithExistingCompany),
    duplicateWithExistingAccounts: z.array(zDuplicateWithExistingAccount),
    tags: z.array(zTagAssignment),
    batchUploadData: zBatchUploadData,
    leadSource: zLeadSource
});

/**
 * SuperuserExecuteBatchUploadRequest
 *
 * HTTP execute body including validation token from parse-validate.
 */
export const zSuperuserExecuteBatchUploadRequest = z.object({
    validationToken: z.string().min(8),
    organizationId: z.string(),
    ownerUserId: z.string(),
    newLeads: z.array(zValidatedCompanyInput),
    matchesWithExistingCompanies: z.array(zMatchWithExistingCompany),
    duplicateWithExistingAccounts: z.array(zDuplicateWithExistingAccount),
    batchUploadData: zBatchUploadData,
    leadSource: zLeadSource
});

/**
 * ValidatedCompany
 *
 * Validated company.
 */
export const zValidatedCompanyOutput = z.object({
    name: z.string(),
    websiteUrl: z.string().min(1).max(2083),
    address: z.string().optional().default(''),
    rowIndex: z.int(),
    websiteDomain: z.string().readonly()
});

/**
 * ValidationError
 */
export const zValidationError = z.object({
    loc: z.array(z.union([z.string(), z.int()])),
    msg: z.string(),
    type: z.string()
});

/**
 * HTTPValidationError
 */
export const zHttpValidationError = z.object({
    detail: z.array(zValidationError).optional()
});

/**
 * ViewportBounds
 *
 * Geographic bounding box for viewport-based filtering.
 */
export const zViewportBounds = z.object({
    north: z.number().gte(-90).lte(90),
    south: z.number().gte(-90).lte(90),
    east: z.number().gte(-180).lte(180),
    west: z.number().gte(-180).lte(180)
});

/**
 * CustomExportDownloadRequest
 *
 * Request body for streaming the final custom export CSV.
 */
export const zCustomExportDownloadRequest = z.object({
    filters: z.union([
        zAccountsFilterState,
        z.array(z.int())
    ]),
    viewportBounds: zViewportBounds.nullish(),
    edits: z.record(z.string(), z.record(z.string(), z.string())).optional(),
    filename: z.string().nullish(),
    format: z.enum(['csv', 'excel']).optional().default('csv')
});

/**
 * ExportAccountsRequest
 *
 * Request body for exporting accounts.
 */
export const zExportAccountsRequest = z.object({
    format: z.enum(['csv', 'excel']).optional().default('csv'),
    selectedFields: z.array(z.string()).nullish(),
    filters: z.union([
        zAccountsFilterState,
        z.array(z.int())
    ]),
    mode: z.enum(['accounts', 'contacts']).optional().default('accounts'),
    filename: z.string().nullish(),
    notifyOnComplete: z.boolean().optional().default(false),
    notificationEmail: z.email().nullish(),
    viewportBounds: zViewportBounds.nullish()
});

/**
 * ExportEmailsRequest
 *
 * Request body for getting email addresses for bulk email.
 */
export const zExportEmailsRequest = z.object({
    filters: z.union([
        zAccountsFilterState,
        z.array(z.int())
    ]),
    viewportBounds: zViewportBounds.nullish()
});

/**
 * MapPointsRequest
 *
 * Request body for the accounts map-points endpoint.
 *
 * ``viewport_bounds`` scopes the result to the map's visible area server-side.
 * Without it the endpoint would have to return every account in the
 * organization, which is what made this path time out (ENG-3632).
 */
export const zMapPointsRequest = z.object({
    filters: zAccountsFilterState.optional(),
    viewportBounds: zViewportBounds.nullish()
});

/**
 * WarningRow
 *
 * Warning row in the upload.
 */
export const zWarningRow = z.object({
    rowIndex: z.int(),
    warning: z.string()
});

/**
 * SuperuserParseValidateResponse
 *
 * Response for superuser parse-validate endpoint.
 */
export const zSuperuserParseValidateResponse = z.object({
    validationToken: z.string(),
    invalidRecords: z.array(zInvalidRow),
    warningRecords: z.array(zWarningRow),
    duplicatesWithinBatch: z.array(zDuplicateWithinBatch),
    newLeads: z.array(zValidatedCompanyOutput),
    matchesWithExistingCompanies: z.array(zMatchWithExistingCompany),
    duplicateWithExistingAccounts: z.array(zDuplicateWithExistingAccount),
    batchUploadData: zBatchUploadData,
    leadSource: zLeadSource
});

/**
 * XlsxSheet
 *
 * One visible sheet from an xlsx attachment, rendered as a string grid.
 */
export const zXlsxSheet = z.object({
    title: z.string(),
    columnCount: z.int(),
    rows: z.array(z.array(z.string()))
});

/**
 * XlsxAttachmentData
 *
 * Parsed cells for an xlsx attachment, used by the order detail preview.
 */
export const zXlsxAttachmentData = z.object({
    sheets: z.array(zXlsxSheet)
});

/**
 * _CustomExportFiltersRequest
 *
 * Request body for custom export endpoints that operate on a set of accounts.
 */
export const zCustomExportFiltersRequest = z.object({
    filters: z.union([
        zAccountsFilterState,
        z.array(z.int())
    ]),
    viewportBounds: zViewportBounds.nullish()
});

/**
 * _ParseValidateResponse
 *
 * Response for parse-validate endpoint.
 */
export const zParseValidateResponse = z.object({
    validationToken: z.string(),
    invalidRecords: z.array(zInvalidRow),
    warningRecords: z.array(zWarningRow),
    duplicatesWithinBatch: z.array(zDuplicateWithinBatch),
    newLeads: z.array(zValidatedCompanyOutput),
    matchesWithExistingCompanies: z.array(zMatchWithExistingCompany),
    duplicateWithExistingAccounts: z.array(zDuplicateWithExistingAccount),
    truncationInfo: zTruncationInfo.nullish(),
    batchUploadData: zBatchUploadData,
    leadSource: zLeadSource
});

/**
 * _UserlandBaseUser
 */
export const zUserlandBaseUser = z.object({
    id: z.string(),
    email: z.email(),
    is_active: z.boolean().optional().default(true),
    is_superuser: z.boolean().optional().default(false),
    is_verified: z.boolean().optional().default(false),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    initials: z.string().nullish(),
    email_system_prompt: z.string().nullish(),
    organization_id: z.string().nullish(),
    role: zOrganizationUserRole.nullish()
});

/**
 * _UserlandBaseUserUpdate
 *
 * User profile update schema.
 *
 * Strips sensitive fields that users should not change about themselves.
 */
export const zUserlandBaseUserUpdate = z.object({
    password: z.string().nullish(),
    email: z.email().nullish(),
    is_active: z.boolean().nullish(),
    is_superuser: z.boolean().nullish(),
    is_verified: z.boolean().nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    initials: z.string().nullish(),
    email_system_prompt: z.string().nullish()
});

/**
 * AdminBaseUser
 *
 * Base user for admin endpoints.
 */
export const zAdminBaseUserWritable = z.object({
    id: z.string(),
    email: z.email(),
    is_active: z.boolean().optional().default(true),
    is_superuser: z.boolean().optional().default(false),
    is_verified: z.boolean().optional().default(false),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    initials: z.string().nullish(),
    email_system_prompt: z.string().nullish(),
    organization_id: z.string().nullish(),
    role: zOrganizationUserRole.nullish()
});

/**
 * AdminOrganizationsResponse
 *
 * Response with all organizations and their users.
 */
export const zAdminOrganizationsResponseWritable = z.object({
    organizations: z.array(zOrganizationInfo),
    usersByOrg: z.record(z.string(), z.array(zAdminBaseUserWritable)),
    integrationsByOrg: z.record(z.string(), zActiveMergeIntegrationInfo.nullable())
});

/**
 * The type of feedback to get
 */
export const zFeedbackTypePathWritable = zFeedbackEntityType;

/**
 * OrganizationMember
 *
 * Model for organization member responses.
 */
export const zOrganizationMemberWritable = z.object({
    id: z.string(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    avatarUrl: z.string().nullish(),
    email: z.string().nullish(),
    role: zOrganizationUserRole,
    isVerified: z.boolean(),
    createdAt: z.iso.datetime({ offset: true }),
    lastLoginAt: z.iso.datetime({ offset: true }).nullish()
});

/**
 * CreateMemberResponse
 *
 * Response after creating a member.
 */
export const zCreateMemberResponseWritable = z.object({
    member: zOrganizationMemberWritable,
    inviteSent: z.boolean()
});

/**
 * OrganizationInfoResponse
 *
 * Response model for organization info endpoint.
 */
export const zOrganizationInfoResponseWritable = z.object({
    organization: zOrganizationBase,
    companyInfo: zCompanyInfoBase.nullable(),
    members: z.array(zOrganizationMemberWritable),
    entitlements: zEntitlementsInfo
});

/**
 * ValidatedCompany
 *
 * Validated company.
 */
export const zValidatedCompanyOutputWritable = z.object({
    name: z.string(),
    websiteUrl: z.string().min(1).max(2083),
    address: z.string().optional().default(''),
    rowIndex: z.int()
});

/**
 * SuperuserParseValidateResponse
 *
 * Response for superuser parse-validate endpoint.
 */
export const zSuperuserParseValidateResponseWritable = z.object({
    validationToken: z.string(),
    invalidRecords: z.array(zInvalidRow),
    warningRecords: z.array(zWarningRow),
    duplicatesWithinBatch: z.array(zDuplicateWithinBatch),
    newLeads: z.array(zValidatedCompanyOutputWritable),
    matchesWithExistingCompanies: z.array(zMatchWithExistingCompany),
    duplicateWithExistingAccounts: z.array(zDuplicateWithExistingAccount),
    batchUploadData: zBatchUploadData,
    leadSource: zLeadSource
});

/**
 * _ParseValidateResponse
 *
 * Response for parse-validate endpoint.
 */
export const zParseValidateResponseWritable = z.object({
    validationToken: z.string(),
    invalidRecords: z.array(zInvalidRow),
    warningRecords: z.array(zWarningRow),
    duplicatesWithinBatch: z.array(zDuplicateWithinBatch),
    newLeads: z.array(zValidatedCompanyOutputWritable),
    matchesWithExistingCompanies: z.array(zMatchWithExistingCompany),
    duplicateWithExistingAccounts: z.array(zDuplicateWithExistingAccount),
    truncationInfo: zTruncationInfo.nullish(),
    batchUploadData: zBatchUploadData,
    leadSource: zLeadSource
});

/**
 * Accounts table filter state
 */
export const zQueryAccountsListAppAccountsAccountsListPostBody = zAccountsTableFilterState;

export const zQueryAccountsListAppAccountsAccountsListPostQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    size: z.int().gte(1).lte(500).optional().default(20)
});

/**
 * Successful Response
 */
export const zQueryAccountsListAppAccountsAccountsListPostResponse = zPageAccountListItem;

/**
 * Accounts table filter state
 */
export const zQueryAccountsListGroupedAppAccountsAccountsListGroupedPostBody = zAccountsTableFilterState;

export const zQueryAccountsListGroupedAppAccountsAccountsListGroupedPostQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    size: z.int().gte(1).lte(500).optional().default(20)
});

/**
 * Successful Response
 */
export const zQueryAccountsListGroupedAppAccountsAccountsListGroupedPostResponse = zGroupedAccountsPage;

/**
 * Accounts table filter state
 */
export const zQueryAccountsByDomainAppAccountsAccountsListDomainDomainPostBody = zAccountsTableFilterState;

export const zQueryAccountsByDomainAppAccountsAccountsListDomainDomainPostPath = z.object({
    domain: z.string()
});

/**
 * Response Query Accounts By Domain App Accounts Accounts List Domain  Domain  Post
 *
 * Successful Response
 */
export const zQueryAccountsByDomainAppAccountsAccountsListDomainDomainPostResponse = z.array(zAccountListItem);

export const zGetAccountDetailsAppAccountsAccountsAccountIdGetPath = z.object({
    account_id: z.int()
});

/**
 * Successful Response
 */
export const zGetAccountDetailsAppAccountsAccountsAccountIdGetResponse = zAccountDetailsResponse;

/**
 * Filters and map viewport
 */
export const zGetMapPointsAppAccountsAccountsMapPointsPostBody = zMapPointsRequest;

/**
 * Successful Response
 */
export const zGetMapPointsAppAccountsAccountsMapPointsPostResponse = zMapPointsResponse;

export const zGetOtherLocationsAppAccountsAccountsAccountIdOtherLocationsGetPath = z.object({
    account_id: z.int()
});

export const zGetOtherLocationsAppAccountsAccountsAccountIdOtherLocationsGetQuery = z.object({
    offset: z.int().gte(0).optional().default(0),
    limit: z.int().gte(1).lte(200).optional().default(50)
});

/**
 * Successful Response
 */
export const zGetOtherLocationsAppAccountsAccountsAccountIdOtherLocationsGetResponse = zOtherLocationsResponse;

export const zGetSiblingLocationsAppAccountsAccountsAccountIdSiblingLocationsGetPath = z.object({
    account_id: z.int()
});

/**
 * Successful Response
 */
export const zGetSiblingLocationsAppAccountsAccountsAccountIdSiblingLocationsGetResponse = zMapPointsResponse;

export const zGetCrmContactsAppAccountsAccountsAccountIdCrmContactsGetPath = z.object({
    account_id: z.int()
});

export const zGetCrmContactsAppAccountsAccountsAccountIdCrmContactsGetQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    size: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zGetCrmContactsAppAccountsAccountsAccountIdCrmContactsGetResponse = zPageCrmContactDetails;

export const zGetCrmAccountsAppAccountsAccountsAccountIdCrmAccountsGetPath = z.object({
    account_id: z.int()
});

/**
 * Successful Response
 */
export const zGetCrmAccountsAppAccountsAccountsAccountIdCrmAccountsGetResponse = zCrmAccountsResponse;

export const zUnassignAccountsAppAccountsAssignDeleteBody = zBodyUnassignAccountsAppAccountsAssignDelete;

/**
 * Successful Response
 */
export const zUnassignAccountsAppAccountsAssignDeleteResponse = zAssignAccountsResponse;

/**
 * Accounts
 */
export const zAssignAccountsAppAccountsAssignPostBody = z.union([
    z.array(z.int()),
    zAccountsFilterState
]);

export const zAssignAccountsAppAccountsAssignPostQuery = z.object({
    target_user_id: z.string().nullish(),
    target_user_ids: z.array(z.string()).nullish(),
    auto_share: z.boolean().optional().default(false),
    notify_via_email: z.boolean().optional().default(false),
    message: z.string().optional().default('')
});

/**
 * Successful Response
 */
export const zAssignAccountsAppAccountsAssignPostResponse = zAssignAccountsResponse;

export const zGetAccountHistoryAppAccountsHistoryAccountIdGetPath = z.object({
    account_id: z.int()
});

export const zGetAccountHistoryAppAccountsHistoryAccountIdGetQuery = z.object({
    events_limit: z.int().gte(1).lte(500).optional().default(100),
    actions_limit: z.int().gte(1).lte(500).optional().default(100)
});

/**
 * Successful Response
 */
export const zGetAccountHistoryAppAccountsHistoryAccountIdGetResponse = zAccountHistoryResponse;

export const zParseValidateFileAppAccountsBatchUploadsParseValidateFilePostBody = zBodyParseValidateFileAppAccountsBatchUploadsParseValidateFilePost;

export const zParseValidateFileAppAccountsBatchUploadsParseValidateFilePostQuery = z.object({
    lead_source: zLeadSource
});

/**
 * Successful Response
 */
export const zParseValidateFileAppAccountsBatchUploadsParseValidateFilePostResponse = zParseValidateResponse;

export const zParseValidateCsvTextAppAccountsBatchUploadsParseValidateCsvTextPostBody = zBodyParseValidateCsvTextAppAccountsBatchUploadsParseValidateCsvTextPost;

export const zParseValidateCsvTextAppAccountsBatchUploadsParseValidateCsvTextPostQuery = z.object({
    lead_source: zLeadSource
});

/**
 * Successful Response
 */
export const zParseValidateCsvTextAppAccountsBatchUploadsParseValidateCsvTextPostResponse = zParseValidateResponse;

/**
 * Companies Data
 */
export const zParseValidateJsonAppAccountsBatchUploadsParseValidateJsonPostBody = z.array(zOriginalCompanyData);

export const zParseValidateJsonAppAccountsBatchUploadsParseValidateJsonPostQuery = z.object({
    lead_source: zLeadSource
});

/**
 * Successful Response
 */
export const zParseValidateJsonAppAccountsBatchUploadsParseValidateJsonPostResponse = zParseValidateResponse;

export const zExecuteBatchUploadAppAccountsBatchUploadsExecutePostBody = zExecuteBatchUploadRequest;

/**
 * Successful Response
 */
export const zExecuteBatchUploadAppAccountsBatchUploadsExecutePostResponse = zExecuteResponse;

export const zCreateAccountFromCompanyDataAppAccountsQuickCreatePostBody = zOriginalCompanyData;

/**
 * Response Create Account From Company Data App Accounts Quick Create  Post
 *
 * Successful Response
 */
export const zCreateAccountFromCompanyDataAppAccountsQuickCreatePostResponse = z.tuple([zAccountModel.nullable(), z.string()]);

export const zCreateAccountFromCompanyIdAppAccountsQuickCreateFromCompanyCompanyIdPostPath = z.object({
    company_id: z.int()
});

/**
 * Response Create Account From Company Id App Accounts Quick Create From Company  Company Id  Post
 *
 * Successful Response
 */
export const zCreateAccountFromCompanyIdAppAccountsQuickCreateFromCompanyCompanyIdPostResponse = z.tuple([zAccountModel, z.string()]);

export const zCreateAccountFromSupplierAppAccountsQuickCreateFromSupplierPostBody = zSupplierCreateRequest;

/**
 * Response Create Account From Supplier App Accounts Quick Create From Supplier Post
 *
 * Successful Response
 */
export const zCreateAccountFromSupplierAppAccountsQuickCreateFromSupplierPostResponse = z.tuple([zAccountModel.nullable(), z.string()]);

export const zSubmitTurnAppAccountsLeadDiscoveryTurnPostBody = zTurnRequest;

/**
 * Successful Response
 */
export const zSubmitTurnAppAccountsLeadDiscoveryTurnPostResponse = zTurnAcceptedResponse;

export const zGetConversationStateAppAccountsLeadDiscoveryConversationsConversationIdStateGetPath = z.object({
    conversation_id: z.string()
});

/**
 * Successful Response
 */
export const zGetConversationStateAppAccountsLeadDiscoveryConversationsConversationIdStateGetResponse = zChatState;

export const zEndConversationAppAccountsLeadDiscoveryConversationsConversationIdEndPostPath = z.object({
    conversation_id: z.string()
});

/**
 * Successful Response
 */
export const zEndConversationAppAccountsLeadDiscoveryConversationsConversationIdEndPostResponse = z.void();

export const zGetAccountProcessingStatusAppAccountsProcessingStatusAccountIdGetPath = z.object({
    account_id: z.int()
});

/**
 * Successful Response
 */
export const zGetAccountProcessingStatusAppAccountsProcessingStatusAccountIdGetResponse = zAccountProcessingStatusResponse;

export const zCheckBatchStatusAppAccountsProcessingStatusBatchAccountIdGetPath = z.object({
    account_id: z.int()
});

/**
 * Successful Response
 */
export const zCheckBatchStatusAppAccountsProcessingStatusBatchAccountIdGetResponse = zBatchProcessingStatus;

/**
 * Account Ids
 */
export const zCheckMultipleBatchStatusAppAccountsProcessingStatusBatchPostBody = z.array(z.int());

/**
 * Response Check Multiple Batch Status App Accounts Processing Status Batch Post
 *
 * Successful Response
 */
export const zCheckMultipleBatchStatusAppAccountsProcessingStatusBatchPostResponse = z.array(zBatchProcessingStatus);

export const zRetryCompanyEnrichmentAppAccountsProcessingStatusRetryCompanyCompanyIdPostPath = z.object({
    company_id: z.int()
});

/**
 * Successful Response
 */
export const zRetryCompanyEnrichmentAppAccountsProcessingStatusRetryCompanyCompanyIdPostResponse = zEnrichmentRetryResponse;

export const zRetryAccountEnrichmentAppAccountsProcessingStatusRetryAccountAccountIdPostPath = z.object({
    account_id: z.int()
});

/**
 * Successful Response
 */
export const zRetryAccountEnrichmentAppAccountsProcessingStatusRetryAccountAccountIdPostResponse = zEnrichmentRetryResponse;

export const zExportAccountsAppAccountsExportExportPostBody = zExportAccountsRequest;

export const zExportContactsAppAccountsExportContactsPostBody = zExportContactsRequest;

export const zGetExportEmailsAppAccountsExportEmailsPostBody = zExportEmailsRequest;

/**
 * Successful Response
 */
export const zGetExportEmailsAppAccountsExportEmailsPostResponse = zExportEmailsResponse;

/**
 * Response Custom Export Get Source Fields App Accounts Export Custom Export Fields Get
 *
 * Successful Response
 */
export const zCustomExportGetSourceFieldsAppAccountsExportCustomExportFieldsGetResponse = z.array(zCustomExportSourceField);

export const zCustomExportParseTemplateAppAccountsExportCustomExportParseTemplatePostBody = zBodyCustomExportParseTemplateAppAccountsExportCustomExportParseTemplatePost;

/**
 * Successful Response
 */
export const zCustomExportParseTemplateAppAccountsExportCustomExportParseTemplatePostResponse = zCustomExportParseResponse;

/**
 * Successful Response
 */
export const zCustomExportClearTemplateAppAccountsExportCustomExportTemplateDeleteResponse = z.void();

/**
 * Successful Response
 */
export const zCustomExportGetTemplateAppAccountsExportCustomExportTemplateGetResponse = zCustomExportTemplateResponse;

export const zCustomExportSaveTemplateAppAccountsExportCustomExportTemplatePutBody = zCustomExportTemplate;

/**
 * Successful Response
 */
export const zCustomExportSaveTemplateAppAccountsExportCustomExportTemplatePutResponse = zCustomExportTemplateResponse;

export const zCustomExportPreviewAppAccountsExportCustomExportPreviewPostBody = zCustomExportFiltersRequest;

/**
 * Successful Response
 */
export const zCustomExportPreviewAppAccountsExportCustomExportPreviewPostResponse = zCustomExportPreviewResponse;

export const zCustomExportDownloadAppAccountsExportCustomExportDownloadPostBody = zCustomExportDownloadRequest;

/**
 * Request
 *
 * Current filters and optional option search
 */
export const zGetAccountFacetOptionsAppAccountsFilterOptionsFacetPostBody = zAccountFacetOptionsRequest.nullable();

export const zGetAccountFacetOptionsAppAccountsFilterOptionsFacetPostPath = z.object({
    facet: z.enum([
        'regions',
        'countries',
        'industries',
        'industries_served',
        'naics',
        'sic',
        'labels',
        'specialties',
        'shared_with_orgs',
        'shared_by_orgs'
    ])
});

/**
 * Successful Response
 */
export const zGetAccountFacetOptionsAppAccountsFilterOptionsFacetPostResponse = zAccountFacetOptionsResponse;

/**
 * Request
 */
export const zGetAccountsHeatMapAppAccountsHeatMapsPostBody = zHeatMapRequest.nullable();

/**
 * Successful Response
 */
export const zGetAccountsHeatMapAppAccountsHeatMapsPostResponse = zHeatMapResponse;

export const zParseFiltersAppAccountsParseFiltersPostBody = zParseFiltersRequest;

/**
 * Successful Response
 */
export const zParseFiltersAppAccountsParseFiltersPostResponse = zParseFiltersResponse;

export const zGetVectorTileAppAccountsAccountsTilesZxyPbfGetPath = z.object({
    z: z.int().gte(0).lte(29),
    x: z.int().gte(0),
    y: z.int().gte(0)
});

export const zGetVectorTileAppAccountsAccountsTilesZxyPbfGetQuery = z.object({
    gridPrecision: z.int().gte(0).lte(8).optional().default(4)
});

export const zShareAccountsAppAccountsSharePostBody = zShareAccountsRequest;

/**
 * Successful Response
 */
export const zShareAccountsAppAccountsSharePostResponse = zShareAccountsResponse;

export const zShareAccountViaEmailAppAccountsShareAccountIdEmailPostBody = zShareAccountViaEmailRequest;

export const zShareAccountViaEmailAppAccountsShareAccountIdEmailPostPath = z.object({
    account_id: z.int()
});

/**
 * Successful Response
 */
export const zShareAccountViaEmailAppAccountsShareAccountIdEmailPostResponse = zShareAccountViaEmailResponse;

export const zGetAccountContactsAppAccountsContactsAccountIdGetPath = z.object({
    account_id: z.int()
});

export const zGetAccountContactsAppAccountsContactsAccountIdGetQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    size: z.int().gte(1).lte(100).optional().default(50)
});

/**
 * Successful Response
 */
export const zGetAccountContactsAppAccountsContactsAccountIdGetResponse = zPageContactDetails;

export const zGetAccountContactsFullAppAccountsContactsAccountIdFullGetPath = z.object({
    account_id: z.int()
});

export const zGetAccountContactsFullAppAccountsContactsAccountIdFullGetQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    size: z.int().gte(1).lte(100).optional().default(50)
});

/**
 * Successful Response
 */
export const zGetAccountContactsFullAppAccountsContactsAccountIdFullGetResponse = zPageContactDetails;

export const zGetAccountContactsSummaryAppAccountsContactsAccountIdSummaryGetPath = z.object({
    account_id: z.int()
});

export const zGetAccountContactsSummaryAppAccountsContactsAccountIdSummaryGetQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    size: z.int().gte(1).lte(100).optional().default(50)
});

/**
 * Successful Response
 */
export const zGetAccountContactsSummaryAppAccountsContactsAccountIdSummaryGetResponse = zPageContactSummary;

export const zGetContactDetailAppAccountsContactsAccountIdDetailPersonIdGetPath = z.object({
    account_id: z.int(),
    person_id: z.int()
});

/**
 * Successful Response
 */
export const zGetContactDetailAppAccountsContactsAccountIdDetailPersonIdGetResponse = zContactDetails;

export const zFetchMoreContactsAppAccountsContactsAccountIdFetchMorePostPath = z.object({
    account_id: z.int()
});

export const zFetchMoreContactsAppAccountsContactsAccountIdFetchMorePostQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    size: z.int().gte(1).lte(100).optional().default(50)
});

/**
 * Successful Response
 */
export const zFetchMoreContactsAppAccountsContactsAccountIdFetchMorePostResponse = zPageContactDetails;

export const zEnrichContactAppAccountsContactsAccountIdEnrichPersonIdPostPath = z.object({
    account_id: z.int(),
    person_id: z.int()
});

/**
 * Successful Response
 */
export const zEnrichContactAppAccountsContactsAccountIdEnrichPersonIdPostResponse = zContactDetails;

export const zSetPrimaryContactAppAccountsContactsAccountIdPrimaryPersonIdPutPath = z.object({
    account_id: z.int(),
    person_id: z.int()
});

/**
 * Successful Response
 */
export const zSetPrimaryContactAppAccountsContactsAccountIdPrimaryPersonIdPutResponse = zContactDetails;

export const zUnsetPrimaryContactAppAccountsContactsAccountIdPrimaryDeletePath = z.object({
    account_id: z.int()
});

export const zListNotesAppAccountsNotesAccountIdGetPath = z.object({
    account_id: z.int()
});

/**
 * Response List Notes App Accounts Notes  Account Id  Get
 *
 * Successful Response
 */
export const zListNotesAppAccountsNotesAccountIdGetResponse = z.array(zAccountNoteResponse);

export const zCreateNoteAppAccountsNotesAccountIdPostBody = zCreateNoteRequest;

export const zCreateNoteAppAccountsNotesAccountIdPostPath = z.object({
    account_id: z.int()
});

/**
 * Successful Response
 */
export const zCreateNoteAppAccountsNotesAccountIdPostResponse = zAccountNoteResponse;

export const zDeleteNoteAppAccountsNotesAccountIdNoteIdDeletePath = z.object({
    account_id: z.int(),
    note_id: z.int()
});

/**
 * Successful Response
 */
export const zDeleteNoteAppAccountsNotesAccountIdNoteIdDeleteResponse = zAccountNoteResponse;

/**
 * Accounts To Delete
 */
export const zDeleteAccountsAppAccountsDeleteBody = z.union([
    z.array(z.int()),
    zAccountsFilterState
]);

/**
 * Successful Response
 */
export const zDeleteAccountsAppAccountsDeleteResponse = zDeleteAccountsResponse;

export const zTriggerSupplierLookupAppAccountsAccountsAccountIdSupplierLookupPostPath = z.object({
    account_id: z.int()
});

/**
 * Successful Response
 */
export const zTriggerSupplierLookupAppAccountsAccountsAccountIdSupplierLookupPostResponse = zSupplierLookupResponse;

export const zListSupplierLookupsAppAccountsAccountsAccountIdSupplierLookupsGetPath = z.object({
    account_id: z.int()
});

/**
 * Response List Supplier Lookups App Accounts Accounts  Account Id  Supplier Lookups Get
 *
 * Successful Response
 */
export const zListSupplierLookupsAppAccountsAccountsAccountIdSupplierLookupsGetResponse = z.array(zSupplierLookupResponse);

export const zTrackAccountViewAppAccountsAccountsAccountIdViewPostPath = z.object({
    account_id: z.int()
});

export const zGetBuildingFootprintAppAccountsAccountsAccountIdBuildingFootprintGetPath = z.object({
    account_id: z.int()
});

/**
 * Response Get Building Footprint App Accounts Accounts  Account Id  Building Footprint Get
 *
 * Successful Response
 */
export const zGetBuildingFootprintAppAccountsAccountsAccountIdBuildingFootprintGetResponse = zBuildingFootprintResponse.nullable();

export const zListAgentRunsAppAgentRunsGetQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(50)
});

/**
 * Response List Agent Runs App Agent Runs Get
 *
 * Successful Response
 */
export const zListAgentRunsAppAgentRunsGetResponse = z.array(zAgentRunSummary);

export const zLaunchAgentRunAppAgentRunsLaunchPostBody = zLaunchAgentRunRequest;

/**
 * Successful Response
 */
export const zLaunchAgentRunAppAgentRunsLaunchPostResponse = zAgentRunSummary;

export const zRespondToAgentRunAppAgentRunsRunIdRespondPostBody = zRespondToAgentRunRequest;

export const zRespondToAgentRunAppAgentRunsRunIdRespondPostPath = z.object({
    run_id: z.int().gte(1)
});

/**
 * Successful Response
 */
export const zRespondToAgentRunAppAgentRunsRunIdRespondPostResponse = z.void();

export const zGetAgentRunAppAgentRunsRunIdGetPath = z.object({
    run_id: z.int().gte(1)
});

/**
 * Successful Response
 */
export const zGetAgentRunAppAgentRunsRunIdGetResponse = zAgentRunDetail;

export const zStreamAgentRunAppAgentRunsRunIdStreamGetPath = z.object({
    run_id: z.int().gte(1)
});

/**
 * Response Get Company Info App Organization Company Info  Get
 *
 * Successful Response
 */
export const zGetCompanyInfoAppOrganizationCompanyInfoGetResponse = zCompanyInfoResponse.nullable();

export const zUpdateCompanyInfoAppOrganizationCompanyInfoPatchBody = zCompanyInfoUpdate;

/**
 * Successful Response
 */
export const zUpdateCompanyInfoAppOrganizationCompanyInfoPatchResponse = zCompanyInfoResponse;

/**
 * Response Get Distributors App Organization Organizations Distributors Get
 *
 * Successful Response
 */
export const zGetDistributorsAppOrganizationOrganizationsDistributorsGetResponse = z.array(zDistributorOrganizationResponse);

export const zUpdateDistributorRelationshipAppOrganizationOrganizationsDistributorsRelationshipIdPatchBody = zUpdateDistributorRelationshipRequest;

export const zUpdateDistributorRelationshipAppOrganizationOrganizationsDistributorsRelationshipIdPatchPath = z.object({
    relationship_id: z.int()
});

/**
 * Successful Response
 */
export const zUpdateDistributorRelationshipAppOrganizationOrganizationsDistributorsRelationshipIdPatchResponse = zDistributorOrganizationResponse;

/**
 * Successful Response
 */
export const zGetAssignableUsersAppOrganizationOrganizationsAssignableUsersGetResponse = zAssignableUsersResponse;

export const zUpdateOrganizationAppOrganizationOrganizationsOrgIdPatchBody = zOrganizationBase;

export const zUpdateOrganizationAppOrganizationOrganizationsOrgIdPatchPath = z.object({
    org_id: z.string()
});

/**
 * Successful Response
 */
export const zUpdateOrganizationAppOrganizationOrganizationsOrgIdPatchResponse = zOrganizationBase;

/**
 * Successful Response
 */
export const zGetOrganizationInfoAppOrganizationOrganizationsInfoGetResponse = zOrganizationInfoResponse;

export const zUpdateMemberRoleAppOrganizationOrganizationsMembersUserIdRolePatchBody = zUpdateMemberRoleRequest;

export const zUpdateMemberRoleAppOrganizationOrganizationsMembersUserIdRolePatchPath = z.object({
    user_id: z.string()
});

/**
 * Successful Response
 */
export const zUpdateMemberRoleAppOrganizationOrganizationsMembersUserIdRolePatchResponse = zOrganizationMember;

export const zResendMemberInvitationAppOrganizationOrganizationsMembersUserIdResendInvitationPostPath = z.object({
    user_id: z.string()
});

/**
 * Successful Response
 */
export const zResendMemberInvitationAppOrganizationOrganizationsMembersUserIdResendInvitationPostResponse = z.void();

export const zDeleteMemberAppOrganizationOrganizationsMembersUserIdDeletePath = z.object({
    user_id: z.string()
});

/**
 * Successful Response
 */
export const zDeleteMemberAppOrganizationOrganizationsMembersUserIdDeleteResponse = z.void();

export const zCreateMemberAppOrganizationOrganizationsMembersPostBody = zUserCreationRequest;

export const zCreateMemberAppOrganizationOrganizationsMembersPostQuery = z.object({
    send_invite: z.boolean().optional().default(true),
    organization_id: z.string().nullish()
});

/**
 * Successful Response
 */
export const zCreateMemberAppOrganizationOrganizationsMembersPostResponse = zCreateMemberResponse;

/**
 * Successful Response
 */
export const zGetInboundConfigAppOrganizationOrganizationsMeInboundConfigGetResponse = zInboundConfigResponse;

export const zUpdateInboundConfigAppOrganizationOrganizationsMeInboundConfigPutBody = zInboundConfigUpdateRequest;

/**
 * Successful Response
 */
export const zUpdateInboundConfigAppOrganizationOrganizationsMeInboundConfigPutResponse = zInboundConfigResponse;

/**
 * Successful Response
 */
export const zGetRequiredOrderFieldsAppOrganizationOrganizationsMeRequiredOrderFieldsGetResponse = zRequiredOrderFieldsResponse;

export const zUpdateRequiredOrderFieldAppOrganizationOrganizationsMeRequiredOrderFieldsDocumentTypeFieldIdPutBody = zRequiredOrderFieldUpdateRequest;

export const zUpdateRequiredOrderFieldAppOrganizationOrganizationsMeRequiredOrderFieldsDocumentTypeFieldIdPutPath = z.object({
    document_type: zDocumentType,
    field_id: z.string().min(1).max(64)
});

/**
 * Successful Response
 */
export const zUpdateRequiredOrderFieldAppOrganizationOrganizationsMeRequiredOrderFieldsDocumentTypeFieldIdPutResponse = zRequiredOrderFieldsResponse;

/**
 * Response Get Personas App Organization Personas  Get
 *
 * Successful Response
 */
export const zGetPersonasAppOrganizationPersonasGetResponse = z.array(zPersonaResponse);

/**
 * Description
 *
 * Description of the persona
 */
export const zCreatePersonaAppOrganizationPersonasPostBody = z.string();

/**
 * Successful Response
 */
export const zCreatePersonaAppOrganizationPersonasPostResponse = zPersonaResponse;

export const zDeletePersonaAppOrganizationPersonasPersonaIdDeletePath = z.object({
    persona_id: z.string()
});

export const zDeleteDistributorRelationshipAppOrganizationDistributorRelationshipsRelationshipIdDeletePath = z.object({
    relationship_id: z.int()
});

export const zCreateDistributorRelationshipAppOrganizationDistributorRelationshipsPostBody = zCreateDistributorRelationshipRequest;

/**
 * Successful Response
 */
export const zCreateDistributorRelationshipAppOrganizationDistributorRelationshipsPostResponse = zDistributorOrganizationResponse;

export const zLogActivityActionsAppAccountActionsActivityActionsPostBody = zActivityActionRequest;

/**
 * Successful Response
 */
export const zLogActivityActionsAppAccountActionsActivityActionsPostResponse = zActivityActionResponse;

export const zGetActivityLeaderboardAppAccountActionsActivityLeaderboardGetQuery = z.object({
    searchTerm: z.string().nullish(),
    minContacted: z.int().optional().default(0),
    minPositive: z.int().optional().default(0),
    selectedReps: z.string().nullish(),
    selectedDistributors: z.string().nullish(),
    includeDistributors: z.boolean().optional().default(false),
    days: z.int().gte(7).lte(90).nullish()
});

/**
 * Successful Response
 */
export const zGetActivityLeaderboardAppAccountActionsActivityLeaderboardGetResponse = zLeaderboardResponse;

export const zGetActivityStatsAppAccountActionsActivityStatsGetQuery = z.object({
    dateRange: z.string().optional().default('all-time'),
    includeDistributors: z.boolean().optional().default(false)
});

/**
 * Successful Response
 */
export const zGetActivityStatsAppAccountActionsActivityStatsGetResponse = zActivityStatsResponse;

export const zGetActivityTimelineAppAccountActionsActivityTimelineGetQuery = z.object({
    includeDistributors: z.boolean().optional().default(false),
    days: z.int().gte(7).lte(365).nullish()
});

/**
 * Successful Response
 */
export const zGetActivityTimelineAppAccountActionsActivityTimelineGetResponse = zActivityTimelineResponse;

export const zGetAccountsReviewedTimelineAppAccountActionsActivityAccountsReviewedGetQuery = z.object({
    days: z.int().gte(7).lte(365).nullish()
});

/**
 * Successful Response
 */
export const zGetAccountsReviewedTimelineAppAccountActionsActivityAccountsReviewedGetResponse = zAccountsReviewedTimelineResponse;

/**
 * Successful Response
 */
export const zGetCompetitorsStatsAppAccountActionsActivityCompetitorsStatsGetResponse = zCompetitorsStatsResponse;

/**
 * Successful Response
 */
export const zGetResearchingAccountsCountAppAccountActionsActivityResearchingCountGetResponse = zResearchingAccountsResponse;

export const zGetActivityLogAppAccountActionsActivityLogGetQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    pageSize: z.int().gte(1).lte(100).optional().default(20),
    userId: z.string().nullish(),
    actionType: z.string().nullish(),
    startDate: z.string().nullish(),
    endDate: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetActivityLogAppAccountActionsActivityLogGetResponse = zActivityLogResponse;

export const zReportDataIssueBody = zReportDataIssueRequest;

export const zReportDataIssuePath = z.object({
    account_id: z.int()
});

/**
 * Successful Response
 */
export const zReportDataIssueResponse2 = zReportDataIssueResponse;

export const zGetFeedbackPath = z.object({
    feedback_type: zFeedbackTypePathWritable,
    record_id: z.int()
});

/**
 * Response Getfeedback
 *
 * Successful Response
 */
export const zGetFeedbackResponse2 = zGetFeedbackResponse.nullable();

/**
 * The feedback to leave
 */
export const zLeaveFeedbackAppAccountActionsFeedbackFeedbackTypeRecordIdPostBody = zFeedback;

export const zLeaveFeedbackAppAccountActionsFeedbackFeedbackTypeRecordIdPostPath = z.object({
    feedback_type: zFeedbackTypePathWritable,
    record_id: z.int()
});

export const zDeleteFeedbackPath = z.object({
    feedback_id: z.int()
});

export const zGenerateEmailAppAccountActionsEmailGenerationPostBody = zGenerateEmailRequest;

/**
 * Successful Response
 */
export const zGenerateEmailAppAccountActionsEmailGenerationPostResponse = zEmailGenerationResponse;

export const zExportRenewalReportAppAccountActionsActivityRenewalReportExportPostBody = zRenewalReportRequest;

/**
 * Successful Response
 */
export const zExportRenewalReportAppAccountActionsActivityRenewalReportExportPostResponse = zRenewalReportQueuedResponse;

/**
 * Response List Buyer Profiles App Buyer Profiles Get
 *
 * Successful Response
 */
export const zListBuyerProfilesAppBuyerProfilesGetResponse = z.array(zBuyerProfileReturn);

export const zCreateBuyerProfileAppBuyerProfilesPostBody = zBuyerProfileModel;

/**
 * Successful Response
 */
export const zCreateBuyerProfileAppBuyerProfilesPostResponse = zBuyerProfileReturn;

export const zUpdateTagAssociationsAppBuyerProfilesAssociationsPatchBody = zBodyUpdateTagAssociationsAppBuyerProfilesAssociationsPatch;

/**
 * Successful Response
 */
export const zUpdateTagAssociationsAppBuyerProfilesAssociationsPatchResponse = zTagAssociationsResponse;

export const zDeleteBuyerProfileAppBuyerProfilesTagIdDeletePath = z.object({
    tag_id: z.int()
});

/**
 * Response Delete Buyer Profile App Buyer Profiles  Tag Id  Delete
 *
 * Successful Response
 */
export const zDeleteBuyerProfileAppBuyerProfilesTagIdDeleteResponse = z.record(z.string(), z.string());

export const zUpdateBuyerProfileAppBuyerProfilesTagIdPatchBody = zBuyerProfileUpdate;

export const zUpdateBuyerProfileAppBuyerProfilesTagIdPatchPath = z.object({
    tag_id: z.int()
});

/**
 * Successful Response
 */
export const zUpdateBuyerProfileAppBuyerProfilesTagIdPatchResponse = zBuyerProfileReturn;

/**
 * Response Get Market Research Reports App Market Research Reports Get
 *
 * Successful Response
 */
export const zGetMarketResearchReportsAppMarketResearchReportsGetResponse = z.array(zMarketResearchReportListItem);

export const zCreateMarketResearchReportAppMarketResearchReportsPostBody = zCreateMarketResearchReportRequest;

/**
 * Successful Response
 */
export const zCreateMarketResearchReportAppMarketResearchReportsPostResponse = zMarketResearchReportResponse;

export const zDeleteMarketResearchReportAppMarketResearchReportsReportIdDeletePath = z.object({
    report_id: z.int()
});

export const zGetMarketResearchReportAppMarketResearchReportsReportIdGetPath = z.object({
    report_id: z.int()
});

/**
 * Successful Response
 */
export const zGetMarketResearchReportAppMarketResearchReportsReportIdGetResponse = zMarketResearchReportResponse;

export const zRegenerateMarketResearchReportAppMarketResearchReportsReportIdRegeneratePostPath = z.object({
    report_id: z.int()
});

/**
 * Successful Response
 */
export const zRegenerateMarketResearchReportAppMarketResearchReportsReportIdRegeneratePostResponse = zMarketResearchReportResponse;

export const zGetNewsfeedAppInboxNewsfeedGetQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    size: z.int().gte(1).lte(100).optional().default(50)
});

/**
 * Successful Response
 */
export const zGetNewsfeedAppInboxNewsfeedGetResponse = zPageNewsfeedItem;

export const zListRecommendationsAppInboxRecommendationsGetQuery = z.object({
    status: z.string().nullish(),
    source: z.string().nullish(),
    is_read: z.boolean().nullish(),
    show_dismissed: z.boolean().optional().default(false),
    limit: z.int().gte(1).lte(100).optional().default(20),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListRecommendationsAppInboxRecommendationsGetResponse = zPaginatedRecommendationsResponse;

/**
 * Successful Response
 */
export const zGetRecommendationCountAppInboxRecommendationsCountGetResponse = zRecommendationCountResponse;

export const zGetRecommendationByPublicIdAppInboxRecommendationsByPublicIdPublicIdGetPath = z.object({
    public_id: z.string()
});

/**
 * Successful Response
 */
export const zGetRecommendationByPublicIdAppInboxRecommendationsByPublicIdPublicIdGetResponse = zRecommendationResponse;

export const zActionRecommendationAppInboxRecommendationsRecommendationIdActionPostBody = zActionRequest;

export const zActionRecommendationAppInboxRecommendationsRecommendationIdActionPostPath = z.object({
    recommendation_id: z.int()
});

/**
 * Successful Response
 */
export const zActionRecommendationAppInboxRecommendationsRecommendationIdActionPostResponse = zRecommendationResponse;

export const zDismissRecommendationAppInboxRecommendationsRecommendationIdDismissPostBody = zDismissRequest;

export const zDismissRecommendationAppInboxRecommendationsRecommendationIdDismissPostPath = z.object({
    recommendation_id: z.int()
});

/**
 * Successful Response
 */
export const zDismissRecommendationAppInboxRecommendationsRecommendationIdDismissPostResponse = zRecommendationResponse;

export const zMarkReadAppInboxRecommendationsRecommendationIdReadPostPath = z.object({
    recommendation_id: z.int()
});

/**
 * Successful Response
 */
export const zMarkReadAppInboxRecommendationsRecommendationIdReadPostResponse = zRecommendationResponse;

export const zMarkUnreadAppInboxRecommendationsRecommendationIdUnreadPostPath = z.object({
    recommendation_id: z.int()
});

/**
 * Successful Response
 */
export const zMarkUnreadAppInboxRecommendationsRecommendationIdUnreadPostResponse = zRecommendationResponse;

export const zGetGeneratedAccountIdsAppInboxRecommendationsRecommendationIdGeneratedAccountIdsGetPath = z.object({
    recommendation_id: z.int()
});

/**
 * Response Get Generated Account Ids App Inbox Recommendations  Recommendation Id  Generated Account Ids Get
 *
 * Successful Response
 */
export const zGetGeneratedAccountIdsAppInboxRecommendationsRecommendationIdGeneratedAccountIdsGetResponse = z.record(z.string(), zGeneratedAccountStatus);

export const zGenerateAccountArAppInboxRecommendationsRecommendationIdGenerateAccountArPostBody = zGenerateAccountArRequest;

export const zGenerateAccountArAppInboxRecommendationsRecommendationIdGenerateAccountArPostPath = z.object({
    recommendation_id: z.int()
});

/**
 * Successful Response
 */
export const zGenerateAccountArAppInboxRecommendationsRecommendationIdGenerateAccountArPostResponse = zGenerateAccountArResponse;

export const zEmailShareRecommendationAppInboxRecommendationsRecommendationIdEmailRenewalReportPostBody = zEmailShareRequest;

export const zEmailShareRecommendationAppInboxRecommendationsRecommendationIdEmailRenewalReportPostPath = z.object({
    recommendation_id: z.int()
});

/**
 * Successful Response
 */
export const zEmailShareRecommendationAppInboxRecommendationsRecommendationIdEmailRenewalReportPostResponse = zEmailShareResponse;

export const zEmailShareRecommendationAppInboxRecommendationsRecommendationIdEmailSharePostBody = zEmailShareRequest;

export const zEmailShareRecommendationAppInboxRecommendationsRecommendationIdEmailSharePostPath = z.object({
    recommendation_id: z.int()
});

/**
 * Successful Response
 */
export const zEmailShareRecommendationAppInboxRecommendationsRecommendationIdEmailSharePostResponse = zEmailShareResponse;

/**
 * Successful Response
 */
export const zChatAuthAppChatAuthGetResponse = zChatAuthResponse;

export const zTranscribeDebriefAppDebriefTranscribePostBody = zBodyTranscribeDebriefAppDebriefTranscribePost;

/**
 * Successful Response
 */
export const zTranscribeDebriefAppDebriefTranscribePostResponse = zTranscribeResponse;

export const zSaveDebriefAppDebriefSavePostBody = zSaveDebriefRequest;

/**
 * Successful Response
 */
export const zSaveDebriefAppDebriefSavePostResponse = zDebriefHistoryItem;

export const zGetDebriefHistoryAppDebriefHistoryGetQuery = z.object({
    limit: z.int().optional().default(50),
    offset: z.int().optional().default(0)
});

/**
 * Successful Response
 */
export const zGetDebriefHistoryAppDebriefHistoryGetResponse = zDebriefHistoryResponse;

/**
 * Successful Response
 */
export const zStartNewDebriefConversationAppDebriefConversationStartPostResponse = zConversationStartResponse;

export const zStartDebriefConversationAppDebriefDebriefIdConversationStartPostPath = z.object({
    debrief_id: z.string()
});

/**
 * Successful Response
 */
export const zStartDebriefConversationAppDebriefDebriefIdConversationStartPostResponse = zConversationStartResponse;

export const zCompleteDebriefConversationAppDebriefDebriefIdConversationCompletePostBody = zConversationCompleteRequest;

export const zCompleteDebriefConversationAppDebriefDebriefIdConversationCompletePostPath = z.object({
    debrief_id: z.string()
});

/**
 * Successful Response
 */
export const zCompleteDebriefConversationAppDebriefDebriefIdConversationCompletePostResponse = z.void();

export const zCompleteDebriefConversationWebhookAppDebriefDebriefIdConversationCompleteWebhookPostBody = zConversationCompleteRequest;

export const zCompleteDebriefConversationWebhookAppDebriefDebriefIdConversationCompleteWebhookPostHeaders = z.object({
    authorization: z.string().nullish()
});

export const zCompleteDebriefConversationWebhookAppDebriefDebriefIdConversationCompleteWebhookPostPath = z.object({
    debrief_id: z.string()
});

/**
 * Successful Response
 */
export const zCompleteDebriefConversationWebhookAppDebriefDebriefIdConversationCompleteWebhookPostResponse = z.void();

export const zCreateMergeLinkTokenAppIntegrationsMergeLinkTokenPostBody = zMergeLinkTokenRequest;

/**
 * Successful Response
 */
export const zCreateMergeLinkTokenAppIntegrationsMergeLinkTokenPostResponse = zMergeLinkTokenResponse;

export const zExchangeTokenAppIntegrationsMergeExchangeTokenPostBody = zExchangeTokenRequest;

/**
 * Successful Response
 */
export const zExchangeTokenAppIntegrationsMergeExchangeTokenPostResponse = zExchangeTokenResponse;

/**
 * Successful Response
 */
export const zGetIntegrationStatusAppIntegrationsMergeStatusGetResponse = zIntegrationStatusResponse;

export const zSyncMergeAccountsAppIntegrationsMergeSyncPostBody = zSyncRequest;

/**
 * Successful Response
 */
export const zSyncMergeAccountsAppIntegrationsMergeSyncPostResponse = zSyncResponse;

export const zDownloadFailedAccountsCsvAppIntegrationsMergeLogsLogIdFailedAccountsCsvGetPath = z.object({
    log_id: z.int()
});

/**
 * Successful Response
 */
export const zGetCrmUsersAppIntegrationsMergeCrmUsersGetResponse = zCrmUsersResponse;

export const zEnrichByLinkedinUrlAppLinkedinEnrichPostBody = zLinkedInEnrichRequest;

/**
 * Successful Response
 */
export const zEnrichByLinkedinUrlAppLinkedinEnrichPostResponse = zLinkedInContactDetails;

/**
 * Successful Response
 */
export const zClaimLeadsAppScoutClaimPostResponse = zClaimLeadsResponse;

/**
 * Successful Response
 */
export const zGetDashboardAppScoutDashboardGetResponse = zDashboardResponse;

export const zParseTerritoryAppOnboardingParseTerritoryPostBody = zTerritoryParseRequest;

/**
 * Successful Response
 */
export const zParseTerritoryAppOnboardingParseTerritoryPostResponse = zTerritoryParseResponse;

/**
 * Response Get Partners App Onboarding Partners Get
 *
 * Successful Response
 */
export const zGetPartnersAppOnboardingPartnersGetResponse = z.array(zPartnerDirectoryEntry);

export const zOnboardingCompleteAppOnboardingCompletePostBody = zOnboardingCompleteRequest;

/**
 * Successful Response
 */
export const zOnboardingCompleteAppOnboardingCompletePostResponse = zOnboardingCompleteResponse;

export const zOnboardingActivateAppOnboardingActivateGetQuery = z.object({
    token: z.string()
});

export const zOnboardingExchangeTokenAppOnboardingExchangeTokenPostQuery = z.object({
    token: z.string()
});

export const zJoinInfoAppOnboardingJoinInfoGetQuery = z.object({
    token: z.string()
});

/**
 * Successful Response
 */
export const zJoinInfoAppOnboardingJoinInfoGetResponse = zJoinInfoResponse;

export const zJoinCompleteAppOnboardingJoinCompletePostBody = zJoinCompleteRequest;

export const zPartnerInviteCreateAppOnboardingPartnerInviteCreatePostBody = zPartnerInviteCreateRequest;

/**
 * Successful Response
 */
export const zPartnerInviteCreateAppOnboardingPartnerInviteCreatePostResponse = zPartnerInviteCreateResponse;

export const zPartnerInviteInfoAppOnboardingPartnerInviteInfoGetQuery = z.object({
    token: z.string()
});

/**
 * Successful Response
 */
export const zPartnerInviteInfoAppOnboardingPartnerInviteInfoGetResponse = zPartnerInviteInfoResponse;

export const zDemodaySignupAppDemodaySignupPostBody = zDemoDaySignupRequest;

/**
 * Successful Response
 */
export const zDemodaySignupAppDemodaySignupPostResponse = zDemoDaySignupResponse;

/**
 * Response List Demoday Orgs App Demoday Orgs Get
 *
 * Successful Response
 */
export const zListDemodayOrgsAppDemodayOrgsGetResponse = z.array(zDemoDayOrgEntry);

export const zJoinAppDemodayJoinPostBody = zDemoDayJoinRequest;

/**
 * Successful Response
 */
export const zJoinAppDemodayJoinPostResponse = zDemoDayJoinResponse;

export const zListDealsAppOrdersDealsGetQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    size: z.int().gte(1).lte(200).optional().default(50),
    bucket: zDealBucket.optional().default('all'),
    sort: zDealSort.optional().default('last_activity_at')
});

/**
 * Successful Response
 */
export const zListDealsAppOrdersDealsGetResponse = zDealListResponse;

export const zTransitionDealStageAppOrdersDealsDealIdStagePostBody = zDealStageTransitionRequest;

export const zTransitionDealStageAppOrdersDealsDealIdStagePostPath = z.object({
    deal_id: z.int()
});

/**
 * Successful Response
 */
export const zTransitionDealStageAppOrdersDealsDealIdStagePostResponse = zDealStageTransitionResponse;

export const zGetDealEventsAppOrdersDealsDealIdEventsGetPath = z.object({
    deal_id: z.int()
});

/**
 * Successful Response
 */
export const zGetDealEventsAppOrdersDealsDealIdEventsGetResponse = zDealEventsResponse;

export const zDeleteOrdersAppOrdersDeleteBody = zDeleteOrdersRequest;

/**
 * Successful Response
 */
export const zDeleteOrdersAppOrdersDeleteResponse = zDeleteOrdersResponse;

export const zListOrdersAppOrdersGetQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    size: z.int().gte(1).lte(200).optional().default(50),
    bucket: zOrderBucket.nullish()
});

/**
 * Successful Response
 */
export const zListOrdersAppOrdersGetResponse = zOrderListResponse;

/**
 * Successful Response
 */
export const zOrderStatsAppOrdersStatsGetResponse = zOrderStats;

/**
 * Successful Response
 */
export const zGetInboundAddressAppOrdersInboundAddressGetResponse = zInboundAddress;

export const zRetryOrderAppOrdersOrderIdRetryPostPath = z.object({
    order_id: z.string()
});

/**
 * Successful Response
 */
export const zRetryOrderAppOrdersOrderIdRetryPostResponse = zRetryOrderResponse;

export const zReprocessOrderAppOrdersOrderIdReprocessPostPath = z.object({
    order_id: z.string()
});

/**
 * Successful Response
 */
export const zReprocessOrderAppOrdersOrderIdReprocessPostResponse = zReprocessOrderResponse;

export const zGetOrderAppOrdersOrderIdGetPath = z.object({
    order_id: z.string()
});

/**
 * Successful Response
 */
export const zGetOrderAppOrdersOrderIdGetResponse = zOrderDetail;

export const zUpdateOrderAppOrdersOrderIdPatchBody = zOrderUpdateRequest;

export const zUpdateOrderAppOrdersOrderIdPatchPath = z.object({
    order_id: z.string()
});

/**
 * Successful Response
 */
export const zUpdateOrderAppOrdersOrderIdPatchResponse = zOrderDetail;

export const zGetOrderPreviewAppOrdersOrderIdPreviewGetPath = z.object({
    order_id: z.string()
});

export const zGetOrderAttachmentAppOrdersOrderIdAttachmentsAttachmentIndexGetPath = z.object({
    order_id: z.string(),
    attachment_index: z.int()
});

export const zGetOrderAttachmentPageAppOrdersOrderIdAttachmentsAttachmentIndexPagesPageGetPath = z.object({
    order_id: z.string(),
    attachment_index: z.int(),
    page: z.int().gte(1).lte(200)
});

export const zGetOrderAttachmentXlsxDataAppOrdersOrderIdAttachmentsAttachmentIndexXlsxDataGetPath = z.object({
    order_id: z.string(),
    attachment_index: z.int()
});

/**
 * Successful Response
 */
export const zGetOrderAttachmentXlsxDataAppOrdersOrderIdAttachmentsAttachmentIndexXlsxDataGetResponse = zXlsxAttachmentData;

export const zListProductsAppProductsGetQuery = z.object({
    search: z.string().min(1).max(200).nullish(),
    limit: z.int().gte(1).lte(500).optional().default(500)
});

/**
 * Successful Response
 */
export const zListProductsAppProductsGetResponse = zProductListResponse;

export const zCreateProductAppProductsPostBody = zProductInput;

/**
 * Successful Response
 */
export const zCreateProductAppProductsPostResponse = zProductOutput;

export const zDeleteProductAppProductsProductIdDeletePath = z.object({
    product_id: z.int()
});

/**
 * Successful Response
 */
export const zDeleteProductAppProductsProductIdDeleteResponse = z.void();

export const zGetProductAppProductsProductIdGetPath = z.object({
    product_id: z.int()
});

/**
 * Successful Response
 */
export const zGetProductAppProductsProductIdGetResponse = zProductOutput;

export const zUpdateProductAppProductsProductIdPatchBody = zProductPatch;

export const zUpdateProductAppProductsProductIdPatchPath = z.object({
    product_id: z.int()
});

/**
 * Successful Response
 */
export const zUpdateProductAppProductsProductIdPatchResponse = zProductOutput;

/**
 * Response Get Tam Reports App Tam Reports Get
 *
 * Successful Response
 */
export const zGetTamReportsAppTamReportsGetResponse = z.array(zTamReportSummary);

export const zGetTamReportAppTamReportsReportIdGetPath = z.object({
    report_id: z.int()
});

/**
 * Successful Response
 */
export const zGetTamReportAppTamReportsReportIdGetResponse = zTamReportDetail;

/**
 * Response Getemailpreferences
 *
 * Successful Response
 */
export const zGetEmailPreferencesResponse = z.array(zEmailPreferenceResponse);

export const zUpdateEmailPreferencesBody = zUpdateEmailPreferencesRequest;

/**
 * Response Updateemailpreferences
 *
 * Successful Response
 */
export const zUpdateEmailPreferencesResponse = z.array(zEmailPreferenceResponse);

/**
 * Successful Response
 */
export const zGetImpersonationStatusSuperuserImpersonationCurrentGetResponse = zImpersonationStatus;

export const zStartImpersonationSuperuserImpersonationStartPostBody = zStartImpersonationRequest;

/**
 * Successful Response
 */
export const zListImpersonationSessionsSuperuserImpersonationSessionsGetResponse = zImpersonationSessionList;

export const zRevokeImpersonationSessionSuperuserImpersonationSessionsTokenPrefixDeletePath = z.object({
    token_prefix: z.string()
});

export const zStartBackfillInboxSuperuserBackfillInboxPostQuery = z.object({
    organization_id: z.string(),
    days: z.int().gte(1).lte(365).optional().default(30)
});

/**
 * Successful Response
 */
export const zStartBackfillInboxSuperuserBackfillInboxPostResponse = zBackfillInboxStartResponse;

export const zGetBackfillInboxStatusSuperuserBackfillInboxStatusGetQuery = z.object({
    workflow_id: z.string()
});

/**
 * Successful Response
 */
export const zGetBackfillInboxStatusSuperuserBackfillInboxStatusGetResponse = zBackfillInboxStatusResponse;

export const zParseValidateSuperuserBatchUploadsParseValidatePostBody = zBodyParseValidateSuperuserBatchUploadsParseValidatePost;

/**
 * Successful Response
 */
export const zParseValidateSuperuserBatchUploadsParseValidatePostResponse = zSuperuserParseValidateResponse;

export const zExecuteBatchUploadSuperuserBatchUploadsExecutePostBody = zSuperuserExecuteBatchUploadRequest;

/**
 * Successful Response
 */
export const zExecuteBatchUploadSuperuserBatchUploadsExecutePostResponse = zExecuteResponse;

export const zCreateOrganizationSuperuserCreatePostBody = zBodyCreateOrganizationSuperuserCreatePost;

/**
 * Successful Response
 */
export const zCreateOrganizationSuperuserCreatePostResponse = zAdminOrganizationCreateResponse;

export const zQuickTrialCreateSuperuserCreateQuickPostBody = zQuickTrialCreateRequest;

/**
 * Successful Response
 */
export const zQuickTrialCreateSuperuserCreateQuickPostResponse = zQuickTrialCreateResponse;

/**
 * Successful Response
 */
export const zGetEnrichmentStatusSuperuserEnrichmentStatusGetResponse = zEnrichmentStatusResponse;

/**
 * Successful Response
 */
export const zGetEnrichmentFreshnessSuperuserFreshnessGetResponse = zFreshnessDashboardResponse;

/**
 * Successful Response
 */
export const zGetAllOrganizationsSuperuserOrganizationsGetResponse = zAdminOrganizationsResponse;

export const zUpdateOrganizationSuperuserOrganizationsOrganizationIdPostBody = zUpdateOrganizationRequest;

export const zUpdateOrganizationSuperuserOrganizationsOrganizationIdPostPath = z.object({
    organization_id: z.string()
});

/**
 * Successful Response
 */
export const zUpdateOrganizationSuperuserOrganizationsOrganizationIdPostResponse = zOrganizationInfo;

export const zGetOrganizationAccountsForAdminSuperuserOrganizationsAccountsOrganizationIdGetPath = z.object({
    organization_id: z.string()
});

export const zGetOrganizationAccountsForAdminSuperuserOrganizationsAccountsOrganizationIdGetQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    size: z.int().gte(1).lte(100).optional().default(50)
});

/**
 * Successful Response
 */
export const zGetOrganizationAccountsForAdminSuperuserOrganizationsAccountsOrganizationIdGetResponse = zPageOrganizationAccountAdmin;

export const zResetUserPasswordSuperuserOrganizationsOrganizationIdUsersUserIdResetPasswordPostPath = z.object({
    organization_id: z.string(),
    user_id: z.string()
});

/**
 * Successful Response
 */
export const zResetUserPasswordSuperuserOrganizationsOrganizationIdUsersUserIdResetPasswordPostResponse = z.void();

export const zDisconnectCrmSuperuserOrganizationsOrganizationIdDisconnectCrmPostPath = z.object({
    organization_id: z.string()
});

/**
 * Successful Response
 */
export const zDisconnectCrmSuperuserOrganizationsOrganizationIdDisconnectCrmPostResponse = zDisconnectCrmResponse;

export const zSyncCrmSuperuserOrganizationsOrganizationIdSyncCrmPostPath = z.object({
    organization_id: z.string()
});

/**
 * Successful Response
 */
export const zSyncCrmSuperuserOrganizationsOrganizationIdSyncCrmPostResponse = zSyncCrmResponse;

export const zTriggerScoutLeadGenerationSuperuserOrganizationsOrganizationIdTriggerScoutLeadGenerationPostBody = zTriggerScoutLeadGenerationRequest;

export const zTriggerScoutLeadGenerationSuperuserOrganizationsOrganizationIdTriggerScoutLeadGenerationPostPath = z.object({
    organization_id: z.string()
});

/**
 * Successful Response
 */
export const zTriggerScoutLeadGenerationSuperuserOrganizationsOrganizationIdTriggerScoutLeadGenerationPostResponse = zTriggerScoutLeadGenerationResponse;

export const zRollbackLastScoutClaimSuperuserOrganizationsOrganizationIdRollbackLastScoutClaimPostPath = z.object({
    organization_id: z.string()
});

/**
 * Successful Response
 */
export const zRollbackLastScoutClaimSuperuserOrganizationsOrganizationIdRollbackLastScoutClaimPostResponse = zRollbackLastScoutClaimResponse;

export const zRerunTriageAnalysisSuperuserRerunTriagePostQuery = z.object({
    organization_id: z.string(),
    clear_triage_data: z.boolean().optional().default(false)
});

/**
 * Successful Response
 */
export const zRerunTriageAnalysisSuperuserRerunTriagePostResponse = zRerunTriageResponse;

export const zGenerateTamReportSuperuserTamReportsPostBody = zGenerateTamReportRequest;

/**
 * Successful Response
 */
export const zGenerateTamReportSuperuserTamReportsPostResponse = zGenerateTamReportResponse;

export const zGetUserSuperuserUsersUserIdGetPath = z.object({
    user_id: z.string()
});

/**
 * Successful Response
 */
export const zGetUserSuperuserUsersUserIdGetResponse = zSuperuserUserResponse;

export const zUpdateUserSuperuserUsersUserIdPatchBody = zSuperuserUserUpdate;

export const zUpdateUserSuperuserUsersUserIdPatchPath = z.object({
    user_id: z.string()
});

/**
 * Successful Response
 */
export const zUpdateUserSuperuserUsersUserIdPatchResponse = zSuperuserUserResponse;

export const zAuthCookieLoginAuthLoginPostBody = zBodyAuthCookieLoginAuthLoginPost;

export const zAuthCookieLoginAuthLoginPostResponse = z.union([
    z.unknown(),
    z.void()
]);

export const zAuthCookieLogoutAuthLogoutPostResponse = z.union([
    z.unknown(),
    z.void()
]);

export const zAuthBearerLoginAuthBearerLoginPostBody = zBodyAuthBearerLoginAuthBearerLoginPost;

/**
 * Successful Response
 */
export const zAuthBearerLoginAuthBearerLoginPostResponse = zBearerResponse;

export const zVerifyRequestTokenAuthRequestVerifyTokenPostBody = zBodyVerifyRequestTokenAuthRequestVerifyTokenPost;

export const zVerifyVerifyAuthVerifyPostBody = zBodyVerifyVerifyAuthVerifyPost;

/**
 * Successful Response
 */
export const zVerifyVerifyAuthVerifyPostResponse = zBaseUser;

export const zResetForgotPasswordAuthForgotPasswordPostBody = zBodyResetForgotPasswordAuthForgotPasswordPost;

export const zResetResetPasswordAuthResetPasswordPostBody = zBodyResetResetPasswordAuthResetPasswordPost;

/**
 * Successful Response
 */
export const zUsersCurrentUserAuthUsersMeGetResponse = zUserlandBaseUser;

export const zUsersPatchCurrentUserAuthUsersMePatchBody = zUserlandBaseUserUpdate;

/**
 * Successful Response
 */
export const zUsersPatchCurrentUserAuthUsersMePatchResponse = zUserlandBaseUser;

export const zUsersDeleteUserAuthUsersIdDeletePath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zUsersDeleteUserAuthUsersIdDeleteResponse = z.void();

export const zUsersUserAuthUsersIdGetPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zUsersUserAuthUsersIdGetResponse = zUserlandBaseUser;

export const zUsersPatchUserAuthUsersIdPatchBody = zUserlandBaseUserUpdate;

export const zUsersPatchUserAuthUsersIdPatchPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zUsersPatchUserAuthUsersIdPatchResponse = zUserlandBaseUser;

export const zMagicLinkAuthMagicLinkPostBody = zBodyMagicLinkAuthMagicLinkPost;

export const zMagicLinkCallbackAuthMagicLinkCallbackPostBody = zBodyMagicLinkCallbackAuthMagicLinkCallbackPost;

/**
 * Successful Response
 */
export const zGetSuperuserImpersonationStatusAuthSuperuserImpersonationStatusGetResponse = zSuperuserImpersonationStatus;

/**
 * Response Inbound Email Webhooks Inbound Email Post
 *
 * Successful Response
 */
export const zInboundEmailWebhooksInboundEmailPostResponse = z.record(z.string(), z.unknown());

/**
 * Response Mailpit Inbound Webhooks Mailpit Inbound Post
 *
 * Successful Response
 */
export const zMailpitInboundWebhooksMailpitInboundPostResponse = z.record(z.string(), z.unknown());

/**
 * Successful Response
 */
export const zListDevUsersDevUsersGetResponse = zDevUsersResponse;

export const zDevLoginDevLoginEmailPostPath = z.object({
    email: z.string()
});

/**
 * Response Health Check Health Get
 *
 * Successful Response
 */
export const zHealthCheckHealthGetResponse = z.record(z.string(), z.unknown());

/**
 * Response Health Detailed Health Detailed Get
 *
 * Successful Response
 */
export const zHealthDetailedHealthDetailedGetResponse = z.record(z.string(), z.unknown());
